/* global globalThis */
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectElementalsConstructionTokens,
  selectFinalRound,
  selectGameType,
  selectGridConfigState,
  selectHexes,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectTurnOrder,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {
  elementalsSetRandomElements,
  elementalsSpendElement,
  fanaticismConvertWinPoints,
  setHexToken,
  setSelectedToken,
  setSpecialBuilding
} from "../../../redux/actions";
import {addMoreElementalsConstructionTokens} from "../../factions/elementals/addMoreElementalsConstructionTokens";
import {
  CENTRAL_QUARTER_PREFIX,
  COMMON_BUILDING_TOKENS,
  KEY_POINT_TOKENS,
  SPECIAL_BUILDING_TOKENS,
  TOKENS
} from "../../../constants/tokens";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {FACTIONS} from "../../../constants/factions";
import {MINE_EXPLORATION} from "../../../configs/factions/dwarves";
import {getD6Roll} from "../../dice/getD6Roll";
import {getBotD6MinRoll} from "../../dice/getBotD6MinRoll";
import {getBotD6RollBonus} from "../../dice/getBotD6RollBonus";
import {getElementForTokenType} from "../../factions/elementals/getElementForTokenType";
import {getUnusedElementIndex} from "../../factions/elementals/getUnusedElementIndex";
import {
  checkIfHasRemainingConstructionActions
} from "../../gameState/construction/checkIfHasRemainingConstructionActions";
import {calcUsedConstructionActions} from "../../gameState/construction/calcUsedConstructionActions";
import {checkIfConstructionAllowedInRound} from "../../gameState/construction/checkIfConstructionAllowedInRound";
import {calculateConstructionCells} from "../../gameState/construction/calculateConstructionCells";
import {selectSelectedToken} from "../../../redux/reducers/privateStateReducer/selectors";
import {sanitizeInvalidPendingBuildings} from "./sanitizeInvalidPendingBuildings";
import {checkIfHexBlockedByEnemyUnit} from "../../gameState/checkIfHexBlockedByEnemyUnit";
import {getBuildingPrice} from "../../economy";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {checkIfCentralQuarter} from "../../buildings/checkIfCentralQuarter";
import {getPlannerMode} from "../planner/plannerStore";
import {ensureRoundPlan} from "../planner/selectPlanByRollout";
import {FINAL_ROUND} from "../../../configs/game";
import {CONSTRUCTION_ACTION_TYPES, QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN} from "../../../constants/construction";
import {
  calcBfsDistances,
  calcSupplyReachCells,
  calcSupplyReachCellsWithRelay,
  getBotGridRenderer,
  getNeighborHexKeys
} from "../botGrid";
import {getPlayerFarms} from "../../gameState/getPlayerFarms";
import {checkIfBotCanHireNewGuardUnit, getPreferredUnitPrice} from "./decideRecruitmentStep";
import {
  calcCenterProximityFactor,
  checkIfKeyPointToken,
  checkIfRuinsToken,
  getControlledKeyPointHexKeys,
  getKeyPointValuePerRound
} from "../evaluation/keyPoints";
import {selectRuinsIncomeRoundLimit} from "../../gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {
  BOT_COIN_SURPLUS_THRESHOLD,
  BOT_CONSTRUCTION_COIN_RESERVE,
  BOT_EXPECTED_DICE_PENALTY,
  BOT_WEIGHTS,
  ELEMENTALS_BARRACKS_FARM_CAP_MARGIN,
  RATMEN_CAVALRY_ROUND
} from "../botConfig";
import {getUnitPrice, getUnitTypeConfig, UNIT_TYPES, UNITS_SAME_TIME_COUNT_LIMIT} from "../../../configs/units";
import {RATMEN_D6_SAME_TYPE_UNIT_LIMIT} from "../../../configs/factions/ratmen";
import {calculateInfluencePathIncome} from "../../gameState/calculateInfluencePathIncome";
import {getEnemyQuarterHexKeys} from "../evaluation/cities";
import {getBotStrategy, rememberBuildCell} from "../strategy/botStrategyStore";
import {
  POLICY_FANATICISM_RUINS_WIN_POINTS_INCOME_BONUS,
  POLICY_FANATICISM_WIN_POINTS_CONVERSION
} from "../../../configs/policy/fanaticism";
import {checkIfFinalRound, getRoundLimit} from "../../checkIfFinalRound";
import {getBotProfile} from "../strategy/getBotProfile";
import {getBotBonusProfile} from "../getBotBonusProfile";
import {decidePolicyMarketRefreshStep, decidePolicyStep} from "./decidePolicyStep";
import {decideInitiativeStep} from "./decideInitiativeStep";
import {
  createRoadInfluenceIncomeDeltaCalculator,
  getEnemyCitiesForInfluence,
  getOwnInfluenceBuildings,
  projectInfluenceBuildingIncome
} from "../evaluation/influencePaths";
import {calcPlayerArmyStrength, calcStrongestOpponentArmy, findOwnCitiesUnderThreat} from "../evaluation/armyStrength";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {calculateQuarterZoneCells} from "../../gameState/construction/calculateQuarterZoneCells";
import {getFactionPreferences} from "../strategy/factionPreferences";
import {checkIfW42CelectialsStormPlayer} from "../factions/celectialsBot";
import {CAMPAIGN_TYPES} from "../strategy/campaigns";
import {isHegemonismBonusHexKey} from "../../policy/hegemonism/isHegemonismBonusHexKey";
import {keyToCoords} from "../../coords/keyToCoords";
import {
  isPreW32CityFollowUpPlayer,
  isPreW32DefensePlayer,
  isPreW32LateCityPlayer,
  isPreW32LateSpecialsPlayer,
  isPreW32RuinsChainPlayer,
  isPreW32RuinsReservePlayer,
  isPreW32RushTemplePlayer,
  isPreW35ArmyReservePlayer,
  isPreW35BuildRiskPlayer,
  isPreW36BarracksCapPlayer,
  isPreW36CityDensityPlayer,
  isPreW36DoctrineSpecialsPlayer,
  isPreW36QuarterGrowthPlayer,
  isPreW36RingReservePlayer,
  isWave27BehaviorPlayer
} from "../abTestGates";
import {checkIfW36Feature} from "../evaluation/goldPlus";
import {
  calcW36CityDensityLoss,
  calcW36QuarterGrowthValue,
  calcW36RingReserveFactor,
  checkIfW36CityBarracksCapReached,
  checkIfW36FreeQuarterCell,
  checkIfW36FrontierCity,
  countW36CityAdjacentTokens,
  createW36QuarterRangeCells,
  findW36AdjacentCentralQuarter,
  findW36CandidateCityCentralQuarter,
  getW36CityBaseQuarters,
  getW36DoctrineSpecialTokens,
  getW36GateMilitary,
  pickW36BestSpecialValue
} from "../evaluation/cityGrowth";
import {
  checkIfW36FactionCityGrowthPartOn,
  getW36CityGrowthKnobs,
  W36_EARLY_TEMPLE_MIN_ROUND,
  W36_FACTION_SWITCHABLE_PARTS,
  W36_FRONTIER_CITY_MAX_ENEMY_DISTANCE,
  W36_INSECTOIDS_CITY_BARRACKS_CAP,
  W36_QUARTER_GROWTH_MIN_SPECIAL_ROUNDS,
  W36_QUARTER_GROWTH_PRICE_FACTOR
} from "../../../configs/botCityGrowth";
import {getFactionGarrisonDoctrine, GARRISON_DOCTRINES} from "../../../configs/botGarrison";
import {
  buildE32RoadEnablerIndex,
  calcCentralQuarterLateNet,
  calcCityPlatformFactor,
  calcE32ChainRoadScore,
  calcE32CityFollowUpValue,
  calcE32RuinsJoinCoinReserve,
  calcEnemyUnitZoneCells,
  calcLatePriceDeduction,
  checkIfCellJoinsUncontrolledRuins,
  E32_CHAIN_MIN_ROUND,
  E32_LATE_COST_TOKENS,
  findE32RuinsChainSites,
  recordE32ChainRoad,
  recordE32Stat
} from "../evaluation/lateEconomy";
import {
  CITY_THREAT_LEVELS,
  getCityCaptureThreats,
  getGuardNeededCityBarracks,
  getLaterWarMoverIds
} from "../evaluation/cityCaptureThreat";
import {calculateAvailableRecruitmentCells} from "../../gameState/calculateAvailableRecruitmentCells";
import {getRecruitableUnitTypeIds} from "../../unit/getRecruitableUnitTypeIds";
import {ensureBonusAxis, getFrontierFarmCap} from "../strategy/bonusAxis";
import {
  BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE,
  calcBonusArmyPhaseCoinReserve,
  isBonusArmyPhase
} from "../../../configs/botBonusBehavior";
import {createBonusCityIndex, createDistanceFactor, findSupplyChainPathCells} from "../evaluation/bonusCities";
import {calculateFutureCitiesCache} from "../../gameState/calculateFutureCitiesCache";
import {
  applyBuildSurvivalFactor,
  calcArmyPhaseReserveWithBuildBudget,
  calcCaptureLoss,
  calcCityBuildSurvivalFactor,
  calcConstructionPhaseCoins,
  calcNewCitySiteSurvivalFactor,
  resolveConstructionPhaseCoins,
} from "../../../configs/botBuildRisk";
import {ADDITIONAL_SUPPLY_SPEED} from "../../../configs/supply";
import {PALACE_CAPTURE_WIN_POINTS} from "../../../configs/winPoints";
import {getSizeRecruitmentPrice} from "../../recruitment/getSizeRecruitmentPrice";
import {calcW39GiantsHireCoinReserve, checkIfW39GiantsPlayer, getW39GiantsFarmsTarget} from "../factions/giantsBot";
import {checkIfW39GoblinsPlayer} from "../factions/goblinsBot";
import {calcW39SeductionCoinReserve} from "../factions/succubesBot";
import {W39_GOBLINS_INCOME_SPECIAL_MULTIPLIER} from "../../../configs/botW39Factions";

// Агрессивные фракции для строительной осторожности элементалей
// (нюанс живого теста 12.2: дороги/города к ним — подарок нападающему)
const CONSTRUCTION_AGGRESSIVE_FACTIONS = new Set([
  FACTIONS.RATMEN, FACTIONS.INSECTOIDS, FACTIONS.ORCS, FACTIONS.DEMONS, FACTIONS.BEASTMEN,
]);

// Волна 19 (18.4): раш-фракции, дороги к городам которых строятся только при
// значительном превосходстве с планом захвата либо ради новых путей влияния
const ROAD_RUSH_FACTIONS = new Set([
  FACTIONS.RATMEN, FACTIONS.ORCS, FACTIONS.INSECTOIDS, FACTIONS.DEMONS,
]);

// Сколько раундов накопительная цель остается актуальной
const SAVINGS_GOAL_MAX_ROUNDS = 3;

// Гексагональная дистанция по осевым координатам (для проверок радиуса снабжения)
const calcHexDistanceLocal = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

const COMMON_CANDIDATE_TOKENS = [
  COMMON_BUILDING_TOKENS.QUARTER,
  COMMON_BUILDING_TOKENS.FARM,
  COMMON_BUILDING_TOKENS.BARRACKS,
  COMMON_BUILDING_TOKENS.ROAD,
];

const SPECIAL_CANDIDATE_TOKENS = [
  SPECIAL_BUILDING_TOKENS.GUILD,
  SPECIAL_BUILDING_TOKENS.TEMPLE,
  SPECIAL_BUILDING_TOKENS.PALACE,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE,
  SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  SPECIAL_BUILDING_TOKENS.ARSENAL,
  SPECIAL_BUILDING_TOKENS.FORTRESS,
];

// Волна 28 (25.1): в последнем раунде бесполезны — партия заканчивается тем
// же раундом, ни одно из них не успевает окупиться (common_problems_25)
const FINAL_ROUND_BANNED_TOKENS = new Set([
  COMMON_BUILDING_TOKENS.FARM,
  COMMON_BUILDING_TOKENS.BARRACKS,
  SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  SPECIAL_BUILDING_TOKENS.PALACE,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE,
]);

/**
 * Ключевая точка считается "обслуженной", если рядом с ней уже есть наша
 * непустая ячейка (строение/дорога — реальные или запланированные в текущей
 * цепочке): путь строительства к смежному центральному кварталу уже обеспечен,
 * и дополнительные дороги к этой точке — потраченные впустую действия.
 */
const checkIfKeyPointServed = (keyPointHexKey, virtual, context) => {
  const {gridRenderer, ownCityCells} = context;

  return getNeighborHexKeys(keyPointHexKey, gridRenderer).some((cellKey) => {
    if (virtual.plannedCells.has(cellKey)) {
      return true; // любой наш запланированный в этой цепочке шаг рядом с точкой
    }
    if (virtual.hexes[cellKey] === TOKENS.ROAD) {
      return true; // дорога дает путь строительства (в т.ч. квартал поверх нее)
    }
    return ownCityCells.has(cellKey); // свое строение рядом — путь уже есть
  });
};

/**
 * SERVARIANISM: сколько своих рудников (реальных или запланированных в
 * цепочке) рядом с клеткой казармы еще не обслужены своей казармой. Казарма у
 * такого рудника поднимает его доход (+1 монета/раунд), вторая казарма у того
 * же рудника дохода не добавляет.
 */
const countAdjacentUnservedOwnMines = (cellKey, adjacentKeys, virtual, context) => {
  const {gridRenderer} = context;
  if (!context.ownMineCells) {
    return 0;
  }
  return adjacentKeys.filter((adjKey) => {
    if (!context.ownMineCells.has(adjKey) && !virtual.plannedMineCells?.has(adjKey)) {
      return false;
    }
    // Рудник уже обслужен своей казармой (реальной или из этой цепочки)?
    const hasOwnBarracksAdjacent = getNeighborHexKeys(adjKey, gridRenderer).some(
      (mineAdjKey) => mineAdjKey !== cellKey
        && (context.ownBarracksCells?.has(mineAdjKey)
          || virtual.hexes[mineAdjKey] === COMMON_BUILDING_TOKENS.BARRACKS)
    );
    return !hasOwnBarracksAdjacent;
  }).length;
};

/**
 * Оценка кандидата строительства на виртуальном состоянии цепочки.
 */
const scoreCandidate = (token, cellKey, virtual, context) => {
  const {gridRenderer, remainingRounds, playerUnitsCount, profile} = context;
  const adjacentKeys = getNeighborHexKeys(cellKey, gridRenderer);

  // Волна 28 (25.1): последний раунд — фермы/казармы/лагерь/дворец/склад не
  // успеют окупиться; кварталы (базовые и центральные) осмысленны только
  // смежно руинам/великим руинам (контроль засчитывается тем же раундом).
  // Всё остальное (в первую очередь дороги под перемещение к целям) — по
  // обычному скорингу (common_problems_25)
  if (context.wave28FinalRoundBanActive && context.isFinalRound) {
    if (FINAL_ROUND_BANNED_TOKENS.has(token)) {
      return 0;
    }
    if ((token === COMMON_BUILDING_TOKENS.QUARTER || checkIfCentralQuarter(token))
      && !adjacentKeys.some((adjKey) => checkIfRuinsToken(virtual.hexes[adjKey]))
    ) {
      return 0;
    }
  }

  // Контроль ключевых точек бинарный: смежные точки, уже контролируемые городами
  // ИЛИ кварталами, запланированными ранее в этой же цепочке, ценности не добавляют.
  // Центр поля ценнее для всех (context.keyPointCenterBias); демоны в раундах 1-4
  // дополнительно форсируют захват рудников стройкой (context.demonEarlyMineFocus).
  const sumAdjacentUncontrolledKeyPointsValue = () => {
    let value = 0;
    adjacentKeys.forEach((adjKey) => {
      const adjToken = virtual.hexes[adjKey];
      if (checkIfKeyPointToken(adjToken) && !virtual.controlledKeyPoints.has(adjKey)) {
        const centerBias = context.keyPointCenterBias ? context.keyPointCenterBias(adjKey) : 1;
        let kpValue = getKeyPointValuePerRound(adjToken, context.round, context.ruinsIncomeRoundLimit);
        // Волна 23 (22.3, запечена): при FANATICISM руины дают +2 очка/раунд —
        // реальный доход политики, фокус стройки на захват руин кварталами
        if (context.hasFanaticism && checkIfRuinsToken(adjToken)) {
          kpValue += POLICY_FANATICISM_RUINS_WIN_POINTS_INCOME_BONUS;
        }
        value += kpValue * centerBias
          * (context.demonEarlyMineFocus || 1) * (context.earlyIncomeDrive || 1);
      }
    });
    return value;
  };

  // Волна 17 (живой тест 16.10): мульти-захват — квартал/город, накрывающий 2+
  // неконтролируемые точки, ценнее по нарастающей; великие рудники/руины
  // поднимают множитель еще выше (обе великие — максимум)
  const calcMultiCaptureMultiplier = () => {
    const uncontrolled = adjacentKeys.filter((adjKey) =>
      checkIfKeyPointToken(virtual.hexes[adjKey]) && !virtual.controlledKeyPoints.has(adjKey)
    );
    if (uncontrolled.length < 2) {
      return 1;
    }
    const supersCount = uncontrolled.filter((adjKey) =>
      virtual.hexes[adjKey] === KEY_POINT_TOKENS.SUPER_MINE
      || virtual.hexes[adjKey] === KEY_POINT_TOKENS.SUPER_RUINS
    ).length;
    if (supersCount >= 2) {
      return 2;
    }
    return supersCount === 1 ? 1.6 : 1.4;
  };

  if (token === COMMON_BUILDING_TOKENS.QUARTER) {
    // Дебют: на базовые кварталы уходит максимум 2 действия за раунд — остальные
    // идут в экономику фракции (ферма+казарма; инсектоидам казармы, крысолюдам фермы)
    if (context.round <= 2) {
      const plannedQuartersCount = [...virtual.plannedCells].filter(
        (plannedCellKey) => virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.QUARTER
      ).length;
      if (plannedQuartersCount + (context.pendingQuartersCount || 0) >= 2) {
        return 0;
      }
    }

    // Базовый квартал строится только под конкретную цель — безусловной ценности у него нет
    let score = 0;

    // Цель 1: захват еще не контролируемой ключевой точки
    // (двойной захват и великие точки — мультипликативный буст)
    const quarterMultiCapture = calcMultiCaptureMultiplier();
    const quarterKeyPointsScore = sumAdjacentUncontrolledKeyPointsValue() * remainingRounds
      * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND * profile.keyPoint * quarterMultiCapture;
    // Волна 33 (C2): у «сильных городов» квартал к точке вдали от
    // сформированных отрядов дешевле — застройка идет за армией
    score += context.strongCities
      ? quarterKeyPointsScore * context.distanceFactor(cellKey)
      : quarterKeyPointsScore;

    // Волна 18 (17.1/17.9): олигархия-опенинг — нужны 2 базовых квартала под
    // 2 гильдии. Квартал с рудником рядом всегда приоритетнее, но и без рудника
    // строим до 2 базовых кварталов (пустые пойдут под гильдии); предпочитаем
    // ячейки ближе к нейтральному городу (будущие пути влияния)
    if (context.oligarchyOpeningActive) {
      const baseQuartersInPlan = virtual.allQuarters.size - virtual.centralQuarters.size;
      if (baseQuartersInPlan < 2) {
        let openingQuarterScore = BOT_WEIGHTS.FARM_NEEDED * 1.4;
        if (context.enemyQuarterDistances) {
          const neutralDistance = context.enemyQuarterDistances.get(cellKey);
          if (neutralDistance !== undefined) {
            openingQuarterScore += Math.max(0, 8 - neutralDistance) * 0.5;
          }
        }
        score = Math.max(score, openingQuarterScore);
      }
    }

    // Дебютный захват рудников (критично для дохода): в раундах 1-2 квартал
    // смежно неконтролируемому руднику обгоняет вторую ферму/казарму —
    // цель: 2 рудника или великий рудник к концу 1 раунда, +1-2 во 2-м
    if (context.round <= 2) {
      // Волна 21 (20.3, запечено): просвещенные не гонятся за новым городом ради
      // ОДНОГО обычного рудника — только великий рудник оправдывает новый город,
      // обычный рудник без соседства сам по себе не стоит отвлечения действий
      // от гильдий (живой тест 20: мелкие города вместо путей влияния)
      const hasUncontrolledMineAdjacent = adjacentKeys.some((adjKey) => {
        const adjToken = virtual.hexes[adjKey];
        if (context.isEnlightened && adjToken === KEY_POINT_TOKENS.MINE) {
          return false;
        }
        return (adjToken === KEY_POINT_TOKENS.MINE || adjToken === KEY_POINT_TOKENS.SUPER_MINE)
          && !virtual.controlledKeyPoints.has(adjKey);
      });
      if (hasUncontrolledMineAdjacent) {
        score += BOT_WEIGHTS.OPENING_MINE_QUARTER_BOOST;
      }
    }

    // Крысолюды: первый базовый квартал — хаб ферменной экономики (вокруг него
    // фермы, на нем потом склад, удваивающий их производительность); смежный
    // рудник добавляет ценность через обычный член ключевых точек выше
    if (context.isRatmen) {
      const baseQuartersCount = virtual.allQuarters.size - virtual.centralQuarters.size;
      if (baseQuartersCount < 1) {
        score += BOT_WEIGHTS.RATMEN_FARM_HUB_VALUE;
      }
    }

    // Цель 2: расширение под нужные (финансируемые) фермы/казармы — только если
    // легальных ячеек под них сейчас НЕТ, а этот квартал их реально откроет
    // (иначе квартал "смежно ничему" строится и никогда не используется)
    const opensNeededSlots =
      ((virtual.farmsCount < context.farmsTarget && context.noFarmCellsAvailable)
        || (virtual.barracksCount < context.barracksTarget && context.noBarracksCellsAvailable))
      && adjacentKeys.some((adjKey) => !virtual.hexes[adjKey] && !virtual.plannedCells.has(adjKey));
    if (opensNeededSlots) {
      score += BOT_WEIGHTS.QUARTER_EXPANSION_SYNERGY;
    }

    const city = (context.playerCities || []).find((playerCity) =>
      getAllQuartersOfCity(playerCity).some((quarterHexKey) => adjacentKeys.includes(quarterHexKey))
    );
    if (city) {
      // Цель 3: платформа под спецстроение, когда свободных базовых кварталов в городе нет.
      // Инсектоидам фермы для базовой экономики не нужны — достаточно казармы.
      // Крысолюдам, наоборот, не нужны казармы — достаточно ферм (армия бесплатна).
      let hasEconomyForSpecials;
      if (context.isInsectoids) {
        hasEconomyForSpecials = virtual.barracksCount >= 1;
      } else if (context.isRatmen) {
        hasEconomyForSpecials = virtual.farmsCount >= 2;
      } else {
        hasEconomyForSpecials = virtual.farmsCount >= 2 && virtual.barracksCount >= 1;
      }
      if (hasEconomyForSpecials) {
        const freeRealQuarters = (city[COMMON_BUILDING_TOKENS.QUARTER] || []).filter((quarterHexKey) =>
          !context.specialBuildings[quarterHexKey] && !virtual.plannedCells.has(quarterHexKey)
        ).length;
        const plannedQuartersInCity = [...virtual.plannedCells].filter((plannedCellKey) =>
            virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.QUARTER
            && getNeighborHexKeys(plannedCellKey, gridRenderer).some(
              (adjKey) => getAllQuartersOfCity(city).includes(adjKey)
            )
        ).length;
        // Платформа ценна, только если архетип реально сможет ее использовать:
        // экономический/влиятельный — гильдия/храм с путями влияния с этой ячейки;
        // агрессивный — лагерь/арсенал при казармах в городе и бюджете на найм
        let isPlatformUsable;
        if (profile.influence >= 1.2) {
          const {count} = calculateInfluencePathIncome(cellKey, context.enemyCitiesForInfluence, virtual.hexes);
          isPlatformUsable = count >= 1;
        } else {
          const cityHasBarracksForSpecials = (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).length > 0;
          isPlatformUsable = cityHasBarracksForSpecials
            && context.currentPlayer.coins >= context.preferredUnitPrice;
        }
        if (freeRealQuarters + plannedQuartersInCity === 0 && isPlatformUsable) {
          score += BOT_WEIGHTS.QUARTER_SPECIAL_PLATFORM;
        }
      }
    }

    // Волна 33 (C2): площадки «сильных городов». Город кандидата — по индексу
    // future-городов (pending и цепочка видны, см. evaluation/bonusCities).
    // Волна 36 (C2 «рост города»): надбавка площадки запоминается — ценность
    // роста города берется max с ней, а не суммой
    let strongCitiesPlatformScore = 0;
    if (context.strongCities) {
      const bonusCity = context.bonusCities.getCandidateCity(adjacentKeys);
      if (bonusCity) {
        const {bonusProfile} = context;
        if (context.bonusCities.isStagingCity(bonusCity)
          && context.isBonusArmyPhase
          && context.bonusCities.countBaseQuarters(bonusCity) < bonusProfile.forgeQuartersTarget
        ) {
          // Квартал кузницы: площадка докупки размера (с каждого квартала по
          // разу за фазу, при арсенале — дешевле) и спецстроений; свободные
          // кварталы не мешают — кузница растет до цели
          strongCitiesPlatformScore = bonusProfile.forgePlatformQuarter
            * (context.bonusCities.hasArsenal(bonusCity) ? 2 : 1);
          score += strongCitiesPlatformScore;
        } else if (context.bonusCities.classifyCity(bonusCity) === 'rear'
          && context.bonusCities.areBaseQuartersOccupied(bonusCity)
          && !context.bonusCities.hasWarehouse(bonusCity)
        ) {
          // Платформа тылового склада: базовые кварталы заняты, у хаба уже
          // кольцо ферм — новый квартал, смежный ≥2 фермам, примет склад
          const hubFarms = getNeighborHexKeys(context.bonusCities.pickRearHubQuarter(bonusCity), gridRenderer)
            .filter((adjKey) => virtual.hexes[adjKey] === COMMON_BUILDING_TOKENS.FARM).length;
          const candidateFarms = adjacentKeys.filter(
            (adjKey) => virtual.hexes[adjKey] === COMMON_BUILDING_TOKENS.FARM
          ).length;
          if (hubFarms >= 2 && candidateFarms >= 2) {
            strongCitiesPlatformScore = bonusProfile.forgePlatformQuarter;
            score += strongCitiesPlatformScore;
          }
        }
      }
    }

    // Волна 36 (C2, боты золото+): ценность роста города — базовый квартал
    // как площадка под спецстроение следующего раунда по доктрине фракции,
    // пока в городе меньше цели БК (у прежнего кода квартал ценен только под
    // точку, в дебюте или как платформа «+4», и города оставались из одного ЦК).
    // С площадкой кузницы/тылового склада — max, а не сумма
    if (context.w36QuarterGrowth) {
      const quarterGrowthScore = context.w36QuarterGrowth(cellKey, adjacentKeys, virtual);
      if (quarterGrowthScore > strongCitiesPlatformScore) {
        score += quarterGrowthScore - strongCitiesPlatformScore;
      }
    }

    // Осторожность направления: неагрессивные фракции без превосходства не
    // строятся под носом у агрессоров (элементали строже — нюансы 12.2/13.4)
    if (score > 0 && context.aggressiveCityDistances
      && (context.aggressiveCityDistances.get(cellKey) ?? Infinity) <= 4
    ) {
      if (context.isElementalsAvoidingAggressors) {
        score *= 0.3;
      } else if (context.isAvoidingAggressorsDirection) {
        score *= 0.5;
      }
    }

    // Орки в режиме экспансии: кварталы в направлении слабейшего противника
    // ценнее — маршрут армии к жертве идет по застроенным ячейкам (живой тест 14)
    if (score > 0 && context.orcExpansionDistances
      && (context.orcExpansionDistances.get(cellKey) ?? Infinity)
      < (context.minOwnCityOrcExpansionDistance ?? Infinity)
    ) {
      score *= 1.3;
    }

    // Волна 23 (22.2, запечена): при HEGEMONISM кварталы бонусной зоны центра
    // дают +1 очко/раунд — строим максимум кварталов в радиусе 2 от центра
    if (context.hasHegemonism && score > 0
      && isHegemonismBonusHexKey(cellKey)
    ) {
      score *= 1.5;
    }

    // Волна 20 (19.11): квартал ближе к цели активной кросс-раундовой
    // кампании отряда (захват/осада/оборона) — стройка тянется туда же, куда
    // уже идет армия, вместо случайного направления
    if (score > 0 && context.campaignDestinationDistances) {
      const destinationDistance = context.campaignDestinationDistances.get(cellKey);
      if (destinationDistance !== undefined) {
        score += Math.max(0, 8 - destinationDistance) * BOT_WEIGHTS.CAMPAIGN_DESTINATION_PROXIMITY_FACTOR;
      }
    }

    return score;
  }

  if (checkIfCentralQuarter(token)) {
    const keyPointsValue = sumAdjacentUncontrolledKeyPointsValue();
    if (keyPointsValue === 0) {
      return 0; // новый город без ключевых точек рядом не интересен
    }
    // Новый город должен брать минимум 2 точки (в идеале 3): мульти-захват
    // премируется, а ЦК к одному обычному руднику окупается 4 раунда — штраф
    const uncontrolledCount = adjacentKeys.filter((adjKey) =>
      checkIfKeyPointToken(virtual.hexes[adjKey]) && !virtual.controlledKeyPoints.has(adjKey)
    ).length;
    let multiCaptureMultiplier = 1;
    if (uncontrolledCount >= 2) {
      // Великие рудники/руины поднимают множитель мульти-захвата (16.10)
      multiCaptureMultiplier = calcMultiCaptureMultiplier();
    } else if (keyPointsValue <= 0.75) {
      multiCaptureMultiplier = 0.5; // одиночный обычный рудник

      // Гномы: город у одиночного обычного рудника без продолжения — тупик
      // (три таких города без единой достройки). ЦК допустим, только если
      // будущий базовый квартал этого города дотянется до еще одной точки
      // (иная точка в радиусе 2 от ЦК); иначе выгоднее разведка рудников
      if (context.isDwarves) {
        const hasFollowUpKeyPoint = adjacentKeys.some((adjKey) =>
          getNeighborHexKeys(adjKey, gridRenderer).some((adj2Key) =>
            adj2Key !== cellKey
            && checkIfKeyPointToken(virtual.hexes[adj2Key])
            && !virtual.controlledKeyPoints.has(adj2Key)
            && !adjacentKeys.includes(adj2Key)
          )
        );
        if (!hasFollowUpKeyPoint) {
          return 0;
        }
      }
    }
    let centralQuarterScore;
    if (context.e32CityFollowUpActive || context.e32LateCityActive) {
      // Волна 32 (экономика, E5): база «платформы расширения» тает к R10
      const platformFactor = context.e32LateCityActive ? calcCityPlatformFactor(context.round) : 1;
      centralQuarterScore = BOT_WEIGHTS.CENTRAL_QUARTER_BASE * platformFactor
        + keyPointsValue * remainingRounds * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND
        * profile.keyPoint * multiCaptureMultiplier;
      if (context.e32LateCityActive) {
        // E5: чистая стоимость ЦК (+1 за точку − цена) с поздним нарастанием
        centralQuarterScore += calcCentralQuarterLateNet({
          joinedKeyPointsCount: uncontrolledCount,
          priceCoins: context.centralQuarterPriceCoins,
          coinsToPoints: context.e32CoinsToPoints,
          round: context.round,
        });
      }
      if (context.e32CityFollowUpActive) {
        // Волна 32 (экономика, E4): руины/великий рудник на кольце нового города —
        // следующим ходом базовый квартал возьмет их. Не дороже самих смежных точек
        const followUpValue = calcE32CityFollowUpValue({
          cqCellKey: cellKey,
          hexes: virtual.hexes,
          gridRenderer,
          controlledKeyPoints: virtual.controlledKeyPoints,
          plannedCells: virtual.plannedCells,
          quarterZoneCells: context.e32QuarterZoneCells,
          enemyUnitZoneCells: context.e32EnemyUnitZoneCells,
          round: context.round,
          hasFanaticism: context.hasFanaticism,
          keyPointCenterBias: context.keyPointCenterBias,
          ruinsIncomeRoundLimit: context.ruinsIncomeRoundLimit,
        });
        centralQuarterScore += Math.min(keyPointsValue * remainingRounds, followUpValue) * profile.keyPoint;
      }
    } else {
      // Прежний код дословно (legacy-группа блока E)
      centralQuarterScore = BOT_WEIGHTS.CENTRAL_QUARTER_BASE
        + keyPointsValue * remainingRounds * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND
        * profile.keyPoint * multiCaptureMultiplier;
    }
    // Волна 33 (C1): «сильные города» — новый город только там, куда дошли
    // сформированные отряды (дальше 3 — ×0.5, дальше 5 — ×0.2), и не там, где
    // противник сильнее в радиусе 3 (город-подарок захватчику). Живой тест:
    // бонусный бот застраивал карту беззащитными городами ради точек
    if (context.strongCities) {
      centralQuarterScore *= context.distanceFactor(cellKey);
      if (context.checkIfEnemyOutweighsNear && context.checkIfEnemyOutweighsNear(cellKey)) {
        centralQuarterScore *= context.bonusProfile.newCityOutweighedFactor;
      }
    }
    // Неагрессивные без превосходства: новый город у агрессора — подарок нападающему
    if (context.aggressiveCityDistances
      && (context.aggressiveCityDistances.get(cellKey) ?? Infinity) <= 4
    ) {
      if (context.isElementalsAvoidingAggressors) {
        centralQuarterScore *= 0.3;
      } else if (context.isAvoidingAggressorsDirection) {
        centralQuarterScore *= 0.5;
      }
    }
    // Орки в режиме экспансии: новый город к слабейшему противнику — и доход
    // до целевых 7-9 монет, и плацдарм будущей агрессии (живой тест 14)
    if (context.orcExpansionDistances
      && (context.orcExpansionDistances.get(cellKey) ?? Infinity)
      < (context.minOwnCityOrcExpansionDistance ?? Infinity)
    ) {
      centralQuarterScore *= 1.6;
    }
    // Волна 35 (B, гейт isPreW35BuildRiskPlayer): место нового города у всех
    // ботов проходит модель угрозы своих городов (синтетический город на этой
    // клетке). Катон R9: ЦК 4,4 в 2 клетках от d6(5) противника — потерян в тот
    // же раунд. У legacy-группы функции нет: множитель 1, код прежний
    const newCitySiteFactor = context.getNewCitySiteFactor ? context.getNewCitySiteFactor(cellKey) : 1;
    // Живой тест 16.4: крысолюды основывают города в направлении ближайшего
    // противника — волна выходит из гор/тыла к линии фронта, а не вязнет у
    // стартового города. Волна 35 (B): только на безопасном месте — город под
    // ударом врага это подарок захватчику, а не плацдарм
    if (context.isRatmen
      && newCitySiteFactor >= 1
      && context.enemyQuarterDistances
      && (context.enemyQuarterDistances.get(cellKey) ?? Infinity)
      < (context.minOwnCityEnemyDistance ?? Infinity)
    ) {
      centralQuarterScore *= 1.5;
    }
    // Волна 18 (живой тест 17.7): инсектоиды в первые 3 раунда до дохода 7 монет
    // основывают города ради захвата ключевых точек, а не строят только казармы
    if (context.isInsectoidsEarlyExpansion) {
      centralQuarterScore *= 1.8;
    }
    // Демоны: приоритет центральной зоны (радиус 4 от центра) — развитие в тыл
    // сжигает действия без ключевых точек центра (живой тест 15.7)
    if (context.isDemons) {
      centralQuarterScore *= calcHexDistanceLocal(cellKey, '0,0') <= 4 ? 1.5 : 0.7;
    }
    // Волна 22 (21.2): демоны форсируют ЦК, берущий великий рудник/великие
    // руины или сразу 2+ ключевые точки (особенно ячейки, смежные двум КТ) —
    // живой тест 21: демоны не забирали точки центра в 2-4 ячейках
    if (context.isDemons) {
      const capturesSuperKeyPoint = adjacentKeys.some((adjKey) =>
        (virtual.hexes[adjKey] === KEY_POINT_TOKENS.SUPER_MINE
          || virtual.hexes[adjKey] === KEY_POINT_TOKENS.SUPER_RUINS)
        && !virtual.controlledKeyPoints.has(adjKey));
      if (capturesSuperKeyPoint || uncontrolledCount >= 2) {
        centralQuarterScore *= 1.6;
      }
    }
    // Волна 22 (21.10): двойной захват в текущей фазе при отсутствии перевеса
    // противника рядом — приоритет ЦК (парный гейт дороги выше отдает эту
    // ячейку под квартал)
    if (uncontrolledCount >= 2
      && (!context.checkIfEnemyOutweighsNear || !context.checkIfEnemyOutweighsNear(cellKey))
    ) {
      centralQuarterScore *= 1.4;
    }
    // Волна 25 (23.7): в первой половине партии ЦК, берущий ключевые точки,
    // приоритетнее дорог — живой тест 23: боты строили «дорога + дорога» там,
    // где «дорога + ЦК» захватывал бы точки (особенно 2 сразу) уже в этой фазе
    // Волна 33 (C1): в армейской фазе «сильных городов» приоритет ЦК первой
    // половины не действует — монеты и действия идут в армию, а не в гонку за точками
    if (context.firstHalfQuarterPriorityActive && context.round <= 7 && uncontrolledCount >= 1
      && !context.bonusArmyPhaseCityPriorityOff
    ) {
      centralQuarterScore *= uncontrolledCount >= 2 ? 1.5 : 1.25;
    }
    // Волна 23 (22.2, запечена): HEGEMONISM — новый город в бонусной зоне центра
    if (context.hasHegemonism
      && isHegemonismBonusHexKey(cellKey)
    ) {
      centralQuarterScore *= 1.5;
    }
    // Волна 36 (C4, боты золото+): плотность своих городов — новый ЦК платит
    // за слоты под базовые кварталы, которые он отнимает у своих городов
    // правилом дальности кварталов разных городов, и за собственное кольцо
    // меньше цели (город, который не вырастет). Реплеи: 71% новых ЦК ботов —
    // на минимальной дистанции к своим кварталам, −0.71 клетки под БК на ЦК
    if (context.w36CityDensity && centralQuarterScore > 0) {
      centralQuarterScore = Math.max(0, centralQuarterScore - context.w36CityDensity(cellKey, virtual));
    }
    // Волна 35 (B): ценность × вероятность удержать город минус потеря при
    // захвате — очки ЦК и точек, которые возьмет новый город, и его цена
    if (newCitySiteFactor < 1) {
      return applyBuildSurvivalFactor(centralQuarterScore, newCitySiteFactor, calcCaptureLoss(
        1 + uncontrolledCount, context.centralQuarterPriceCoins, context.e32CoinsToPoints
      ));
    }
    return centralQuarterScore;
  }

  if (token === COMMON_BUILDING_TOKENS.FARM) {
    // Волна 33 (C3): «сильные города» — фермы фронтирного города ограничены
    // (одна: звено цепочки снабжения тыл → фронт; кузнице две при голодном
    // сформированном отряде рядом). Живой тест: пачка ферм во фронтирном
    // городе без гарнизона — двойная добыча захватчика (очки + снабжение).
    // Город кандидата — по индексу future-городов, строения — по virtual.hexes
    // (citiesCache внутри фазы не обновляется, правило не течет между тиками).
    // Кап стоит ДО фракционных веток (аграризм, инсектоиды, элементали);
    // крысолюдов не касается — их фермы и есть армия, хаб-логика ниже своя.
    // Исключение: цель ферм не достигнута, а у тыловых городов ячеек под
    // ферму нет вовсе — тогда кап не держим, иначе армия останется без корма
    let bonusFarmCity = null;
    let bonusFarmCityClass = null;
    if (context.strongCities && !context.isRatmen) {
      bonusFarmCity = context.bonusCities.getCandidateCity(adjacentKeys);
      if (bonusFarmCity) {
        bonusFarmCityClass = context.bonusCities.classifyCity(bonusFarmCity);
        let farmCap = getFrontierFarmCap(
          bonusFarmCityClass,
          context.bonusCities.isStagingCity(bonusFarmCity)
        );
        // Калибровочный оверрайд харнесса (SIM_W33_FRONTIER_FARM_CAP_UNSUPPLIED_EXTRA):
        // голодный свой отряд в 4 ячейках от кандидата поднимает кап на N —
        // ферма там, где реально стоит армия; в проде не задается
        if (typeof globalThis.__bonusFrontierFarmCapUnsuppliedExtra === 'number'
          && Number.isFinite(farmCap)
          && context.unsuppliedOwnUnitCells && context.unsuppliedOwnUnitCells.size > 0
          && [...context.unsuppliedOwnUnitCells].some((unitHexKey) => calcHexDistanceLocal(cellKey, unitHexKey) <= 4)
        ) {
          farmCap += globalThis.__bonusFrontierFarmCapUnsuppliedExtra;
        }
        if (context.bonusCities.countCityAdjacentTokens(bonusFarmCity, COMMON_BUILDING_TOKENS.FARM) >= farmCap
          && !context.checkIfFrontierFarmCapWaived()
        ) {
          return 0;
        }
      }
    }

    // Волна 25 (23.2): кап ферм для AGRARIANISM до 6 раунда — не больше, чем
    // отрядов + 1 (живой тест 23: 5 ферм при 1 отряде — снабжение некому
    // раздавать, действия и монеты сожжены). Крысолюдов не касается: их ферма
    // сама создает отряд (найм d6 за снабжение)
    const isAgrarianismFarmCapReached = context.agrarianismFarmCap !== null
      && context.agrarianismFarmCap !== undefined
      && virtual.farmsCount >= context.agrarianismFarmCap;

    // Волна 23 (22.1a): AGRARIANISM — первая ферма фазы не тратит действие и
    // стоит 2 монеты: строим ее обязательно каждый раунд (пока есть ячейка);
    // небольшой позиционный вес — ближе к важным ключевым точкам
    if (context.hasAgrarianism
      && context.agrarianismFreeFarmAvailable
      && (virtual.plannedFarmActions || 0) === 0
      && !isAgrarianismFarmCapReached
    ) {
      return BOT_WEIGHTS.AGRARIANISM_FREE_FARM_SCORE
        + (context.kpProximityValues?.get(cellKey) || 0);
    }
    if (isAgrarianismFarmCapReached) {
      return BOT_WEIGHTS.FARM_SURPLUS;
    }
    // Инсектоиды: базовое снабжение бесплатно у всех отрядов — ферма только как
    // роскошь дополнительного снабжения, не более 1 фермы на 2 отряда
    if (context.isInsectoids) {
      return virtual.farmsCount < context.farmsTarget
        ? (BOT_WEIGHTS.FARM_NEEDED / 2) * profile.economy
        : BOT_WEIGHTS.FARM_SURPLUS;
    }
    // Крысолюды: фермы — это армия (найм d6 за снабжение), казармы не нужны.
    // Кластеризуем фермы вокруг БАЗОВЫХ кварталов (будущих складов-хабов,
    // удваивающих смежные фермы), а не вокруг стартового центрального;
    // фермы ближе к противнику — снабжение по пути волны
    if (context.isRatmen) {
      if (virtual.farmsCount >= context.farmsTarget) {
        // Волна 17 (16.8): в 1-й половине лишнее действие лучше вложить в ферму
        // (у крысолюдов ферма = найм d6 за снабжение), чем не потратить вовсе
        return context.isFirstHalfSpendAll ? 1.5 : BOT_WEIGHTS.FARM_SURPLUS;
      }
      let score = BOT_WEIGHTS.FARM_NEEDED * profile.economy;
      const isNearWarehouse = adjacentKeys.some((adjKey) =>
        context.specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
        || virtual.plannedWarehouses.has(adjKey)
      );
      if (isNearWarehouse) {
        score += BOT_WEIGHTS.RATMEN_FARM_NEAR_WAREHOUSE_BONUS;
      }
      const isNearBaseQuarter = adjacentKeys.some((adjKey) =>
        virtual.allQuarters.has(adjKey) && !virtual.centralQuarters.has(adjKey)
      );
      if (isNearBaseQuarter) {
        score += BOT_WEIGHTS.RATMEN_FARM_NEAR_HUB_BONUS;
      }
      if (context.enemyQuarterDistances) {
        const enemyDistance = context.enemyQuarterDistances.get(cellKey);
        if (enemyDistance !== undefined) {
          score += Math.max(0, 10 - enemyDistance) * BOT_WEIGHTS.RATMEN_FARM_FRONTIER_PER_CELL;
        }
      }
      return score;
    }
    // Элементали: ферма строится только когда выпал подходящий элемент
    // (findFreeElementIndex уже отсеял кандидата, если элемента нет) — редкое
    // окно, пропускать нельзя. Форс-приоритет держится, пока в ЭТОЙ фазе еще
    // не запланировано ни одной фермы (после первой — обычный конкурс ниже).
    // Первые 2 фермы за игру тяготеют к одному базовому кварталу — на нем
    // потом склад удвоит их снабжение (common_problems_25)
    if (context.isElementals) {
      if (context.wave28ElementalsFarmForceActive && (virtual.plannedFarmActions || 0) === 0) {
        let score = BOT_WEIGHTS.ELEMENTALS_FORCED_FARM_SCORE;
        if (virtual.farmsCount < 2) {
          const nearHubWithFarm = adjacentKeys.some((adjKey) =>
              virtual.allQuarters.has(adjKey)
              && !virtual.centralQuarters.has(adjKey)
              && getNeighborHexKeys(adjKey, gridRenderer).some(
                (quarterAdjKey) => virtual.hexes[quarterAdjKey] === COMMON_BUILDING_TOKENS.FARM
              )
          );
          const nearAnyBaseQuarter = adjacentKeys.some((adjKey) =>
            virtual.allQuarters.has(adjKey) && !virtual.centralQuarters.has(adjKey)
          );
          if (nearHubWithFarm) {
            score += BOT_WEIGHTS.ELEMENTALS_FARM_HUB_BONUS;
          } else if (nearAnyBaseQuarter) {
            score += BOT_WEIGHTS.ELEMENTALS_FARM_HUB_BONUS * 0.4;
          }
        }
        return score;
      }
    }
    // Спрос на фермы привязан к финансируемой армии (context.farmsTarget):
    // строить кормовую базу сверх того, что можем нанять, — потраченные монеты.
    // Нюанс #1: пока нет казармы, вторая ферма простаивает — сначала казарма.
    if (virtual.barracksCount === 0 && virtual.farmsCount >= 1) {
      return BOT_WEIGHTS.FARM_SURPLUS;
    }
    // Волна 17 (16.8): в 1-й половине неистраченное действие строительства —
    // потерянный темп; ферма сверх цели получает минимальный ненулевой скоринг
    // (осознанное накопление защищено coinReserve/savingsGoal планировщика).
    // Волна 18 (запечена, 17.2): фермовый добор — только пока фермы не обгоняют
    // отряды на 2 (иначе действие лучше вложить в захват точки кварталом)
    let farmSurplusScore = context.isFirstHalfSpendAll ? 1.5 : BOT_WEIGHTS.FARM_SURPLUS;
    if (virtual.farmsCount > playerUnitsCount + 2) {
      farmSurplusScore = BOT_WEIGHTS.FARM_SURPLUS;
    }
    let farmScore = virtual.farmsCount < context.farmsTarget
      ? BOT_WEIGHTS.FARM_NEEDED * profile.economy
      : farmSurplusScore;
    // Волна 19 (18.2): в раундах 1-5 ферма ближе к важной ключевой точке ценнее —
    // строительство расширяет зону в нужную сторону И приносит пользу само
    if (farmScore > 0 && context.kpProximityValues && context.round <= 5) {
      farmScore += (context.kpProximityValues.get(cellKey) || 0)
        * BOT_WEIGHTS.KP_PROXIMITY_ECONOMY_FACTOR;
    }
    // Волна 20 (19.4): ферма в центральной зоне поля (<=4 ячеек от центра) —
    // приоритет снабжения ключевых точек центра.
    // Волна 33 (C3): у «сильных городов» не действует — центр это фронт, а
    // фермы фронта и так ограничены капом; кластер снабжения растет в тылу
    if (farmScore > 0 && !context.strongCities && calcHexDistanceLocal(cellKey, '0,0') <= 4) {
      farmScore += BOT_WEIGHTS.CENTRAL_CITY_ECONOMY_FACTOR * profile.economy;
    }
    // Волна 20 (19.7): ферма, продлевающая снабжение до своего отряда, не
    // получившего его в этом раунде войны, — критичный приоритет над любым
    // другим строением (превышает даже цель "фермы достаточно")
    // Волна 36 (C1): такая ферма не попадает под бронь кольца ЦК
    let isW36UnsuppliedUnitFarm = false;
    if (context.unsuppliedOwnUnitCells && context.unsuppliedOwnUnitCells.size > 0) {
      const extendsToUnsupplied = [...context.unsuppliedOwnUnitCells].some(
        (unitHexKey) => calcHexDistanceLocal(cellKey, unitHexKey) <= 4
      );
      if (extendsToUnsupplied) {
        farmScore = Math.max(farmScore, BOT_WEIGHTS.FARM_UNSUPPLIED_UNIT_BONUS);
        isW36UnsuppliedUnitFarm = true;
      }
    }
    // Волна 33 (C3): у «сильных городов» надбавка «город без фермы» считается
    // по индексу future-городов (pending и цепочка видны — не течет между
    // тиками), во фронтирном городе режется вдвое. Ретранслятор: ферма
    // фронтирного города ставится подальше от чужих кварталов (ее не гасит
    // один отряд, цепочка не рвется). Тыловой кластер: ферма у хаба или у
    // склада (реального/запланированного) держит базу FARM_NEEDED, пока
    // емкость ферм тылового города ниже цели, — сверх глобальной цели ферм
    if (context.strongCities && !context.isRatmen) {
      const {bonusProfile} = context;
      if (bonusFarmCity) {
        if (context.bonusCities.countCityAdjacentTokens(bonusFarmCity, COMMON_BUILDING_TOKENS.FARM) === 0) {
          farmScore += bonusProfile.undefendedCityFarmScore
            * (bonusFarmCityClass === 'frontier' ? bonusProfile.frontierUndefendedFarmFactor : 1);
        }
        if (bonusFarmCityClass === 'frontier') {
          const enemyDistance = context.realEnemyQuarterDistances
            ? (context.realEnemyQuarterDistances.get(cellKey) ?? 6)
            : 6;
          farmScore += bonusProfile.frontierFarmEnemyDistancePerCell * Math.min(6, enemyDistance);
        } else if (context.bonusCities.calcCityFarmCapacity(bonusFarmCity) < bonusProfile.rearClusterCapacityTarget) {
          const isNearWarehouse = adjacentKeys.some((adjKey) =>
            context.specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
            || virtual.plannedWarehouses.has(adjKey)
          );
          const isNearHub = adjacentKeys.includes(context.bonusCities.pickRearHubQuarter(bonusFarmCity));
          if (isNearWarehouse || isNearHub) {
            farmScore = Math.max(farmScore, BOT_WEIGHTS.FARM_NEEDED * profile.economy)
              + (isNearWarehouse ? bonusProfile.rearFarmNearWarehouse : bonusProfile.rearFarmHub);
          }
        }
      }
    } else if (context.w36BarracksCap && context.bonusProfile) {
      // Волна 36 (C3, боты золото+): та же надбавка «город без фермы», но фермы
      // города считаются по виртуальному состоянию — с pending этой фазы и
      // цепочкой (citiesCache в фазе не обновляется: ферма прошлого тика не
      // видна, и город получал вторую ферму с надбавкой)
      if (context.w36BarracksCap.countOwnCityTokens(adjacentKeys, COMMON_BUILDING_TOKENS.FARM, virtual) === 0) {
        farmScore += context.bonusProfile.undefendedCityFarmScore;
      }
    } else if (context.bonusProfile) {
      // Бонусный бот: свой город без фермы не прокормит там ни гарнизон, ни
      // подкрепления — отряд вне снабжения теряет размер и гибнет. Одна ферма за
      // цепочку на город, дальше действует обычный конкурс
      const bonusCityWithoutFarm = (context.playerCities || []).find((playerCity) =>
        (playerCity[COMMON_BUILDING_TOKENS.FARM] || []).length === 0
        && getAllQuartersOfCity(playerCity).some((quarterHexKey) => adjacentKeys.includes(quarterHexKey))
      );
      if (bonusCityWithoutFarm) {
        const cityQuarters = new Set(getAllQuartersOfCity(bonusCityWithoutFarm));
        const hasPlannedCityFarm = [...virtual.plannedCells].some((plannedCellKey) =>
          virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.FARM
          && getNeighborHexKeys(plannedCellKey, gridRenderer).some((adjKey) => cityQuarters.has(adjKey))
        );
        if (!hasPlannedCityFarm) {
          farmScore += context.bonusProfile.undefendedCityFarmScore;
        }
      }
    }

    // Цепочка снабжения к центру (нюанс живого теста 11): ферма, дотягивающая
    // снабжение до своего центрального города вне текущей досягаемости, ценнее
    if (farmScore > 0
      && context.centralQuartersOutOfSupply
      && context.centralQuartersOutOfSupply.length > 0
    ) {
      const extendsToCenter = context.centralQuartersOutOfSupply.some(
        (quarterHexKey) => calcHexDistanceLocal(cellKey, quarterHexKey) <= 4
      );
      if (extendsToCenter) {
        farmScore += BOT_WEIGHTS.FARM_CENTRAL_SUPPLY_BONUS;
      }
    }
    // Волна 36 (C1, боты золото+): бронь кольца ЦК — клетка, легальная под
    // базовый квартал, пока в городе меньше цели БК, идет под ферму в
    // последнюю очередь (ферма встанет у базового квартала, на внешнем кольце).
    // Ферма к голодному отряду брони не знает
    if (context.w36RingReserve && farmScore > 0 && !isW36UnsuppliedUnitFarm) {
      farmScore *= context.w36RingReserve(cellKey, adjacentKeys, virtual);
    }
    return farmScore;
  }

  if (token === COMMON_BUILDING_TOKENS.BARRACKS) {
    // Крысолюды до кавалерийского раунда казармы не строят вообще
    // (армия — бесплатные d6 за снабжение на фермах)
    if (context.isRatmen && context.barracksTarget === 0) {
      return 0;
    }

    // Волна 28 (25.2, смягчено): элементалей казарм не больше, чем ферм +
    // ELEMENTALS_BARRACKS_FARM_CAP_MARGIN — сверх этого действие лучше
    // вложить в дорогу под будущие захватные кварталы (common_problems_25)
    if (context.isElementals
      && context.wave28ElementalsBarracksCapActive
      && virtual.barracksCount >= virtual.farmsCount + ELEMENTALS_BARRACKS_FARM_CAP_MARGIN
    ) {
      return 0;
    }

    // Эльфы: казарма в зоне блокировки бесполезна — вражеский отряд успевает
    // встать на нее за 2 раунда подхода (живой тест 14)
    if (context.isElves
      && (context.enemyUnitDistances?.get(cellKey) ?? Infinity) <= 4
    ) {
      return 0;
    }

    // Инсектоиды: казармы не обгоняют рой — на найм больше чем units+2 казарм
    // все равно не хватит монет, стройка впереди найма морит рой голодом
    if (context.isInsectoids && virtual.barracksCount >= playerUnitsCount + 2) {
      return 0;
    }

    // SERVARIANISM (политика или драйв фракции): свои рудники рядом, еще не
    // обслуженные своей казармой — надбавка дохода рудника ниже
    const adjacentUnservedOwnMines = (context.hasServarianism || context.hasServarianismDrive)
      ? countAdjacentUnservedOwnMines(cellKey, adjacentKeys, virtual, context)
      : 0;

    // Волна 36 (C3, боты золото+): кап казарм на город (2, у кузницы — кап
    // кластера), вторая — только при лагере/арсенале в городе или свежем
    // базовом квартале рядом под лагерь. Реплеи: 21% казарм ботов — третья и
    // далее в городе, у людей таких нет; смежно лагерю — 3% казарм ботов.
    // Казарму у необслуженного своего рудника при SERVARIANISM кап не держит:
    // она приносит доход рудника до конца партии и без найма (как и в гейте
    // «3 свободных» ниже); таких казарм не больше одной на рудник
    if (context.w36BarracksCap
      && adjacentUnservedOwnMines === 0
      && context.w36BarracksCap.isCityCapReached(adjacentKeys, virtual)
    ) {
      return 0;
    }

    // Волна 33 (C4): «сильные города» — казарма кузницы (город-staging оси
    // удара). Город кандидата по индексу future-городов; кузница собирает
    // кластер до forgeBarracksCap казарм, лагерь и арсенал рядом с ними.
    // Крысолюдов не касается: их армия — рой с ферм, казармы только под кавалерию.
    // Кузницей считается только город, ОТДЕЛЕННЫЙ от тыла (isForgeCity): при
    // одном городе кузница = тыл, цепочки ферм из тыла нет, и снятие гейтов
    // ниже давало дебют «казарма первой, три казармы до второй фермы» —
    // ровно тот старт, от которого гейт «казарм не больше ферм» защищает.
    // Пока казарм у кузницы меньше forgeBarracksCap — кластер достраивается
    // (forgeBarracksScore, гейт «3 свободных» без казарм кузницы); с капа
    // казарма кузницы идет по общим правилам (все гейты legacy)
    let bonusBarracksCity = null;
    let isForgeBarracks = false;
    if (context.strongCities && !context.isRatmen) {
      bonusBarracksCity = context.bonusCities.getCandidateCity(adjacentKeys);
      isForgeBarracks = Boolean(bonusBarracksCity)
        && context.bonusCities.isForgeCity(bonusBarracksCity)
        && context.bonusCities.countCityAdjacentTokens(bonusBarracksCity, COMMON_BUILDING_TOKENS.BARRACKS)
        < context.bonusProfile.forgeBarracksCap;
    }

    // Ранняя игра: казармы идут парами с фермами (ферма — первой) — казарм
    // не больше, чем ферм (иначе старты вида "3 казармы и 1 ферма").
    // Инсектоидам фермы не нужны — на них правило не распространяется.
    // Волна 33 (C4): кузницу (отделенную от тыла, кластер до капа) не держит —
    // ее кормит цепочка ферм из тыла
    if (!context.isInsectoids && context.round <= 5 && virtual.barracksCount >= virtual.farmsCount
      && !isForgeBarracks
    ) {
      return 0;
    }

    // Волна 19 (18.11b, запечено): при 3+ СВОБОДНЫХ казармах (без своего
    // отряда на них) новые казармы запрещены — кроме владельцев
    // SERVARIANISM/TOTALITARIANISM (у них казармы работают и без найма:
    // доход рудников / полуочки)
    if (!context.hasServarianism
      && !context.hasTotalitarianism
    ) {
      if (isForgeBarracks) {
        // Волна 33 (C4): пока кластер кузницы не собран, свободные казармы
        // считаются ВНЕ ее — пустые казармы самой кузницы это будущий
        // кластер найма, а не повод запретить его достройку
        const plannedBarracksOutsideForge = [...virtual.plannedCells].filter(
          (plannedCellKey) => virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS
            && context.bonusCities.getCandidateCity(getNeighborHexKeys(plannedCellKey, gridRenderer))
            !== bonusBarracksCity
        ).length;
        // Волна 36 (C3): и свободные казармы, поставленные прошлыми тиками этой
        // фазы (в citiesCache их еще нет)
        const w36PendingBarracksOutsideForge = context.w36PendingFreeBarracks
          ? context.w36PendingFreeBarracks.filter((pendingCellKey) =>
            context.bonusCities.getCandidateCity(getNeighborHexKeys(pendingCellKey, gridRenderer))
            !== bonusBarracksCity
          ).length
          : 0;
        if ((context.freeOwnBarracksCountOutsideForge || 0) + plannedBarracksOutsideForge
          + w36PendingBarracksOutsideForge >= 3
        ) {
          return 0;
        }
      } else {
        const plannedBarracksInChain = [...virtual.plannedCells].filter(
          (plannedCellKey) => virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS
        ).length;
        // Волна 36 (C3): плюс свободные pending-казармы прошлых тиков фазы
        const w36PendingBarracksCount = context.w36PendingFreeBarracks
          ? context.w36PendingFreeBarracks.length
          : 0;
        if ((context.freeOwnBarracksCount || 0) + plannedBarracksInChain + w36PendingBarracksCount >= 3) {
          return 0;
        }
      }
    }

    let score = virtual.barracksCount < context.barracksTarget
      ? BOT_WEIGHTS.BARRACKS_NEEDED * profile.military
      : BOT_WEIGHTS.BARRACKS_SURPLUS;

    // Волна 33 (C4): казарма кузницы сверх глобальной цели — база кластера,
    // пока казарм у кузницы меньше forgeBarracksCap; смежность лагерю
    // (реальному или из цепочки) — рост отрядов на месте найма
    if (isForgeBarracks) {
      score = Math.max(score, context.bonusProfile.forgeBarracksScore);
      const isNearCamp = adjacentKeys.some((adjKey) =>
        context.specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
        || virtual.plannedMilitaryCamps.has(adjKey)
      );
      if (isNearCamp) {
        score += context.bonusProfile.barracksNearCamp;
      }
    }

    // SERVARIANISM: казарма смежно своим рудникам поднимает их доход (+1
    // монета/раунд за каждый рудник с рабочей казармой рядом).
    // Волна 19 (18.3, запечено): бонус только за рудники БЕЗ уже смежной
    // своей казармы (вторая казарма у того же рудника дохода не добавляет);
    // драйв действует и для фракций с рекомендованной SERVARIANISM до 6
    // раунда включительно, даже если политика еще не куплена
    // (context.hasServarianismDrive). Рудники посчитаны выше, до капа C3
    if (adjacentUnservedOwnMines > 0) {
      score += adjacentUnservedOwnMines * remainingRounds
        * BOT_WEIGHTS.COINS_TO_POINTS * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND;
    }

    // Казармы ценнее в топологически важных местах — ближе к городам противника,
    // чтобы новые отряды росли рядом с будущими боевыми действиями
    if (score > 0 && context.enemyQuarterDistances) {
      const enemyDistance = context.enemyQuarterDistances.get(cellKey);
      if (enemyDistance !== undefined) {
        score += Math.max(0, 10 - enemyDistance) * BOT_WEIGHTS.BARRACKS_FRONTIER_PROXIMITY * profile.military;
      }
    }

    // Волна 19 (18.2): в раундах 1-5 казарма ближе к важной ключевой точке ценнее
    if (score > 0 && context.kpProximityValues && context.round <= 5) {
      score += (context.kpProximityValues.get(cellKey) || 0)
        * BOT_WEIGHTS.KP_PROXIMITY_ECONOMY_FACTOR;
    }
    // Волна 20 (19.4): казарма в центральной зоне поля (<=4 ячеек от центра) —
    // защита ключевых точек центра требует местного гарнизона
    if (score > 0 && calcHexDistanceLocal(cellKey, '0,0') <= 4) {
      score += BOT_WEIGHTS.CENTRAL_CITY_ECONOMY_FACTOR * profile.military;
    }

    // Нюанс #5: в угрожаемом городе казарма — оборонная необходимость (найм
    // гарнизона на месте), но только если в ЭТОМ городе казармы еще нет,
    // и СТРОГО ОДНА за цепочку — против одного d6 не нужны три казармы.
    // Крысолюдам гарнизонные казармы не нужны — их оборона это рой d6 с ферм.
    // Волна 36 (C3): казармы города — по виртуальному состоянию (pending прошлых
    // тиков фазы видны); (C1) первая казарма угрожаемого города брони кольца не знает
    let isW36ThreatenedCityBarracks = false;
    if (context.threatenedQuarters && !context.isRatmen) {
      const hasPlannedThreatBarracks = [...virtual.plannedCells].some((plannedCellKey) =>
          virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS
          && getNeighborHexKeys(plannedCellKey, gridRenderer).some(
            (adjKey) => context.threatenedQuarters.has(adjKey)
          )
      );
      const threatenedCityWithoutBarracks = !hasPlannedThreatBarracks
        && (context.playerCities || []).find((playerCity) =>
          getAllQuartersOfCity(playerCity).some((quarterHexKey) =>
            context.threatenedQuarters.has(quarterHexKey) && adjacentKeys.includes(quarterHexKey)
          )
          && (context.w36BarracksCap
            ? context.w36BarracksCap.countCityBarracks(playerCity, virtual)
            : (playerCity[COMMON_BUILDING_TOKENS.BARRACKS] || []).length) === 0
        );
      if (threatenedCityWithoutBarracks) {
        score += (BOT_WEIGHTS.DEFEND_OWN_CITY / 2) * profile.military;
        isW36ThreatenedCityBarracks = true;
      }
    }

    // Волна 33 (C4): у «сильных городов» надбавка «город без казармы» — по
    // индексу future-городов: pending-казармы и казармы цепочки видны, вторая
    // казарма тому же городу за фазу не достается (утечка между тиками закрыта)
    if (context.strongCities && !context.isRatmen) {
      if (bonusBarracksCity
        && context.bonusCities.countCityAdjacentTokens(bonusBarracksCity, COMMON_BUILDING_TOKENS.BARRACKS) === 0
      ) {
        score += context.bonusProfile.undefendedCityBarracksScore;
      }
    } else if (context.w36BarracksCap && context.bonusProfile) {
      // Волна 36 (C3, боты золото+): надбавка «город без казармы» — казармы
      // города по виртуальному состоянию (pending этой фазы и цепочка видны)
      if (context.w36BarracksCap.countOwnCityTokens(adjacentKeys, COMMON_BUILDING_TOKENS.BARRACKS, virtual) === 0) {
        score += context.bonusProfile.undefendedCityBarracksScore;
      }
    } else if (context.bonusProfile) {
      // Бонусный бот (см. configs/botBonusBehavior): свой город без казармы —
      // это город, в котором некого нанять в гарнизон. Богатый бот застраивал
      // такие города дорогами и кварталами, а оборонять их было нечем; вес
      // держится выше базового BARRACKS_NEEDED, чтобы выигрывать этот конкурс.
      // СТРОГО ОДНА казарма за цепочку на город — второй гарнизон там не нужен
      const bonusCityWithoutBarracks = (context.playerCities || []).find((playerCity) =>
        (playerCity[COMMON_BUILDING_TOKENS.BARRACKS] || []).length === 0
        && getAllQuartersOfCity(playerCity).some((quarterHexKey) => adjacentKeys.includes(quarterHexKey))
      );
      if (bonusCityWithoutBarracks) {
        const cityQuarters = new Set(getAllQuartersOfCity(bonusCityWithoutBarracks));
        const hasPlannedCityBarracks = [...virtual.plannedCells].some((plannedCellKey) =>
          virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS
          && getNeighborHexKeys(plannedCellKey, gridRenderer).some((adjKey) => cityQuarters.has(adjKey))
        );
        if (!hasPlannedCityBarracks) {
          score += context.bonusProfile.undefendedCityBarracksScore;
        }
      }
    }

    // Элементали: казарма в каждом городе (локальный гарнизон против агрессоров)
    if (context.isElementals && score >= 0) {
      const cityWithoutBarracks = (context.playerCities || []).find((playerCity) =>
        getAllQuartersOfCity(playerCity).some((quarterHexKey) => adjacentKeys.includes(quarterHexKey))
        && (playerCity[COMMON_BUILDING_TOKENS.BARRACKS] || []).length === 0
      );
      if (cityWithoutBarracks) {
        score += 4;
      }
    }

    // Волна 22 (21.15): при SERVARIANISM, пока есть свой рудник без смежной
    // своей казармы, казарма НЕ у рудника резко теряет ценность — смежные
    // рудникам ячейки обязаны выигрывать (живой тест 21: казармы все еще
    // ставились в стороне от рудников).
    // Волна 22.1 (отчет №18 §4.3): демотирование сужено до КУПЛЕННОЙ политики —
    // фракционный драйв (гномы/просвещенные/лепреконы/вентори до 6 раунда)
    // сохраняет только позитивный бонус волны 19, без штрафа прочим казармам
    if (context.hasServarianism
      && context.hasUnservedOwnMines
      && score > 0
    ) {
      const isAdjacentToOwnMine = adjacentKeys.some((adjKey) =>
        context.ownMineCells?.has(adjKey) || virtual.plannedMineCells?.has(adjKey));
      if (!isAdjacentToOwnMine) {
        score *= 0.25;
      }
    }

    // Волна 36 (C1, боты золото+): бронь кольца ЦК — казарма на клетке,
    // легальной под базовый квартал, пока в городе меньше цели БК, идет в
    // последнюю очередь (реплеи: 80% казарм ботов — в город без БК, у людей 33%)
    if (context.w36RingReserve && score > 0 && !isW36ThreatenedCityBarracks) {
      score *= context.w36RingReserve(cellKey, adjacentKeys, virtual);
    }

    return score;
  }

  if (token === MINE_EXPLORATION) {
    // Гномы: мат.ожидание разведки — 2/3 рудник (1 монета) + 1/3 великий (2 монеты).
    // Первые раунды — фокус на рудники; ячейка разведки предпочитается такая,
    // которая заодно прокладывает маршрут к неконтролируемым точкам поля
    // (построенный рудник — и доход, и непустая ячейка пути строительства)
    const earlyFocusMultiplier = context.round <= 3 ? 1.5 : 1;
    const routeValue = context.keyPointApproachValues
      ? (context.keyPointApproachValues.get(cellKey) || 0) * remainingRounds
      * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND * BOT_WEIGHTS.ROAD_KEY_POINT_APPROACH_FACTOR
      * profile.keyPoint
      : 0;
    return (4 / 3) * BOT_WEIGHTS.COINS_TO_POINTS * remainingRounds
      * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND * profile.economy * earlyFocusMultiplier
      + routeValue;
  }

  if (token === COMMON_BUILDING_TOKENS.ROAD) {
    // Волна 19 (18.8, запечено): лимит дорог за фазу СНЯТ — он мешал
    // прокладке путей перемещения к городам противника (rush рано, buildup
    // после 5 раунда)

    // Волна 22 (21.8b/21.10/21.11): ячейку, где центральный квартал взял бы
    // сразу 2+ неконтролируемые ключевые точки, дорогой не занимаем — при
    // монетах на сам ЦК и отсутствии перевеса противника в отрядах в радиусе
    // 3 ячеек (живой тест 21: боты ставили дорогу там, где ЦК брал 2 точки)
    {
      const uncontrolledAdjacentKeyPoints = adjacentKeys.filter((adjKey) =>
        checkIfKeyPointToken(virtual.hexes[adjKey]) && !virtual.controlledKeyPoints.has(adjKey)
      ).length;
      if (uncontrolledAdjacentKeyPoints >= 2
        && virtual.coins >= (context.centralQuarterPriceCoins ?? Infinity)
        && (!context.checkIfCellCanHostQuarter || context.checkIfCellCanHostQuarter(cellKey))
        && (!context.checkIfEnemyOutweighsNear || !context.checkIfEnemyOutweighsNear(cellKey))
      ) {
        return 0;
      }
    }

    // Волна 27 (24.3): просвещенные в раундах 1-4 не строят дороги в сторону
    // городов ЖИВЫХ противников, кроме немедленных путей влияния уже
    // построенных гильдий — живой тест 24: бесполезные дороги к противнику в
    // дебюте вместо экономического роста, да еще и путь для его атаки.
    // Дороги к рудникам/центру/нейтральным городам не ограничиваются
    if (context.enlightenedEarlyRoadGateActive && context.livingOpponentCityDistances) {
      const immediateInfluenceDelta = context.roadInfluenceDeltas
        ? (context.roadInfluenceDeltas.get(cellKey) || 0)
        : 0;
      const isTowardLivingOpponentCity =
        (context.livingOpponentCityDistances.get(cellKey) ?? Infinity)
        < (context.minOwnCityLivingOpponentDistance ?? Infinity);
      if (isTowardLivingOpponentCity && immediateInfluenceDelta <= 0) {
        return 0;
      }
    }

    // Неагрессивные фракции без военного превосходства не строят дороги в
    // сторону агрессивных фракций — такой путь лишь облегчает нападающему марш
    // (нюансы живых тестов 12.2 и 13.4)
    if ((context.isElementalsAvoidingAggressors || context.isAvoidingAggressorsDirection)
      && context.aggressiveCityDistances) {
      const aggressorDistance = context.aggressiveCityDistances.get(cellKey);
      if (aggressorDistance !== undefined
        && context.minOwnCityAggressorDistance !== undefined
        && aggressorDistance < context.minOwnCityAggressorDistance
      ) {
        return 0;
      }
    }

    // Волна 19 (18.4, запечено): дороги, тянущиеся к городам раш-фракций
    // (крысолюды, орки, инсектоиды, демоны), — только при значительном
    // военном превосходстве с планом захвата их города, либо если дорога
    // открывает новые пути влияния (иначе это дорога для марша нападающего)
    if (context.rushCityDistances) {
      const rushDistance = context.rushCityDistances.get(cellKey);
      const influenceDeltaForRushGate = context.roadInfluenceDeltas
        ? (context.roadInfluenceDeltas.get(cellKey) || 0)
        : 0;
      if (rushDistance !== undefined
        && context.minOwnCityRushDistance !== undefined
        && rushDistance < context.minOwnCityRushDistance
        && influenceDeltaForRushGate <= 0
        && !context.hasRushCaptureSuperiority
      ) {
        return 0;
      }
    }

    // Дорога ценна только к еще НЕ обслуженной ключевой точке:
    // одной примыкающей дороги/строения достаточно для будущего центрального
    // квартала, вторая дорога к той же точке — потраченное впустую действие.
    // Дорога несет часть ценности квартала, который она откроет, — иначе
    // жадный планировщик никогда не начнет цепочку "дорога -> центральный квартал".
    let bestUnservedKeyPointValue = 0;
    adjacentKeys.forEach((adjKey) => {
      const adjToken = virtual.hexes[adjKey];
      if (!checkIfKeyPointToken(adjToken) || virtual.controlledKeyPoints.has(adjKey)) {
        return;
      }
      if (checkIfKeyPointServed(adjKey, virtual, context)) {
        return;
      }
      bestUnservedKeyPointValue = Math.max(bestUnservedKeyPointValue, getKeyPointValuePerRound(adjToken, context.round, context.ruinsIncomeRoundLimit));
    });

    // Нюанс #3: дорога, открывающая пути влияния к городам противника,
    // увеличивает доход гильдий/храмов каждый раунд.
    // Волна 25 (23.5): военные профили больше не занижают ценность таких дорог
    // (max(1, influence)), а дорога с немедленным приростом дохода получает
    // флат-бонус до 12 раунда включительно — живой тест 23: боты систематически
    // не достраивали пути влияния от уже стоящих гильдий/храмов
    const influenceDelta = context.roadInfluenceDeltas
      ? (context.roadInfluenceDeltas.get(cellKey) || 0)
      : 0;
    let influenceScore = 0;
    if (influenceDelta > 0) {
      const influenceProfileFactor = context.influenceRoadBoostActive
        ? Math.max(1, profile.influence)
        : profile.influence;
      influenceScore = influenceDelta * remainingRounds
        * BOT_WEIGHTS.INFLUENCE_ROAD_PER_INCOME_PER_ROUND * influenceProfileFactor;
      if (context.influenceRoadBoostActive && context.round <= 12) {
        influenceScore += BOT_WEIGHTS.INFLUENCE_ROAD_IMMEDIATE_BONUS;
      }
    }

    // Коридор наступления: дорога внутри цепочки снабжения ферм в сторону
    // городов противника продлевает снабжение армии на марше (путь снабжения
    // идет по непустым ячейкам) — иначе отряды застревают на границе снабжения.
    // При ЭКОАНАРХИЗМЕ снабжение достает везде — коридоры не нужны.
    // Крысолюдам коридоры критичны: рой d6 должен маршировать к противнику.
    // Волна 32, раунд 5: коридор только к живым противникам (L1) удален вместе с
    // гейтом isPreW32CorridorPlayer (A/B r4: −0.59 очка)
    let corridorScore = 0;
    if (context.supplyReachCells
      && context.enemyQuarterDistances
      && context.supplyReachCells.has(cellKey)
      && playerUnitsCount > 0
      && profile.captureEagerness >= 1
      && !context.isEcoanarchism
      && !context.isElementals
    ) {
      const enemyDistance = context.enemyQuarterDistances.get(cellKey);
      // Коридор строится только ВПЕРЕД от своей базы — дороги «в никуда»
      // (вбок/назад) не имеют коридорной ценности
      if (enemyDistance !== undefined
        && (context.minOwnCityEnemyDistance === undefined || enemyDistance < context.minOwnCityEnemyDistance)
      ) {
        // Крысолюдам коридоры критичны всегда; инсектоидам — в раше ВАРВАРСТВА
        // (дороги ведут рой d6 к ближайшему уязвимому противнику).
        // Волна 27 (24.5): зверолюдам — тоже: без дорог к городу противника их
        // выросшие отряды бездействуют дома (common_problems_24)
        let corridorFactionMultiplier = 1;
        if (context.isRatmen) {
          corridorFactionMultiplier = BOT_WEIGHTS.RATMEN_ROAD_CORRIDOR_MULTIPLIER;
        } else if (context.isInsectoidBarbarismRush) {
          corridorFactionMultiplier = BOT_WEIGHTS.RATMEN_ROAD_CORRIDOR_MULTIPLIER;
        } else if (context.isBeastmen && context.wave27BeastmenCorridorActive) {
          corridorFactionMultiplier = BOT_WEIGHTS.RATMEN_ROAD_CORRIDOR_MULTIPLIER;
        }
        corridorScore = Math.max(0, 8 - enemyDistance)
          * BOT_WEIGHTS.ROAD_SUPPLY_CORRIDOR * profile.military * corridorFactionMultiplier;
      }
    }

    // Дорога на кратчайшем пути к цели АКТИВНОЙ захватной кампании: армия и
    // стройка делят один вектор — отрядам нужен короткий путь, а не обходной
    // (нюансы живого теста 12.3/12.5)
    // Волна 32, раунд 5: такие дороги за конвертом снабжения (L2) удалены вместе с
    // гейтом isPreW32CampaignRoadPlayer (A/B r4: эффекта нет)
    let campaignPathScore = 0;
    if (context.campaignTargetDistances
      && context.supplyReachCells
      && context.supplyReachCells.has(cellKey)
    ) {
      const campaignDistance = context.campaignTargetDistances.get(cellKey);
      if (campaignDistance !== undefined
        && (context.minOwnCityCampaignDistance === undefined
          || campaignDistance < context.minOwnCityCampaignDistance)
      ) {
        campaignPathScore = Math.max(0, 10 - campaignDistance)
          * BOT_WEIGHTS.ROAD_CAMPAIGN_PATH_FACTOR * profile.military;
      }
    }

    // Орки в режиме экспансии: дороги к слабейшему противнику — маршрут
    // будущего марша армии (живой тест 14)
    let orcExpansionRoadScore = 0;
    if (context.orcExpansionDistances
      && context.supplyReachCells
      && context.supplyReachCells.has(cellKey)
    ) {
      const orcDistance = context.orcExpansionDistances.get(cellKey);
      if (orcDistance !== undefined
        && (context.minOwnCityOrcExpansionDistance === undefined
          || orcDistance < context.minOwnCityOrcExpansionDistance)
      ) {
        orcExpansionRoadScore = Math.max(0, 10 - orcDistance)
          * BOT_WEIGHTS.ROAD_CAMPAIGN_PATH_FACTOR * profile.military;
      }
    }

    // При ЭКОАНАРХИЗМЕ проходные дороги (маршруты кампаний и экспансии) не
    // строятся — снабжение достает везде, дороги только ради влияния и
    // подключения ключевых точек (16.13)
    if (context.isEcoanarchism) {
      campaignPathScore = 0;
      orcExpansionRoadScore = 0;
    }

    // Под угрозой своим городам дороги не ко времени — сначала оборона
    const roadThreatFactor = context.threatenedQuarters && context.threatenedQuarters.size > 0 ? 0.3 : 1;

    // Волна 20 (19.6): дорога, у которой 3 и более из 6 смежных ячеек уже
    // не пустые (сплошная застроенная цепочка вокруг), — тупиковый довесок к
    // существующей сети, строить запрещено.
    // Волна 25 (23.5): дорога с немедленным приростом дохода влияния — не
    // тупик, а замыкающее звено пути (путь влияния идет по непустым ячейкам,
    // застроенность вокруг для нее норма) — фильтр ее не касается
    const filledAdjacentCount = adjacentKeys.filter((adjKey) => Boolean(virtual.hexes[adjKey])).length;
    if (filledAdjacentCount >= 3 && !(context.influenceRoadBoostActive && influenceDelta > 0)) {
      return 0;
    }

    // Скученность: дорога с 3+ смежными дорогами почти наверняка дублирует
    // уже существующую сеть (16.13)
    let roadClusterFactor = 1;
    const adjacentRoadsCount = adjacentKeys.filter(
      (adjKey) => virtual.hexes[adjKey] === TOKENS.ROAD
    ).length;
    if (adjacentRoadsCount >= 3) {
      roadClusterFactor = 0.4;
    }

    // Волна 33 (C8): у «сильных городов» подходные дороги и дороги-опоры к
    // точкам вдали от сформированных отрядов дешевле (та же ступень, что у ЦК);
    // дорога на пути «фермы тыла → кварталы кузницы» замыкает цепочку снабжения
    const bonusRoadDistanceFactor = context.strongCities ? context.distanceFactor(cellKey) : 1;
    const supplyChainRoadScore = context.strongCities
      && context.supplyChainPathCells
      && context.supplyChainPathCells.has(cellKey)
      ? context.bonusProfile.supplyChainRoad
      : 0;

    if (bestUnservedKeyPointValue === 0) {
      // Подход к дальней ценной точке (смежная ветка ниже дает полную ценность):
      // цепочка дорог тянется к центру, следующая дорога всегда чуть ценнее
      let approachScore = 0;
      if (context.keyPointApproachValues) {
        const approachValue = context.keyPointApproachValues.get(cellKey) || 0;
        if (approachValue > 0) {
          approachScore = approachValue * remainingRounds
            * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND * BOT_WEIGHTS.ROAD_KEY_POINT_APPROACH_FACTOR
            * profile.keyPoint;
          if (context.strongCities) {
            approachScore *= bonusRoadDistanceFactor;
          }
        }
      }
      const approachRoadScore = (BOT_WEIGHTS.ROAD_DEFAULT + influenceScore + corridorScore + approachScore
        + campaignPathScore + orcExpansionRoadScore + supplyChainRoadScore) * roadThreatFactor * roadClusterFactor;
      // Волна 32 (экономика, E3): дорога-опора ЦК у руин в этом же ходу. Бонус — после
      // всех защитных гейтов выше (двойной захват, просвещенные, агрессоры, раш, 3+ соседей)
      const e32ChainInfo = context.getE32ChainRoadInfo ? context.getE32ChainRoadInfo(cellKey) : null;
      return e32ChainInfo ? Math.max(approachRoadScore, e32ChainInfo.score) : approachRoadScore;
    }

    const keyPointEnablementScore = bestUnservedKeyPointValue * remainingRounds
      * BOT_WEIGHTS.KEY_POINT_CONTROL_PER_ROUND * BOT_WEIGHTS.ROAD_KEY_POINT_ENABLEMENT_FACTOR
      * profile.keyPoint;
    const keyPointRoadScore = (BOT_WEIGHTS.ROAD_TOWARD_KEY_POINT
      + influenceScore
      + corridorScore
      + campaignPathScore
      + orcExpansionRoadScore
      + supplyChainRoadScore
      + (context.strongCities ? keyPointEnablementScore * bonusRoadDistanceFactor : keyPointEnablementScore)
    ) * roadThreatFactor * roadClusterFactor;
    const e32ChainInfo = context.getE32ChainRoadInfo ? context.getE32ChainRoadInfo(cellKey) : null;
    return e32ChainInfo ? Math.max(keyPointRoadScore, e32ChainInfo.score) : keyPointRoadScore;
  }

  // Спецстроения — только после базовой экономики: без ферм армия голодает,
  // а храм/гильдия на единственном квартале блокируют развитие.
  // Инсектоидам фермы не нужны — их базовая экономика это казарма.
  // Крысолюдам не нужны казармы — их базовая экономика это фермы.
  let hasBasicEconomy;
  if (context.isInsectoids) {
    hasBasicEconomy = virtual.barracksCount >= 1;
  } else if (context.isRatmen) {
    hasBasicEconomy = virtual.farmsCount >= 2;
  } else {
    hasBasicEconomy = virtual.farmsCount >= 2 && virtual.barracksCount >= 1;
  }
  // Ранние гильдии с OLIGARCHY — ядро дебюта (пути влияния к нейтралам со
  // 2 раунда), гейт экономики для гильдий смягчается до 1 фермы: вентори,
  // просвещенные, элементали (олигархия-опенинг, 16.11). Волна 18 (17.9): в
  // активном опенинге ферма не требуется — серия 2 квартала + 2 гильдии all-in
  const isOligarchyEarlyGuild = token === SPECIAL_BUILDING_TOKENS.GUILD
    && context.hasOligarchy
    && (context.oligarchyOpeningActive || virtual.farmsCount >= 1)
    && (context.isVentory || context.isEnlightened || context.isElementals);

  if (SPECIAL_CANDIDATE_TOKENS.includes(token) && !hasBasicEconomy && !isOligarchyEarlyGuild) {
    return 0;
  }

  // Инсектоиды, крысолюды и зверолюди: гильдии/храмы только при немедленных
  // путях влияния и не в поздней игре
  if ((context.isInsectoids || context.isRatmen || context.isBeastmen)
    && (token === SPECIAL_BUILDING_TOKENS.GUILD || token === SPECIAL_BUILDING_TOKENS.TEMPLE)
  ) {
    // Волна 32 (экономика, E7, гейт isPreW32RushTemplePlayer): храм с немедленным
    // путем влияния окупается и в R10-12 (+3..+9 очков), запрет оставлен гильдиям.
    // Поздний гейт храма (R12 — 1+ путь, R13 — 3+) действует как раньше
    if (context.round >= 10
      && !(context.e32RushTempleActive && token === SPECIAL_BUILDING_TOKENS.TEMPLE)
    ) {
      return 0;
    }
    const {count} = calculateInfluencePathIncome(cellKey, context.enemyCitiesForInfluence, virtual.hexes);
    if (count < 1) {
      return 0;
    }
  }

  // Безопасное размещение для строений купленных политик (живой тест 13.1/13.2):
  // город без угрозы (тыл/спокойный) либо фронтирный с гарнизоном не слабее угрозы
  const checkIfSafePolicyPlacement = (placementCellKey) => {
    const placementCity = (context.playerCities || []).find((playerCity) =>
      getAllQuartersOfCity(playerCity).includes(placementCellKey)
    );
    if (!placementCity) {
      return false;
    }
    const placementThreat = (context.cityThreats || []).find((threat) => threat.city === placementCity);
    if (!placementThreat) {
      return true;
    }
    return placementThreat.garrison >= placementThreat.enemyStrengthNear;
  };

  if (token === SPECIAL_BUILDING_TOKENS.GUILD) {
    // Волна 20 (19.12): агрессивные архетипы никогда не строят гильдию, если
    // она не дает путь влияния СРАЗУ при постройке — отложенная окупаемость
    // экономического строения не оправдана для военного профиля.
    // Исключение: активный олигархия-опенинг (вентори/просвещенные/элементали,
    // см. OLIGARCHY_OPENING_FACTIONS) — их 2 гильдии авансом это осознанная
    // доктрина фракции, а не случайная постройка временно агрессивного архетипа
    // (архетип этих ботов может на раунд-два переключиться в AGGRESSIVE_RUSH
    // при слабом соседе и обратно — гильдии опенинга не должны от этого страдать)
    // Волна 36 (C5, боты золото+): military архетипа без бонусного множителя
    // (иначе экономический архетип 0.8 у бонусного бота «военный»), у
    // экономической доктрины — без разовой смены архетипа на агрессивный
    if ((context.w36GateMilitary ?? profile.military) > 1 && !context.oligarchyOpeningActive) {
      const {count: immediateGuildInfluenceCount} = calculateInfluencePathIncome(
        cellKey, context.enemyCitiesForInfluence, virtual.hexes
      );
      if (immediateGuildInfluenceCount < 1) {
        return 0;
      }
    }
    // Проекция реального дохода с путями влияния от этой ячейки (в монетах).
    // Элементали: экономика путей влияния — их доктрина (гильдии в ранней игре).
    // OLIGARCHY: купленная политика обязана работать — фокус на 2 гильдии
    // в безопасных размещениях с путями влияния
    const guildElementalsMultiplier = context.isElementals && context.round <= 7 ? 1.5 : 1;
    // Волна 21 (20.3): просвещенные мало строят гильдий на живых тестах —
    // отдельный устойчивый буст (не только первые 2 под олигархией, как
    // guildOligarchyMultiplier ниже), сильнее при купленной OLIGARCHY, где
    // несколько путей влияния особенно окупаются
    const guildEnlightenedMultiplier = context.isEnlightened
      ? (context.hasOligarchy ? 1.8 : 1.3)
      : 1;
    const guildOligarchyMultiplier = context.hasOligarchy
    && (context.ownGuildsCount || 0) < 2
    && checkIfSafePolicyPlacement(cellKey)
      ? 2 : 1;
    const projectedIncome = projectInfluenceBuildingIncome(
      SPECIAL_BUILDING_TOKENS.GUILD, cellKey, context.currentPlayer, context.enemyCitiesForInfluence, virtual.hexes
    );
    const guildIncomeScore = projectedIncome * BOT_WEIGHTS.COINS_TO_POINTS * remainingRounds
      * BOT_WEIGHTS.SPECIAL_INCOME_PER_ROUND * ((profile.economy + profile.influence) / 2)
      * guildElementalsMultiplier * guildEnlightenedMultiplier * guildOligarchyMultiplier
      * (context.w39IncomeSpecialMultiplier || 1);
    // Волна 18: в олигархия-опенинге ранний доход путей влияния мал — гильдия
    // получает флат-приоритет, чтобы обе гильдии гарантированно построились
    // (пути влияния дозреют дорогой к нейтралу к 3-му раунду). Размещение на
    // свежем базовом квартале этой цепочки — тыл опенинга, проверку угрозы
    // (по реальным городам) пропускаем, если ячейка запланирована в этом ходу
    if (context.oligarchyOpeningActive
      && (virtual.plannedCells.has(cellKey) || checkIfSafePolicyPlacement(cellKey))
    ) {
      return Math.max(guildIncomeScore, BOT_WEIGHTS.FARM_NEEDED * 1.6);
    }
    return guildIncomeScore;
  }
  if (token === SPECIAL_BUILDING_TOKENS.TEMPLE) {
    // Волна 21 (20.2, запечено): просвещенные не строят храмы вообще без THEOCRACY —
    // без бонуса дохода политики храм для них просто дорогая гильдия с
    // худшей отдачей (живой тест 20: строили храмы впустую под другими политиками)
    if (context.isEnlightened && !context.hasTheocracy) {
      return 0;
    }
    // Нюанс #3: ранний храм без экономического/военного преимущества — рискованная
    // инвестиция (делает игрока целью). Разрешен рано только архетипу ECONOMIC_POINTS
    // и владельцу THEOCRACY в безопасном размещении (политика обязана работать)
    const isTheocracySafePlacement = context.hasTheocracy && checkIfSafePolicyPlacement(cellKey);
    // Волна 36 (C5, боты золото+): экономической доктрине храм в нефронтирном
    // городе разрешен с W36_EARLY_TEMPLE_MIN_ROUND (Сципион-Вентори: 0 гильдий,
    // 2 храма за партию при +1 к доходу храмов)
    if (!profile.earlyTempleAllowed && context.round <= 5 && !isTheocracySafePlacement
      && !(context.w36EarlyTemple && context.w36EarlyTemple(cellKey))
    ) {
      return 0;
    }
    // Волна 25 (23.4): храм в конце партии не окупается без немедленной отдачи
    // (живой тест 23: храмы в 12-13 раундах без единого пути влияния).
    // 12 раунд — только при 1+ пути влияния сразу после постройки или THEOCRACY;
    // 13 раунд — только THEOCRACY или 3+ пути сразу
    if (context.lateTempleGateActive && context.round >= FINAL_ROUND - 1 && !context.hasTheocracy) {
      const {count: immediateTemplePathsCount} = calculateInfluencePathIncome(
        cellKey, context.enemyCitiesForInfluence, virtual.hexes
      );
      const minImmediatePaths = context.round >= FINAL_ROUND ? 3 : 1;
      if (immediateTemplePathsCount < minImmediatePaths) {
        return 0;
      }
    }
    // Проекция реального дохода с путями влияния от этой ячейки (в очках).
    // THEOCRACY дает большой бонус дохода храмов — фокус на храмы в каждом городе.
    // Элементали: храмы — доктрина средней/поздней игры (пути влияния)
    const projectedIncome = projectInfluenceBuildingIncome(
      SPECIAL_BUILDING_TOKENS.TEMPLE, cellKey, context.currentPlayer, context.enemyCitiesForInfluence, virtual.hexes
    );
    const theocracyMultiplier = context.hasTheocracy ? (isTheocracySafePlacement ? 2 : 1.6) : 1;
    const templeElementalsMultiplier = context.isElementals && context.round >= 6 ? 1.5 : 1;
    // Волна 23 (22.2, запечена): HEGEMONISM — храм в бонусной зоне центра дает +2 очка/раунд
    const hegemonismTempleMultiplier = context.hasHegemonism
    && isHegemonismBonusHexKey(cellKey) ? 1.6 : 1;
    return projectedIncome * remainingRounds
      * BOT_WEIGHTS.SPECIAL_INCOME_PER_ROUND * ((profile.economy + profile.influence) / 2)
      * theocracyMultiplier * templeElementalsMultiplier * hegemonismTempleMultiplier
      * (context.w39IncomeSpecialMultiplier || 1);
  }
  if (token === SPECIAL_BUILDING_TOKENS.PALACE) {
    // Бонусный бот: дворец дает ровно одно дополнительное действие
    // строительства и ничего больше, а действия у такого бота уже в избытке от
    // самого бонуса. Значит это чистый убыток: монеты, действие на постройку и
    // лакомая цель захвата в своем городе
    if (context.bonusProfile?.hasConstructionActionsBonus) {
      return 0;
    }
    // Волна 22 (21.8a): второй дворец запрещен — дополнительное действие
    // строительства уже есть, лишние дворцы жгут профицит и создают лакомую
    // цель захвата (живой тест 21: боты «полили деньги» дворцами)
    if ((context.ownPalacesCount || 0) + virtual.plannedPalaces.size >= 1) {
      return 0;
    }
    // Крысолюды: лишние действия строительства ускоряют ферменную экономику.
    // Профицит монет (20+): дворец — полезный сток вместо мертвого запаса.
    // Гномы с 4 раунда без угрозы: дворец ускоряет разведку рудников и захваты
    let palaceMultiplier = context.isRatmen ? 1.5 : 1;
    if (context.hasCoinSurplus) {
      palaceMultiplier *= 1.5;
    }
    if ((context.isDwarves || context.isVentory) && context.round >= 4
      && (!context.threatenedQuarters || context.threatenedQuarters.size === 0)
    ) {
      palaceMultiplier *= 1.4;
    }
    // Волна 22 (21.9a): дворец — лакомая цель захвата, предпочитаем самый
    // удаленный от ЖИВЫХ противников город (тыл), близкие размещения штрафуются
    if (context.realEnemyQuarterDistances) {
      const nearestEnemyDistance = context.realEnemyQuarterDistances.get(cellKey);
      if (nearestEnemyDistance !== undefined) {
        palaceMultiplier *= 0.5 + Math.min(1, nearestEnemyDistance / 8);
      }
    }
    return Math.max(0, remainingRounds - 4)
      * BOT_WEIGHTS.PALACE_PER_REMAINING_ROUND * profile.economy * palaceMultiplier;
  }
  if (token === SPECIAL_BUILDING_TOKENS.WAREHOUSE) {
    const adjacentFarms = adjacentKeys.filter((adjKey) => virtual.hexes[adjKey] === COMMON_BUILDING_TOKENS.FARM).length;
    // Крысолюды: склад только на квартале с 2+ смежными фермами, зато максимально
    // рано — он увеличивает снабжение ферм, то есть рост армии d6
    if (context.isRatmen) {
      if (adjacentFarms < 2) {
        return 0;
      }
      return adjacentFarms * remainingRounds
        * BOT_WEIGHTS.WAREHOUSE_PER_ADJACENT_FARM_PER_ROUND * profile.economy
        * BOT_WEIGHTS.RATMEN_WAREHOUSE_MULTIPLIER;
    }
    let warehouseScore = adjacentFarms * remainingRounds
      * BOT_WEIGHTS.WAREHOUSE_PER_ADJACENT_FARM_PER_ROUND * profile.economy;
    // Волна 23 (22.1b): AGRARIANISM удешевляет склад на 4 монеты, а склад
    // удваивает снабжение смежных ферм — на квартале с 2+ фермами приоритет высокий
    if (context.hasAgrarianism && adjacentFarms >= 2) {
      warehouseScore += BOT_WEIGHTS.AGRARIANISM_WAREHOUSE_BOOST;
    }
    // Волна 33 (C5): «сильные города» — склад это ядро тылового кластера
    // снабжения (кольцо ферм с удвоенной емкостью); во фронтирном городе,
    // кроме кузницы, склад — добыча захватчика
    if (context.strongCities) {
      const warehouseCity = context.bonusCities.getCityByQuarterKey(cellKey);
      if (warehouseCity) {
        const warehouseCityClass = context.bonusCities.classifyCity(warehouseCity);
        if (warehouseCityClass === 'rear' && adjacentFarms >= 2) {
          warehouseScore += context.bonusProfile.rearWarehouseBoost;
        } else if (warehouseCityClass === 'frontier' && !context.bonusCities.isStagingCity(warehouseCity)) {
          warehouseScore *= 0.5;
        }
      }
    }
    return warehouseScore;
  }
  if (token === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP) {
    // Крысолюды растят армию на фермах, а не на казармах — лагерь им не нужен
    if (context.isRatmen) {
      return 0;
    }
    const adjacentBarracks = adjacentKeys.filter((adjKey) => virtual.hexes[adjKey] === COMMON_BUILDING_TOKENS.BARRACKS).length;
    // Зверолюди: рост на казарме у лагеря +5 вместо +3 — лагерь вдвое ценнее.
    // Инсектоиды: ровно ОДИН лагерь и только смежно 2+ казармам — второй лагерь
    // и лагерь при одной казарме съедают деньги, нужные на найм отрядов
    let factionMultiplier = 1;
    if (context.isBeastmen) {
      factionMultiplier = 2;
    } else if (context.isInsectoids) {
      if (adjacentBarracks < 2 || context.ownMilitaryCampsCount + virtual.plannedMilitaryCamps.size >= 1) {
        return 0;
      }
      factionMultiplier = 3;
    }
    let campScore = adjacentBarracks * remainingRounds
      * BOT_WEIGHTS.MILITARY_CAMP_PER_ADJACENT_BARRACKS_PER_ROUND * profile.military * factionMultiplier;
    // Волна 33 (C6): «сильные города» — лагерь в кузнице у ее казарм: отряды
    // растут там же, где нанимаются и откуда идут на противника
    if (context.strongCities && adjacentBarracks >= 1) {
      const campCity = context.bonusCities.getCityByQuarterKey(cellKey);
      if (campCity && context.bonusCities.isStagingCity(campCity)) {
        campScore += context.bonusProfile.forgeCampBoost;
      }
    }
    return campScore;
  }
  if (token === SPECIAL_BUILDING_TOKENS.ARSENAL) {
    // Волна 32 (экономика, E6): в последнем раунде найма нет — арсенал бесполезен
    if (context.e32LateSpecialsActive && context.isFinalRound) {
      return 0;
    }
    // Инсектоидам арсенал почти всегда невыгоден: их сила — количество отрядов,
    // а не разгон найма размера за монеты
    if (context.isInsectoids) {
      return 0;
    }
    // Волна 27 (24.1/24.5): крысолюдам и зверолюдам арсенал не строится
    // никогда — рой d6 размер за монеты не докупает, а зверолюды и так растут
    // быстрее всех на казармах (common_problems_24)
    if (context.wave27ArsenalBanActive && (context.isRatmen || context.isBeastmen)) {
      return 0;
    }
    if (playerUnitsCount === 0) {
      return 0;
    }
    // Эльфы: арсенал — ядро доктрины крупных отрядов (дешевый найм размера),
    // а с PATRIOTISM дешевле и сам арсенал, и найм (живой тест 14)
    let arsenalFactionMultiplier = 1;
    if (context.isElves) {
      arsenalFactionMultiplier = context.hasPatriotism ? 2.5 : 1.5;
    } else if (context.isLeprechauns) {
      // Лепреконы: арсенал форсируется только под РЕАЛЬНЫЙ найм размера
      // (недорощенный отряд стоит на своей казарме) и только первый —
      // безусловный буст волны 14 давал 0.9 арсенала за партию и просадку
      // темпа захватов у владельцев PATRIOTISM (отчет №9).
      const hasSizeHireCandidate = (context.playerUnits || []).some((unit) =>
        context.ownBarracksCells?.has(unit.hexKey)
        && unit.size < (getUnitTypeConfig(unit.type)?.maxSize || 0) * 0.8
      );
      if (hasSizeHireCandidate && (context.ownArsenalsCount || 0) === 0) {
        arsenalFactionMultiplier = context.hasPatriotism ? 2.5 : 1.5;
      }
    }

    // Арсенал полезен только в городе размещения: он ускоряет найм размера
    // с кварталов ЭТОГО города для отрядов на казармах ЭТОГО города.
    // Без казарм в городе арсенал бесполезен.
    const city = (context.playerCities || []).find((playerCity) =>
      getAllQuartersOfCity(playerCity).includes(cellKey)
    );
    if (!city) {
      return 0;
    }

    const cityQuarters = getAllQuartersOfCity(city);
    const plannedBarracksInCity = [...virtual.plannedCells].filter((plannedCellKey) =>
      virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS
      && getNeighborHexKeys(plannedCellKey, gridRenderer).some((adjKey) => cityQuarters.includes(adjKey))
    ).length;
    const cityBarracksCount = (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).length + plannedBarracksInCity;
    if (cityBarracksCount === 0) {
      return 0;
    }

    let arsenalScore = cityQuarters.length * remainingRounds
      * BOT_WEIGHTS.ARSENAL_PER_CITY_QUARTER_PER_ROUND * profile.military * arsenalFactionMultiplier;

    // Оборонный ответ: арсенал в угрожаемом казарменном городе — максимальный
    // найм размера именно там, где идет бой
    if (context.threatenedQuarters
      && cityQuarters.some((quarterHexKey) => context.threatenedQuarters.has(quarterHexKey))
    ) {
      arsenalScore += BOT_WEIGHTS.DEFEND_OWN_CITY / 3;
    }

    // Волна 33 (C7): «сильные города» — арсенал в кузнице (казарма там уже
    // есть, см. гейт выше): дешевая докупка размера с кварталов кузницы
    if (context.strongCities && city.centralQuarter === context.bonusAxis.stagingCityKey) {
      arsenalScore += context.bonusProfile.forgeArsenalBoost;
    }

    return arsenalScore;
  }

  if (token === SPECIAL_BUILDING_TOKENS.FORTRESS) {
    // Крепость — инструмент СЛАБОГО защитника (нюанс живого теста 12.6:
    // 47% крепостей строили доминирующие по армии — пустые инвестиции).
    // Требования: город под угрозой, угроза сильнее гарнизона, армия не доминирует
    if (!context.threatenedQuarters || context.threatenedQuarters.size === 0) {
      return 0;
    }
    // Живой тест 15.1: крепость бессмысленна, если противник успевает ее
    // заблокировать до достройки (любой чужой отряд в 2 ячейках) или занять
    // ближайшим ходом (отряд игрока, ходящего раньше бота, в 3 ячейках)
    if ((context.enemyUnitDistances?.get(cellKey) ?? Infinity) <= 2) {
      return 0;
    }
    if ((context.earlierEnemyUnitDistances?.get(cellKey) ?? Infinity) <= 3) {
      return 0;
    }
    if (!context.hasArmyInferiority) {
      return 0;
    }
    const fortressCity = (context.playerCities || []).find((playerCity) =>
      getAllQuartersOfCity(playerCity).includes(cellKey)
    );
    if (!fortressCity) {
      return 0;
    }
    const cityThreat = (context.cityThreats || []).find((threat) => threat.city === fortressCity);
    if (!cityThreat || cityThreat.enemyStrengthNear <= cityThreat.garrison) {
      return 0;
    }
    // Одна крепость на город: вторая (построенная или запланированная в цепочке)
    // оборону не усиливает — только сжигает действия и монеты
    const cityHasFortress = getAllQuartersOfCity(fortressCity).some((quarterHexKey) =>
      context.specialBuildings[quarterHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS
      || virtual.plannedFortresses.has(quarterHexKey)
    );
    if (cityHasFortress) {
      return 0;
    }
    return (BOT_WEIGHTS.DEFEND_OWN_CITY / 2) * (context.hasFortificationism ? 1.5 : 1);
  }

  return 0;
};

/**
 * Планирует всю цепочку строительства фазы на виртуальном состоянии.
 * Каждый следующий виртуальный шаг видит результаты предыдущих (новый квартал
 * открывает смежные ячейки под фермы/казармы), поэтому цепочки зависимых
 * строений планируются целиком. Порядок в плане enabler-first: сначала квартал,
 * затем зависящая от него ферма — каждый исполненный префикс плана валиден.
 */
// Планировщик (новая архитектура): множитель скоринга кандидата стройки по
// фокусу плана раунда. economy поднимает доходные строения и приглушает
// военные, military — наоборот; дороги и прочее нейтральны
const ECONOMY_FOCUS_TOKENS = new Set([
  COMMON_BUILDING_TOKENS.FARM, COMMON_BUILDING_TOKENS.QUARTER,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE, SPECIAL_BUILDING_TOKENS.GUILD,
  SPECIAL_BUILDING_TOKENS.TEMPLE, SPECIAL_BUILDING_TOKENS.PALACE,
]);
const MILITARY_FOCUS_TOKENS = new Set([
  COMMON_BUILDING_TOKENS.BARRACKS, SPECIAL_BUILDING_TOKENS.CAMP,
  SPECIAL_BUILDING_TOKENS.ARSENAL, SPECIAL_BUILDING_TOKENS.FORTRESS,
]);
const getPlannerBuildFocusFactor = (token, buildFocus) => {
  const isEconomyToken = ECONOMY_FOCUS_TOKENS.has(token) || checkIfCentralQuarter(token);
  if (buildFocus === 'economy') {
    return isEconomyToken ? 1.35 : MILITARY_FOCUS_TOKENS.has(token) ? 0.65 : 1;
  }
  if (buildFocus === 'military') {
    return isEconomyToken ? 0.7 : MILITARY_FOCUS_TOKENS.has(token) ? 1.5 : 1;
  }
  return 1;
};

// Состояние, в котором выбрано строение token, — то же, что дал бы
// dispatch(setSelectedToken(token)): редьюсер меняет только selectedToken
const withSelectedToken = (state, token) => ({
  ...state,
  private: {
    ...state.private,
    selectedEntities: {...state.private.selectedEntities, selectedToken: token},
  },
});

const planConstructionChain = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const pendingBuildings = selectPendingBuildings(state);
  const round = selectRound(state);
  const ruinsIncomeRoundLimit = selectRuinsIncomeRoundLimit(state);
  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const citiesCache = selectCitiesCache(state);
  // План раунда планировщика: фаза строительства идет ДО фазы войны, поэтому
  // план строится (или выбирается роллаутами) уже здесь; война читает кэш
  const plannerBuildFocus = getPlannerMode(state, currentPlayer.id).enabled
    ? ensureRoundPlan({
    currentPlayer, units, players: selectPlayers(state), citiesCache, hexes,
    gridRenderer: getBotGridRenderer(state), round, state,
  }, Boolean(store.__isBotSandbox))?.buildFocus || null
    : null;
  const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
  const isInitiativeInterceptedWithIsolationism = selectIsInitiativeInterceptedWithIsolationism(state);

  const usedActions = calcUsedConstructionActions(
    currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
  );
  let actionsRemaining = (currentPlayer.constructionPhaseActions || 0) - usedActions;
  if (actionsRemaining <= 0) {
    return [];
  }

  const gridRenderer = getBotGridRenderer(state);
  const controlledKeyPoints = getControlledKeyPointHexKeys(citiesCache);
  const remainingRounds = Math.max(1, FINAL_ROUND - round + 1);
  const playerUnitsCount = units.filter((unit) => unit.ownerId === currentPlayer.id).length;

  const isInsectoids = isPlayerFaction(currentPlayer, FACTIONS.INSECTOIDS);
  const isBeastmen = isPlayerFaction(currentPlayer, FACTIONS.BEASTMEN);
  const isRatmen = isPlayerFaction(currentPlayer, FACTIONS.RATMEN);
  const isDwarves = isPlayerFaction(currentPlayer, FACTIONS.DWARVES);

  const playerUnits = units.filter((unit) => unit.ownerId === currentPlayer.id);

  // Финансируемая армия: сколько ПРЕДПОЧТИТЕЛЬНЫХ отрядов реально можем нанять.
  // Кормовую базу (фермы) и казармы строим под нее, а не под абстрактное "армия + 2".
  const preferredUnitPrice = Math.max(
    1, Math.min(8, getPreferredUnitPrice(currentPlayer, round, playerUnits, citiesCache))
  );
  const affordableNewUnits = Math.min(2, Math.floor(currentPlayer.coins / preferredUnitPrice));
  const plannedArmySize = Math.min(UNITS_SAME_TIME_COUNT_LIMIT, playerUnitsCount + affordableNewUnits);

  // Инсектоиды: фермы — только на дополнительное снабжение (1 на 2 отряда),
  // казарм больше — они массово нанимают всегда снабженные отряды.
  // Крысолюды: фермы = армия (бесплатный найм d6 за снабжение, лимит 6),
  // казармы не строим до кавалерийского раунда, дальше максимум одну.
  let farmsTarget;
  let barracksTarget;
  if (isInsectoids) {
    farmsTarget = Math.floor(playerUnitsCount / 2);
    barracksTarget = Math.min(3, Math.max(1, playerUnitsCount + 1));
    // Раш ВАРВАРСТВА: хотя бы одна ферма для раздачи дополнительного
    // снабжения рою (скорость марша и перебросы костей)
    if (isPlayerPolicy(currentPlayer, POLICIES.BARBARISM)) {
      farmsTarget = Math.max(farmsTarget, 1);
    }
  } else if (isRatmen) {
    farmsTarget = RATMEN_D6_SAME_TYPE_UNIT_LIMIT;
    barracksTarget = round < RATMEN_CAVALRY_ROUND ? 0 : 1;
    // Волна 27 (24.1, запечена): во второй половине при запасе монет — до 2
    // казарм под кавалерию d8 для быстрых атак и захвата городов
    if (round >= RATMEN_CAVALRY_ROUND
      && currentPlayer.coins >= BOT_COIN_SURPLUS_THRESHOLD
    ) {
      barracksTarget = 2;
    }
  } else {
    farmsTarget = Math.max(plannedArmySize, 2);
    barracksTarget = Math.min(2, Math.max(1, plannedArmySize));
    // Орки: лишнее снабжение = дополнительное снабжение отрядам (скорость и урон)
    if (isPlayerFaction(currentPlayer, FACTIONS.ORCS)) {
      farmsTarget += 1;
    }
    // Эльфы: казарма НЕ ускоряет их рост (+1 в раунд где угодно — балансное
    // ограничение фракции), нужна только как платформа найма/апгрейда —
    // одной достаточно на всю партию, монеты экономим
    if (isPlayerFaction(currentPlayer, FACTIONS.ELVES)) {
      barracksTarget = Math.min(barracksTarget, 1);
    }
    // Волна 39: великанам каждая ферма — рост без предела «раз за ход»
    // (giants.md): ферм заметно больше, чем у остальных
    if (checkIfW39GiantsPlayer(currentPlayer)) {
      farmsTarget = Math.max(farmsTarget, getW39GiantsFarmsTarget(round));
    }
  }

  // Первый раунд дает 4 действия — их надо использовать полностью: "финансируемая
  // армия" от дорогого предпочтительного типа (кавалерия за 8) не должна
  // схлопывать дебют до 2-3 построек
  if (round === 1) {
    if (isInsectoids) {
      barracksTarget = Math.max(barracksTarget, 3);
    } else if (checkIfW39GiantsPlayer(currentPlayer)) {
      // Волна 39: великанам в дебюте 1 казарма и 2–3 фермы (giants.md) —
      // второй великан нанимается не раньше середины партии
      barracksTarget = 1;
    } else if (!isRatmen) {
      farmsTarget = Math.max(farmsTarget, 2);
      barracksTarget = Math.max(barracksTarget, 2);
    }
  }

  // Виртуальное состояние цепочки
  const virtual = {
    coins: currentPlayer.coins,
    hexes: {...hexes},
    centralQuarters: new Set(),
    allQuarters: new Set(),
    farmsCount: 0,
    barracksCount: 0,
    // Фермы, запланированные в ЭТОЙ цепочке (для прайсинга AGRARIANISM)
    plannedFarmActions: 0,
    plannedCells: new Set(),
    plannedRoadCells: new Set(),
    plannedWarehouses: new Set(),
    plannedMilitaryCamps: new Set(),
    plannedFortresses: new Set(),
    plannedGuildCells: new Set(),
    plannedMineCells: new Set(),
    plannedPalaces: new Set(),
    // Волна 36: спецстроения цепочки по ячейкам (квартал → строение) — «тип уже
    // есть в городе» и «лагерь/арсенал в городе» для ботов золото+
    plannedSpecials: new Map(),
    // Контроль ключевых точек с учетом кварталов, запланированных в этой цепочке
    controlledKeyPoints: new Set(controlledKeyPoints),
  };

  // Все ячейки своих городов — для предиката "обслуженной" ключевой точки
  const ownCityCells = new Set();

  (citiesCache?.[currentPlayer.color] || []).forEach((city) => {
    if (city.centralQuarter) {
      virtual.centralQuarters.add(city.centralQuarter);
    }
    getAllQuartersOfCity(city).forEach((quarterHexKey) => virtual.allQuarters.add(quarterHexKey));
    virtual.farmsCount += (city[COMMON_BUILDING_TOKENS.FARM] || []).length;
    virtual.barracksCount += (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).length;

    getAllQuartersOfCity(city).forEach((hexKey) => ownCityCells.add(hexKey));
    (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach((hexKey) => ownCityCells.add(hexKey));
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((hexKey) => ownCityCells.add(hexKey));
  });
  // Волна 23 (22.3, запечена): FANATICISM активна — руины ценнее в планировании
  const hasFanaticismForPlanning = isPlayerPolicy(currentPlayer, POLICIES.FANATICISM);

  pendingBuildings.forEach((building) => {
    if (checkIfCentralQuarter(building.token)) {
      virtual.centralQuarters.add(building.hexKey);
      virtual.allQuarters.add(building.hexKey);
    } else if (building.token === COMMON_BUILDING_TOKENS.QUARTER) {
      virtual.allQuarters.add(building.hexKey);
    } else if (building.token === COMMON_BUILDING_TOKENS.FARM) {
      virtual.farmsCount += 1;
    } else if (building.token === COMMON_BUILDING_TOKENS.BARRACKS) {
      virtual.barracksCount += 1;
    }
    // Живой тест 15.8: ключевые точки, смежные УЖЕ размещенным в этом раунде
    // кварталам, контролируются — второй базовый квартал к тем же руинам/руднику
    // дохода не добавляет (ценность точек бинарна)
    if (checkIfCentralQuarter(building.token) || building.token === COMMON_BUILDING_TOKENS.QUARTER) {
      getNeighborHexKeys(building.hexKey, gridRenderer).forEach((adjKey) => {
        if (checkIfKeyPointToken(hexes[adjKey])) {
          virtual.controlledKeyPoints.add(adjKey);
        }
      });
    }
  });
  // Волна 32 (экономика, E2): казармы на начало плана (virtual.barracksCount растет в цикле)
  const e32BarracksCountAtStart = virtual.barracksCount;

  // Резерв монет на найм: стройка не должна съедать деньги фазы войны — иначе
  // бот всю партию нанимает только d6 (или не нанимает вовсе), а строения простаивают.
  // Резервируем цену САМОГО ПРЕДПОЧТИТЕЛЬНОГО типа отряда (фракция/архетип), кап 8.
  // Крысолюды армию получают бесплатно за снабжение — монеты идут в стройку;
  // резерв под кавалерию d8 появляется только с кавалерийского раунда.
  let coinReserve = 0;
  if (isRatmen) {
    const cavalryCount = playerUnits.filter(
      (unit) => unit.type === UNIT_TYPES.D8.id || unit.type === UNIT_TYPES.D12.id
    ).length;
    if (round >= RATMEN_CAVALRY_ROUND && virtual.barracksCount >= 1 && cavalryCount === 0) {
      coinReserve = getUnitPrice(UNIT_TYPES.D8.id, currentPlayer).price;
    }
  } else if (playerUnitsCount === 0) {
    coinReserve = Math.max(BOT_CONSTRUCTION_COIN_RESERVE, virtual.barracksCount >= 1 ? preferredUnitPrice : 0);
  } else if (playerUnitsCount < UNITS_SAME_TIME_COUNT_LIMIT && virtual.barracksCount >= 1) {
    coinReserve = preferredUnitPrice;
  }

  // Волна 39: великаны — резерв по полной цене великана (10 > потолка 8 выше) и
  // только когда найм следующего великана разрешен
  if (checkIfW39GiantsPlayer(currentPlayer)) {
    coinReserve = calcW39GiantsHireCoinReserve({
      player: currentPlayer,
      playerUnits,
      citiesCount: (citiesCache?.[currentPlayer.color] || []).length,
      hasBarracks: virtual.barracksCount >= 1,
      isFinalRound: checkIfFinalRound(state),
      minReserve: BOT_CONSTRUCTION_COIN_RESERVE,
    });
  }

  // Эльфы: если подросший отряд стоит на казарме и ждет апгрейда (d6->d10 за 5,
  // d10->d20 за 8) — резервируем цену апгрейда, иначе стройка его вечно съедает.
  // При армии меньше 3 отрядов резерв не держим: сперва восполняем линию —
  // вайп малочисленной элитной армии фатален (27% партий эльфов без отрядов)
  if (isPlayerFaction(currentPlayer, FACTIONS.ELVES) && playerUnits.length >= 3) {
    const upgradeNeeds = playerUnits
      .filter((unit) => hexes[unit.hexKey] === COMMON_BUILDING_TOKENS.BARRACKS)
      .map((unit) => {
        if (unit.type === UNIT_TYPES.D6.id && unit.size >= 3) return 5;
        if (unit.type === UNIT_TYPES.D10.id && unit.size >= 5) return 8;
        return 0;
      });
    const maxUpgradeNeed = Math.max(0, ...upgradeNeeds);
    if (maxUpgradeNeed > 0) {
      coinReserve = Math.max(coinReserve, maxUpgradeNeed);
    }
  }

  // Волна 32 (оборона, гейт isPreW32DefensePlayer): у города под риском захвата
  // нет живучего держателя, но есть свободная рабочая казарма — стройка не
  // съедает цену самого дешевого отряда (заслон нанимается в фазе войны и в той
  // же фазе встает на квартал). Обычно резерв предпочтительного типа выше уже
  // покрывает заслон — модель угрозы считаем только когда он меньше цены заслона,
  // у бота есть казарма и найм нового отряда не упрется в общий потолок.
  // В раунде 13 новый отряд нанять нельзя при любом риске — резерв не держим
  if (!isRatmen && !isPreW32DefensePlayer(currentPlayer.id)
    && virtual.barracksCount >= 1
    && (playerUnitsCount < UNITS_SAME_TIME_COUNT_LIMIT || isInsectoids)
    && !checkIfFinalRound(state)
  ) {
    const blockerPrice = Math.min(...getRecruitableUnitTypeIds(currentPlayer)
      .map((unitTypeId) => getUnitPrice(unitTypeId, currentPlayer).price));
    if (Number.isFinite(blockerPrice) && blockerPrice > coinReserve) {
      const guardBarracks = getGuardNeededCityBarracks(
        getCityCaptureThreats(state, currentPlayer).cityThreats, CITY_THREAT_LEVELS.NEXT_ROUND,
        {skipCitiesWithOwnUnitOnBarracks: true}
      );
      if (guardBarracks.size > 0
        && calculateAvailableRecruitmentCells(state).some((cellKey) => guardBarracks.has(cellKey))
      ) {
        coinReserve = blockerPrice;
      }
    }
  }

  const centralQuarterToken = CENTRAL_QUARTER_PREFIX + currentPlayer.color;
  const candidateTokens = [...COMMON_CANDIDATE_TOKENS, centralQuarterToken, ...SPECIAL_CANDIDATE_TOKENS];

  // Гномы: разведка рудников — фракционный кандидат строительства
  if (isPlayerFaction(currentPlayer, FACTIONS.DWARVES)) {
    candidateTokens.push(MINE_EXPLORATION);
  }

  // Элементали: строить можно только строения с неиспользованным подходящим элементом
  const isElementals = isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS);
  const elementalsTokens = isElementals ? selectElementalsConstructionTokens(state) : null;
  const virtualUsedElementIndices = new Set();
  const findFreeElementIndex = (token) => {
    if (!isElementals) return null;
    const element = getElementForTokenType(token);
    if (!element) return null;
    return (elementalsTokens || []).findIndex((elementToken, elementIndex) =>
      elementToken.tokenName === element && !elementToken.isUsed && !virtualUsedElementIndices.has(elementIndex)
    ); // -1 = нет свободного элемента
  };

  // Реальная доступность ячеек по каждому типу строения (расчет один раз на план).
  // Считается по состоянию с подставленным выбранным строением, а не через
  // dispatch(setSelectedToken): в сетевой игре стор бота — настоящий стор
  // клиента-делегата, и каждый такой dispatch синхронно перерисовывал ему
  // сцену (с тем же расчетом ячеек для подсветки) — дюжина перерисовок на шаг.
  // Выбор строения меняет в состоянии только selectedToken, так что ответ тот же
  // Состояния серии отличаются только выбранным строением — общее для всех
  // строений (кэш городов, ячейки обычных строений, зона кварталов) считается
  // один раз (см. sharedCache в calculateConstructionCells)
  const availabilityBaseState = store.getState();
  const constructionCellsCache = {};
  const realAvailability = {};
  for (const token of candidateTokens) {
    if (!checkIfConstructionAllowedInRound(token, round)) {
      continue;
    }
    const {availableCells, blockedCells} = calculateConstructionCells(
      withSelectedToken(availabilityBaseState, token), {sharedCache: constructionCellsCache}
    );
    realAvailability[token] = {
      availableCells: new Set(availableCells),
      blockedCells,
    };
  }
  // Итог прежний: выбранное строение сброшено
  if (selectSelectedToken(store.getState()) !== null) {
    store.dispatch(setSelectedToken(null));
  }

  // Профиль архетипа и радиус экспансии (для DEFENSIVE — развитие только около дома)
  const strategy = getBotStrategy(currentPlayer.id);
  const profile = getBotProfile(state, currentPlayer);
  const bonusProfile = getBotBonusProfile(currentPlayer, selectGameType(state));
  // Волна 36 (участок «Стройка», только боты золото+, см. configs/botCityGrowth):
  // подчасти C1–C5, каждая за своим гейтом A/B. У остальных ботов и у
  // legacy-наборов все флаги false — ни одного нового расчета, код ниже идет
  // дословно прежним
  const gameTypeForW36 = selectGameType(state);
  // C1 и C2 — еще и за рубильником фракций (W36_FACTION_CITY_GROWTH_SWITCHES в
  // configs/botCityGrowth: орки и демоны)
  const isW36RingReserve = checkIfW36Feature(currentPlayer, gameTypeForW36, isPreW36RingReservePlayer)
    && checkIfW36FactionCityGrowthPartOn(currentPlayer.factionId, W36_FACTION_SWITCHABLE_PARTS.RING_RESERVE);
  const isW36QuarterGrowth = checkIfW36Feature(currentPlayer, gameTypeForW36, isPreW36QuarterGrowthPlayer)
    && checkIfW36FactionCityGrowthPartOn(currentPlayer.factionId, W36_FACTION_SWITCHABLE_PARTS.QUARTER_GROWTH);
  const isW36BarracksCap = checkIfW36Feature(currentPlayer, gameTypeForW36, isPreW36BarracksCapPlayer);
  const isW36CityDensity = checkIfW36Feature(currentPlayer, gameTypeForW36, isPreW36CityDensityPlayer);
  const isW36DoctrineSpecials = checkIfW36Feature(currentPlayer, gameTypeForW36, isPreW36DoctrineSpecialsPlayer);
  const isW36Construction = isW36RingReserve || isW36QuarterGrowth || isW36BarracksCap
    || isW36CityDensity || isW36DoctrineSpecials;
  const w36Knobs = isW36Construction ? getW36CityGrowthKnobs() : null;
  const w36Doctrine = isW36Construction
    ? getFactionGarrisonDoctrine(currentPlayer.factionId, checkIfW42CelectialsStormPlayer(currentPlayer, gameTypeForW36))
    : null;
  // C5: military гейтов спецстроений — архетипа без бонусного множителя
  const w36GateMilitary = isW36DoctrineSpecials
    ? getW36GateMilitary(strategy.archetype, strategy.w35HomeArchetype, w36Doctrine)
    : null;
  // Дом бота — до расчета оси волны 33: он тай-брейк выбора тылового хаба
  const playerCities = citiesCache?.[currentPlayer.color] || [];
  if (!strategy.homeHexKey && playerCities[0]?.centralQuarter) {
    strategy.homeHexKey = playerCities[0].centralQuarter;
  }
  // Волна 33: пакет «сильных городов» (LARGE/GREAT, не legacy-группа A/B) и
  // ось удара раунда (ближайший живой противник, кузница, тыл). Без оси —
  // нет живых противников или своих городов — новые правила не действуют,
  // и весь код ниже идет дословно как у обычного бота
  const strongCities = Boolean(bonusProfile?.strongCities);
  const bonusAxis = strongCities ? ensureBonusAxis(state, currentPlayer, bonusProfile) : null;
  const isStrongCitiesActive = strongCities && Boolean(bonusAxis);
  const isBonusArmyPhaseNow = isStrongCitiesActive && isBonusArmyPhase(round);
  // Волна 33 (C10): в армейской фазе стройка оставляет монеты на два отряда
  // (кап) — иначе богатый бот тратил доход на шестую постройку раньше найма.
  // Тот же резерв вычитает decidePolicyStep (политика покупается до построек).
  // Волна 35 (S, гейт isPreW35ArmyReservePlayer): вторая цена отряда держится
  // только из монет сверх минимального бюджета стройки (см. configs/botBuildRisk) —
  // при 9–15 монетах резерв 16 блокировал стройку целиком, включая фермы и
  // дороги снабжения самой армии. Бюджет считается от монет на начало фазы
  // (план пересчитывается каждый тик, а потраченное уже лежит в pending; цена
  // политики, купленной в этой фазе, — в записи стратегии w35ConstructionPhaseCoins)
  if (isBonusArmyPhaseNow) {
    const armyPhaseReserve = calcBonusArmyPhaseCoinReserve(bonusProfile, playerUnitsCount, preferredUnitPrice);
    coinReserve = Math.max(
      coinReserve,
      isPreW35ArmyReservePlayer(currentPlayer.id)
        ? armyPhaseReserve
        : calcArmyPhaseReserveWithBuildBudget(
          armyPhaseReserve, preferredUnitPrice,
          resolveConstructionPhaseCoins(strategy.w35ConstructionPhaseCoins, round, currentPlayer, pendingBuildings)
        )
    );
  }
  const homeDistances = profile.expansionRadius && strategy.homeHexKey
    ? calcBfsDistances([strategy.homeHexKey], hexes, gridRenderer)
    : null;

  const specialBuildings = selectSpecialBuildings(state);
  const enemyCitiesForInfluence = getEnemyCitiesForInfluence(citiesCache, currentPlayer.color);
  const ownInfluenceBuildings = getOwnInfluenceBuildings(specialBuildings, currentPlayer.color, citiesCache);

  // Кварталы своих городов под угрозой (нюанс #5: оборона прифронтовых городов).
  // Полный список угроз (с силой врага и гарнизоном) нужен крепостям v2
  const cityThreats = findOwnCitiesUnderThreat(currentPlayer, units, citiesCache, gridRenderer);
  const threatenedQuarters = new Set();
  cityThreats.forEach((threat) => {
    threat.quarters.forEach((quarterHexKey) => threatenedQuarters.add(quarterHexKey));
  });

  // Баланс сил: крепости — инструмент слабого; элементали без превосходства
  // не строятся к агрессорам (нюансы живого теста 12.2/12.6)
  const ownArmyForConstruction = calcPlayerArmyStrength(currentPlayer.id, units);
  const strongestOpponentForConstruction = calcStrongestOpponentArmy(
    currentPlayer.id, selectPlayers(state), units
  );
  const hasArmyInferiority = ownArmyForConstruction < strongestOpponentForConstruction;

  // Дистанции до городов агрессивных фракций: неагрессивные фракции без
  // превосходства развиваются К НЕагрессивным соседям (живой тест 13.4);
  // элементальское правило (волна 12) строже и сохраняется отдельно
  const isElementalsAvoidingAggressors = isElementals && hasArmyInferiority;
  // Волна 36 (C5): у бота золото+ — по military архетипа без бонусного множителя
  const isAvoidingAggressorsDirection = (w36GateMilitary ?? profile.military) <= 1 && hasArmyInferiority;
  let aggressiveCityDistances = null;
  let minOwnCityAggressorDistance;
  if (isElementalsAvoidingAggressors || isAvoidingAggressorsDirection) {
    const playersForAggr = selectPlayers(state);
    const aggressorQuarterCells = [];
    getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).forEach((cityInfo, quarterHexKey) => {
      const owner = playersForAggr.find((player) => player.color === cityInfo.color);
      if (owner && CONSTRUCTION_AGGRESSIVE_FACTIONS.has(owner.factionId)) {
        aggressorQuarterCells.push(quarterHexKey);
      }
    });
    if (aggressorQuarterCells.length > 0) {
      aggressiveCityDistances = calcBfsDistances(aggressorQuarterCells, hexes, gridRenderer);
      ownCityCells.forEach((cellKey) => {
        const distance = aggressiveCityDistances.get(cellKey);
        if (distance !== undefined
          && (minOwnCityAggressorDistance === undefined || distance < minOwnCityAggressorDistance)
        ) {
          minOwnCityAggressorDistance = distance;
        }
      });
    }
  }

  // Волна 27 (24.3): просвещенные в раундах 1-4 — без дорог в направлении
  // городов живых противников (кроме немедленных путей влияния, см. гейт в
  // скоринге дорог); дистанции до кварталов живых противников считаем здесь
  const isEnlightenedForRoads = isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED);
  // Волна 27 (24.3, запечена): безусловно для просвещенных в раундах 1-4
  const enlightenedEarlyRoadGateActive = isEnlightenedForRoads && round <= 4;
  let livingOpponentCityDistances = null;
  let minOwnCityLivingOpponentDistance;
  if (enlightenedEarlyRoadGateActive) {
    const livingOpponentQuarterCells = [];
    getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).forEach((cityInfo, quarterHexKey) => {
      if (cityInfo.color !== NEUTRAL_PLAYER_COLOR) {
        livingOpponentQuarterCells.push(quarterHexKey);
      }
    });
    if (livingOpponentQuarterCells.length > 0) {
      livingOpponentCityDistances = calcBfsDistances(livingOpponentQuarterCells, hexes, gridRenderer);
      ownCityCells.forEach((cellKey) => {
        const distance = livingOpponentCityDistances.get(cellKey);
        if (distance !== undefined
          && (minOwnCityLivingOpponentDistance === undefined
            || distance < minOwnCityLivingOpponentDistance)
        ) {
          minOwnCityLivingOpponentDistance = distance;
        }
      });
    }
  }

  // Орки: до целевого дохода 7 монет — режим экспансии в направлении слабейшего
  // досягаемого противника (живой тест 14): новые города/кварталы/дороги к его
  // кварталам, чтобы армия шла к жертве по застроенным ячейкам
  const isOrcs = isPlayerFaction(currentPlayer, FACTIONS.ORCS);
  let orcExpansionDistances = null;
  let minOwnCityOrcExpansionDistance;
  if (isOrcs && (currentPlayer.coinsIncome || 0) < 7) {
    const playersForOrcs = selectPlayers(state);
    let weakestOpponent = null;
    let weakestArmy = Infinity;
    playersForOrcs.forEach((player) => {
      if (player.id === currentPlayer.id || player.color === NEUTRAL_PLAYER_COLOR) {
        return;
      }
      const army = calcPlayerArmyStrength(player.id, units);
      if (army <= ownArmyForConstruction && army < weakestArmy) {
        weakestArmy = army;
        weakestOpponent = player;
      }
    });
    if (weakestOpponent) {
      const weakQuarterCells = [];
      getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).forEach((cityInfo, quarterHexKey) => {
        if (cityInfo.color === weakestOpponent.color) {
          weakQuarterCells.push(quarterHexKey);
        }
      });
      if (weakQuarterCells.length > 0) {
        orcExpansionDistances = calcBfsDistances(weakQuarterCells, hexes, gridRenderer);
        ownCityCells.forEach((cellKey) => {
          const distance = orcExpansionDistances.get(cellKey);
          if (distance !== undefined
            && (minOwnCityOrcExpansionDistance === undefined || distance < minOwnCityOrcExpansionDistance)
          ) {
            minOwnCityOrcExpansionDistance = distance;
          }
        });
      }
    }
  }

  // Цели активных захватных кампаний: дороги обязаны дать армии кратчайший путь
  const campaignCaptureCells = [...getBotStrategy(currentPlayer.id).campaigns.values()]
    .filter((campaign) => campaign.type === CAMPAIGN_TYPES.CAPTURE)
    .map((campaign) => campaign.targetHexKey)
    .filter((targetHexKey) => gridRenderer.hexes[targetHexKey]);
  let campaignTargetDistances = null;
  let minOwnCityCampaignDistance;
  if (campaignCaptureCells.length > 0) {
    campaignTargetDistances = calcBfsDistances(campaignCaptureCells, hexes, gridRenderer);
    ownCityCells.forEach((cellKey) => {
      const distance = campaignTargetDistances.get(cellKey);
      if (distance !== undefined
        && (minOwnCityCampaignDistance === undefined || distance < minOwnCityCampaignDistance)
      ) {
        minOwnCityCampaignDistance = distance;
      }
    });
  }

  // Волна 20 (19.11): куда отряды кросс-раундово УЖЕ идут (любой тип
  // кампании — захват/осада/оборона) — стройка тяготеет к этим ячейкам, а не
  // только дороги к целям захвата
  const campaignDestinationCells = [...getBotStrategy(currentPlayer.id).campaigns.values()]
    .map((campaign) => campaign.targetHexKey)
    .filter((targetHexKey) => gridRenderer.hexes[targetHexKey]);
  const campaignDestinationDistances = campaignDestinationCells.length > 0
    ? calcBfsDistances(campaignDestinationCells, hexes, gridRenderer)
    : null;

  // Гномы: экономный эндшпиль при SERVARIANISM с доходом 18+ — платное
  // строительство замораживается (монеты копятся на конвертацию в очки),
  // траты допустимы только при угрозе своим городам
  if (isDwarves
    && isPlayerPolicy(currentPlayer, POLICIES.SERVARIANISM)
    && (currentPlayer.coinsIncome || 0) >= 18
    && round >= FINAL_ROUND - 3
    && threatenedQuarters.size === 0
  ) {
    coinReserve = currentPlayer.coins;
  }

  // Волна 39: суккубы держат монеты на соблазнение этого хода (у остальных 0)
  coinReserve = Math.min(currentPlayer.coins, coinReserve + calcW39SeductionCoinReserve(currentPlayer, units));

  // Волна 32 (экономика, E2, гейт isPreW32RuinsReservePlayer): постройка,
  // присоединяющая неконтролируемые руины (и дорога-опора цепочки E3), не упирается
  // в резерв найма. Резерв сохраняется под угрозой городу (радиус 2 или, при
  // активной обороне D, модель W32) и при армии из 0 отрядов; в R13 найма нет — 0.
  // Модель угроз считается лениво: только когда кандидат у руин упирается в резерв
  const e32RuinsReserveActive = !isPreW32RuinsReservePlayer(currentPlayer.id);
  let e32RuinsJoinReserveMemo = null;
  const getE32RuinsJoinCoinReserve = () => {
    if (!e32RuinsReserveActive) {
      return coinReserve;
    }
    if (e32RuinsJoinReserveMemo === null) {
      const isFinal = checkIfFinalRound(state);
      const hasCityThreat = !isFinal && (threatenedQuarters.size > 0
        || (!isRatmen
          && !isPreW32DefensePlayer(currentPlayer.id)
          && e32BarracksCountAtStart >= 1
          && getGuardNeededCityBarracks(
            getCityCaptureThreats(state, currentPlayer).cityThreats, CITY_THREAT_LEVELS.NEXT_ROUND,
            {skipCitiesWithOwnUnitOnBarracks: true}
          ).size > 0));
      e32RuinsJoinReserveMemo = calcE32RuinsJoinCoinReserve({
        coinReserve, isFinalRound: isFinal, playerUnitsCount, hasCityThreat,
      });
    }
    return e32RuinsJoinReserveMemo;
  };

  // Дистанции до кварталов чужих/нейтральных городов — для фронтир-бонуса казарм
  // и коридорных дорог снабжения
  const enemyQuarterHexKeys = [...getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).keys()];
  const enemyQuarterDistances = enemyQuarterHexKeys.length > 0
    ? calcBfsDistances(enemyQuarterHexKeys, hexes, gridRenderer)
    : null;

  // Ближайшая к противнику своя городская ячейка — коридоры только вперед от нее
  let minOwnCityEnemyDistance;
  if (enemyQuarterDistances) {
    ownCityCells.forEach((cellKey) => {
      const distance = enemyQuarterDistances.get(cellKey);
      if (distance !== undefined && (minOwnCityEnemyDistance === undefined || distance < minOwnCityEnemyDistance)) {
        minOwnCityEnemyDistance = distance;
      }
    });
  }

  // Зона снабжения ферм — дороги внутри нее продлевают цепочку снабжения к фронту
  const playerFarmsForReach = getPlayerFarms(state);
  const supplyReachCells = playerFarmsForReach.length > 0
    ? calcSupplyReachCells(playerFarmsForReach, hexes, gridRenderer)
    : null;

  // Волна 20 (19.7): свои отряды вне текущей зоны снабжения — критичные
  // кандидаты на приоритетное строительство фермы, продлевающей снабжение до них
  const unsuppliedOwnUnitCells = supplyReachCells
    ? new Set(
      playerUnits
        .filter((unit) => !supplyReachCells.has(unit.hexKey))
        .map((unit) => unit.hexKey)
    )
    : null;

  // Волна 25 (23.3): отряды, до которых снабжение ДОСТАЕТ, но БЮДЖЕТА не
  // хватит — живой тест 23: 2 отряда на одной общей ферме (1 снабжение) —
  // второй остается голодным каждую фазу снабжения. Жадное сопоставление
  // отряд->слот фермы (отряды с наименьшим числом достижимых ферм первыми);
  // несопоставленные отряды получают тот же критичный фермерский приоритет,
  // что и отряды вне зоны снабжения (FARM_UNSUPPLIED_UNIT_BONUS).
  // Инсектоиды исключены (базовое снабжение бесплатно у любой ячейки)
  const isWave25SupplyBudgetCheck = !isInsectoids;
  // Волна 33 (C12): у «сильных городов» досягаемость фермы считается с
  // ретрансляцией через другие свои фермы (правила снабжения): емкость тыловой
  // фермы доходит до отрядов у кузницы, если цепочка непустых ячеек есть.
  // Крысолюдов правила ферм не касаются — их модель бюджета прежняя
  const ownFarmKeysForRelay = isStrongCitiesActive && !isRatmen ? new Set(playerFarmsForReach) : null;
  if (isWave25SupplyBudgetCheck && unsuppliedOwnUnitCells && playerUnits.length > 1) {
    const hasEcoanarchismForBudget = isPlayerPolicy(currentPlayer, POLICIES.ECOANARCHISM);
    const farmSlots = playerFarmsForReach.map((farmHexKey) => {
      const hasAdjacentWarehouse = getNeighborHexKeys(farmHexKey, gridRenderer).some(
        (adjKey) => specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
      );
      let reach = null;
      if (!hasEcoanarchismForBudget) {
        reach = ownFarmKeysForRelay
          ? calcSupplyReachCellsWithRelay(farmHexKey, ownFarmKeysForRelay, hexes, gridRenderer)
          : calcSupplyReachCells([farmHexKey], hexes, gridRenderer);
      }
      return {
        farmHexKey,
        capacity: hasAdjacentWarehouse ? 2 : 1,
        reach,
      };
    });
    const reachesUnit = (slot, unitHexKey) => !slot.reach || slot.reach.has(unitHexKey);
    const candidates = playerUnits
      .filter((unit) => supplyReachCells.has(unit.hexKey) || hasEcoanarchismForBudget)
      .map((unit) => ({
        unit,
        reachableFarms: farmSlots.filter((slot) => reachesUnit(slot, unit.hexKey)),
      }))
      .sort((a, b) => a.reachableFarms.length - b.reachableFarms.length);
    candidates.forEach(({unit, reachableFarms}) => {
      const slotWithBudget = reachableFarms.find((slot) => slot.capacity > 0);
      if (slotWithBudget) {
        slotWithBudget.capacity -= 1;
      } else {
        unsuppliedOwnUnitCells.add(unit.hexKey);
      }
    });
  }

  // Свои кварталы центральной зоны вне досягаемости снабжения — фермы,
  // дотягивающие до них цепочку, получают бонус (нюанс живого теста 11)
  const centralQuartersOutOfSupply = [];
  (citiesCache?.[currentPlayer.color] || []).forEach((city) => {
    getAllQuartersOfCity(city).forEach((quarterHexKey) => {
      if (isHegemonismBonusHexKey(quarterHexKey)
        && (!supplyReachCells || !supplyReachCells.has(quarterHexKey))
      ) {
        centralQuartersOutOfSupply.push(quarterHexKey);
      }
    });
  });

  // Реально доступные ячейки под фермы/казармы: если их нет, квартал-расширение
  // становится осмысленным (он такие ячейки откроет)
  const countUsableCells = (token) => {
    const availability = realAvailability[token];
    if (!availability) return 0;
    return [...availability.availableCells].filter((cellKey) => !availability.blockedCells.has(cellKey)).length;
  };
  const noFarmCellsAvailable = countUsableCells(COMMON_BUILDING_TOKENS.FARM) === 0;
  const noBarracksCellsAvailable = countUsableCells(COMMON_BUILDING_TOKENS.BARRACKS) === 0;

  // Свои военные лагеря (для капа инсектоидов)
  const ownMilitaryCampsCount = (citiesCache?.[currentPlayer.color] || []).reduce(
    (sum, city) => sum + (city[SPECIAL_BUILDING_TOKENS.MILITARY_CAMP] || []).length, 0
  );

  // Дистанции до отрядов ИГРОКОВ (нейтралы не двигаются): казармы эльфов в зоне
  // блокировки (живой тест 14) и крепости под блокировкой (живой тест 15.1)
  const isElvesPlayer = isPlayerFaction(currentPlayer, FACTIONS.ELVES);
  let enemyUnitDistances = null;
  const enemyUnitCells = units
    .filter((unit) => unit.ownerId !== currentPlayer.id && unit.ownerId !== NEUTRAL_PLAYER_ID)
    .map((unit) => unit.hexKey);
  if (enemyUnitCells.length > 0) {
    enemyUnitDistances = calcBfsDistances(enemyUnitCells, hexes, gridRenderer, 4);
  }
  // Отряды противников, ходящих в фазе войны РАНЬШЕ бота, — они успевают
  // заблокировать/занять недостроенную крепость еще до ее достройки
  let earlierEnemyUnitDistances = null;
  // Волна 35 (B): порядок фазы войны, а не порядок мест в списке игроков —
  // войну открывает владелец инициативы (тот же порядок у модели угрозы
  // городов). Ходящие раньше — все, кто не ходит после бота
  const isBuildRiskActive = !isPreW35BuildRiskPlayer(currentPlayer.id);
  let buildRiskLaterMoverIds = null;
  if (isBuildRiskActive) {
    const turnOrderForRisk = selectTurnOrder(state) || [];
    buildRiskLaterMoverIds = getLaterWarMoverIds({
      phase: selectPhase(state),
      turnOrder: turnOrderForRisk.length > 0 ? turnOrderForRisk : selectPlayers(state).map((player) => player.id),
      nextInitiativePlayerId,
      phaseCompletedPlayers: selectPhaseCompletedPlayers(state),
      currentPlayerId: currentPlayer.id,
    });
  }
  {
    const playersForOrder = selectPlayers(state);
    const selfOrderIndex = playersForOrder.findIndex((player) => player.id === currentPlayer.id);
    const earlierPlayerIds = isBuildRiskActive
      ? new Set(playersForOrder
        .filter((player) => player.id !== currentPlayer.id && player.id !== NEUTRAL_PLAYER_ID
          && !buildRiskLaterMoverIds.has(player.id))
        .map((player) => player.id))
      : new Set(playersForOrder.slice(0, Math.max(0, selfOrderIndex)).map((player) => player.id));
    const earlierCells = units
      .filter((unit) => earlierPlayerIds.has(unit.ownerId))
      .map((unit) => unit.hexKey);
    if (earlierCells.length > 0) {
      earlierEnemyUnitDistances = calcBfsDistances(earlierCells, hexes, gridRenderer, 3);
    }
  }

  // Центр карты должен оспариваться всеми: ключевые точки центральной части
  // поля ценнее для любой фракции (до x1.5 у самого центра)
  const fieldRadius = selectGridConfigState(state)?.radius;
  const keyPointCenterBias = (hexKey) => calcCenterProximityFactor(hexKey, fieldRadius);

  // Волна 19 (18.7): зона влияния кварталов — пустые ячейки/дороги в 2 ходах от
  // любого квартала (включая pending). На них нельзя ставить квартал ДРУГОГО
  // города — цепочки «дорога -> квартал» к таким ячейкам жгут действия впустую
  // Зона уже посчитана при расчете доступности, если поле то же самое
  const isAvailabilityFieldSame = selectHexes(availabilityBaseState) === hexes
    && selectPendingBuildings(availabilityBaseState) === pendingBuildings
    && selectSpecialBuildings(availabilityBaseState) === selectSpecialBuildings(state);
  const quarterZoneCells = isAvailabilityFieldSame && constructionCellsCache.quarterZoneCells
    ? constructionCellsCache.quarterZoneCells
    : calculateQuarterZoneCells(state, isAvailabilityFieldSame ? constructionCellsCache.futureCitiesCache : null);

  // Ячейка допускает будущий квартал: реальный калькулятор разрешает базовый
  // квартал сейчас, либо ячейка свободна и вне зоны влияния кварталов (новый
  // город). Используется для отсева бессмысленных подходных цепочек (18.7)
  const quarterAvailability = realAvailability[COMMON_BUILDING_TOKENS.QUARTER];
  const checkIfCellCanHostQuarter = (cellKey) => {
    if (!quarterZoneCells) {
      return true; // legacy (волна 18): без проверки зоны
    }
    if (quarterAvailability
      && quarterAvailability.availableCells.has(cellKey)
      && !quarterAvailability.blockedCells.has(cellKey)
    ) {
      return true;
    }
    const cellToken = virtual.hexes[cellKey];
    return (!cellToken || cellToken === TOKENS.ROAD) && !quarterZoneCells.has(cellKey);
  };

  // Подходные дороги: затухающая по дальности (до 4 ячеек) ценность движения
  // к ценным неконтролируемым и необслуженным ключевым точкам — иначе цепочка
  // дорог к центру (руины, великие рудники) никогда не начинается
  const keyPointApproachValues = new Map();
  const servedCheckContext = {gridRenderer, ownCityCells};
  // Волна 19 (18.2): карта тяготения экономики к важным точкам — произведение
  // близости (затухание до 4 ячеек) и коэффициента важности точки
  // (рудник 1 -> великий рудник/руины 1.5 -> великие руины 2)
  const kpProximityValues = new Map();
  const KP_PROXIMITY_COEF = {
    [KEY_POINT_TOKENS.MINE]: 1,
    [KEY_POINT_TOKENS.SUPER_MINE]: 1.5,
    [KEY_POINT_TOKENS.RUINS]: 1.5,
    [KEY_POINT_TOKENS.SUPER_RUINS]: 2,
  };
  // Подходные цепочки только к точкам в разумной близости от своих городов:
  // дороги через всю карту бессмысленны (пути влияния ограничены 5 ячейками)
  const ownCityDistances = ownCityCells.size > 0
    ? calcBfsDistances([...ownCityCells], hexes, gridRenderer, 8)
    : new Map();
  Object.entries(hexes).forEach(([kpHexKey, kpToken]) => {
    if (!checkIfKeyPointToken(kpToken) || virtual.controlledKeyPoints.has(kpHexKey)) {
      return;
    }
    if (!ownCityDistances.has(kpHexKey)) {
      return; // дальше 8 от своих городов — не тянем цепочку
    }
    // Волна 19 (18.7/18.2): точка без единой ячейки под будущий квартал рядом
    // недостижима для захвата — ни дорог к ней, ни тяготения экономики
    const isKeyPointCapturable = getNeighborHexKeys(kpHexKey, gridRenderer)
      .some((adjKey) => checkIfCellCanHostQuarter(adjKey));
    if (!isKeyPointCapturable) {
      return;
    }
    // Тяготение ферм/казарм раундов 1-5 к важным точкам (18.2)
    if (kpProximityValues) {
      const proximityCoef = KP_PROXIMITY_COEF[kpToken] || 1;
      const proximityDistances = calcBfsDistances([kpHexKey], hexes, gridRenderer, 4);
      proximityDistances.forEach((distance, cellKey) => {
        if (distance < 1) {
          return;
        }
        const proximityValue = proximityCoef * (1 - distance / 5);
        if ((kpProximityValues.get(cellKey) || 0) < proximityValue) {
          kpProximityValues.set(cellKey, proximityValue);
        }
      });
    }
    if (checkIfKeyPointServed(kpHexKey, virtual, servedCheckContext)) {
      return; // своя ячейка уже рядом — путь к точке обеспечен
    }
    let kpValue = getKeyPointValuePerRound(kpToken, round, ruinsIncomeRoundLimit) * keyPointCenterBias(kpHexKey);
    // Волна 23 (22.3, запечена): FANATICISM — подходные цепочки к руинам ценнее (+2 очка/раунд)
    if (hasFanaticismForPlanning && checkIfRuinsToken(kpToken)) {
      kpValue += POLICY_FANATICISM_RUINS_WIN_POINTS_INCOME_BONUS * keyPointCenterBias(kpHexKey);
    }
    if (kpValue < 1) {
      return; // рядовые дальние точки не стоят цепочек дорог
    }
    const kpDistances = calcBfsDistances([kpHexKey], hexes, gridRenderer, 4);
    kpDistances.forEach((distance, cellKey) => {
      if (distance < 1) {
        return;
      }
      const approachValue = kpValue * (1 - distance / 5);
      if ((keyPointApproachValues.get(cellKey) || 0) < approachValue) {
        keyPointApproachValues.set(cellKey, approachValue);
      }
    });
  });

  // Волна 19 (18.4, запечено): дистанции до городов раш-фракций (крысолюды,
  // орки, инсектоиды, демоны) — дороги в их сторону строятся только при
  // значительном превосходстве с планом захвата либо ради новых путей влияния
  let rushCityDistances = null;
  let minOwnCityRushDistance;
  let hasRushCaptureSuperiority = false;
  {
    const playersForRush = selectPlayers(state);
    const rushQuarterCells = [];
    const rushCityQuarterSet = new Set();
    let strongestRushArmy = 0;
    getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).forEach((cityInfo, quarterHexKey) => {
      const owner = playersForRush.find((player) => player.color === cityInfo.color);
      if (owner && ROAD_RUSH_FACTIONS.has(owner.factionId)) {
        rushQuarterCells.push(quarterHexKey);
        rushCityQuarterSet.add(quarterHexKey);
        strongestRushArmy = Math.max(strongestRushArmy, calcPlayerArmyStrength(owner.id, units));
      }
    });
    if (rushQuarterCells.length > 0) {
      rushCityDistances = calcBfsDistances(rushQuarterCells, hexes, gridRenderer);
      ownCityCells.forEach((cellKey) => {
        const distance = rushCityDistances.get(cellKey);
        if (distance !== undefined
          && (minOwnCityRushDistance === undefined || distance < minOwnCityRushDistance)
        ) {
          minOwnCityRushDistance = distance;
        }
      });
      // Превосходство с планом захвата: армия в 1.5 раза сильнее сильнейшего
      // раша И есть активная захватная кампания на квартал раш-города
      const hasCaptureCampaignOnRush = [...getBotStrategy(currentPlayer.id).campaigns.values()]
        .some((campaign) => campaign.type === CAMPAIGN_TYPES.CAPTURE
          && rushCityQuarterSet.has(campaign.targetHexKey));
      hasRushCaptureSuperiority = hasCaptureCampaignOnRush
        && ownArmyForConstruction >= strongestRushArmy * 1.5;
    }
  }

  // Волна 22: вспомогательные расчеты новых правил.
  // (21.9a) дистанции только до кварталов ЖИВЫХ противников — тыл для дворца
  const realEnemyQuarterCells = [];
  getEnemyQuarterHexKeys(citiesCache, currentPlayer.color).forEach((cityInfo, quarterHexKey) => {
    if (cityInfo.color !== NEUTRAL_PLAYER_COLOR) {
      realEnemyQuarterCells.push(quarterHexKey);
    }
  });
  const realEnemyQuarterDistances = realEnemyQuarterCells.length > 0
    ? calcBfsDistances(realEnemyQuarterCells, hexes, gridRenderer)
    : null;
  // (21.8a) свои дворцы — для запрета второго
  const ownPalacesCount = (citiesCache?.[currentPlayer.color] || []).reduce(
    (sum, city) => sum + (city[SPECIAL_BUILDING_TOKENS.PALACE] || []).length, 0
  );
  // (21.10/21.11) перевес противника в отрядах в радиусе 3 от ячейки
  const checkIfEnemyOutweighsNear = (cellKey) => {
    let enemySizeNear = 0;
    let ownSizeNear = 0;
    units.forEach((unit) => {
      if (calcHexDistanceLocal(unit.hexKey, cellKey) > 3) {
        return;
      }
      if (unit.ownerId === currentPlayer.id) {
        ownSizeNear += unit.size;
      } else if (unit.ownerId !== NEUTRAL_PLAYER_ID) {
        enemySizeNear += unit.size;
      }
    });
    return enemySizeNear > ownSizeNear;
  };
  // (21.10) цена ЦК — гейт «дорога не занимает ячейку двойного захвата»
  const centralQuarterPriceCoins = getBuildingPrice(
    centralQuarterToken, currentPlayer, pendingBuildings
  ).priceCoins;
  // Волна 22.1 (21.14, смягчение): цена казармы — условие второй казармы
  // элементалей 1-го раунда (монет должно хватать на казарму + найм)
  const elementalsBarracksPriceCoins = getBuildingPrice(
    COMMON_BUILDING_TOKENS.BARRACKS, currentPlayer, pendingBuildings
  ).priceCoins;
  // (21.15) есть ли свой рудник без смежной своей казармы
  const ownMineCellsSet = new Set((citiesCache?.[currentPlayer.color] || []).flatMap((city) => [
    ...(city[KEY_POINT_TOKENS.MINE] || []),
    ...(city[KEY_POINT_TOKENS.SUPER_MINE] || []),
  ]));
  const ownBarracksCellsSet = new Set((citiesCache?.[currentPlayer.color] || []).flatMap(
    (city) => city[COMMON_BUILDING_TOKENS.BARRACKS] || []
  ));
  const hasUnservedOwnMines = [...ownMineCellsSet].some((mineCellKey) =>
    !getNeighborHexKeys(mineCellKey, gridRenderer).some((adjKey) => ownBarracksCellsSet.has(adjKey))
  );

  // Волна 32 (экономика): зона чужих отрядов — E3 (площадки) и E4 (слоты кольца)
  const e32RuinsChainActive = !isPreW32RuinsChainPlayer(currentPlayer.id) && round >= E32_CHAIN_MIN_ROUND;
  const e32CityFollowUpActive = !isPreW32CityFollowUpPlayer(currentPlayer.id);
  const e32EnemyUnitZoneCells = e32RuinsChainActive || e32CityFollowUpActive
    ? calcEnemyUnitZoneCells(units, currentPlayer.id, gridRenderer)
    : null;
  // Волна 32 (экономика, E3, гейт isPreW32RuinsChainPlayer): площадки под ЦК у руин,
  // которые откроет одна дорога, и индекс «дорога -> площадки». Новых BFS нет
  const e32ChainSites = e32RuinsChainActive && realAvailability[centralQuarterToken] && quarterZoneCells
    ? findE32RuinsChainSites({
      hexes,
      gridRenderer,
      controlledKeyPoints: virtual.controlledKeyPoints,
      ownCityDistances,
      quarterZoneCells,
      centralQuarterAvailableCells: realAvailability[centralQuarterToken].availableCells,
      enemyUnitZoneCells: e32EnemyUnitZoneCells,
      isWithinExpansionRadius: (cellKey) => !homeDistances
        || (homeDistances.get(cellKey) ?? Infinity) <= profile.expansionRadius,
    })
    : null;
  const e32RoadEnablers = e32ChainSites && e32ChainSites.size > 0
    ? buildE32RoadEnablerIndex(e32ChainSites, hexes, gridRenderer)
    : null;
  const e32RoadPriceCoins = e32RoadEnablers
    ? getBuildingPrice(COMMON_BUILDING_TOKENS.ROAD, currentPlayer, pendingBuildings).priceCoins
    : 0;
  // Волна 32 (экономика, E6, гейт isPreW32LateSpecialsPlayer)
  const e32LateSpecialsActive = !isPreW32LateSpecialsPlayer(currentPlayer.id);
  // E5/E6: вес монет в очках при вычитании цены (просвещенные конвертируют монеты 1:1,
  // sharedGameStateReducer buildGameResults)
  const e32CoinsToPoints = isEnlightenedForRoads ? 1 : BOT_WEIGHTS.COINS_TO_POINTS;

  // Волна 33: карты и наборы «сильных городов» — раз на тик (см. раздел
  // пакета в configs/botBonusBehavior). Все под гейтом: обычному боту ни
  // одного лишнего BFS
  let bonusDistanceFactor = null;
  let bonusFutureOwnCities = null;
  // Повреждения строений — для «рабочего» арсенала кузницы (C2), под гейтом
  let bonusBuildingsDamage = null;
  let supplyChainPathCells = null;
  let freeOwnBarracksCountOutsideForge = 0;
  if (isStrongCitiesActive) {
    // Сформированные отряды (size ≥ 3): новый город/квартал/дорога к точке
    // ценятся по BFS до них (лимит 8 — дальше все равно ×0.2)
    const formedUnitCells = playerUnits
      .filter((unit) => unit.size >= BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE)
      .map((unit) => unit.hexKey);
    const ownFormedUnitDistances = formedUnitCells.length > 0
      ? calcBfsDistances(formedUnitCells, hexes, gridRenderer, 8)
      : null;
    // Штраф за удаленность от армии действует только в зоне риска у живых
    // противников — в глубоком тылу город безопасен (см. botBonusBehavior)
    bonusDistanceFactor = createDistanceFactor(
      ownFormedUnitDistances, bonusProfile, realEnemyQuarterDistances
    );

    // Города на тик: future-кэш (реальные + pending) из общего кэша расчета
    // доступности, если поле то же; синтетические ЦК цепочки добавляет индекс
    const futureCitiesForBonus = isAvailabilityFieldSame && constructionCellsCache.futureCitiesCache
      ? constructionCellsCache.futureCitiesCache
      : calculateFutureCitiesCache(hexes, specialBuildings, pendingBuildings);
    bonusFutureOwnCities = futureCitiesForBonus[currentPlayer.color] || [];
    bonusBuildingsDamage = selectBuildingsDamage(state) || {};

    const stagingCity = playerCities.find((city) => city.centralQuarter === bonusAxis.stagingCityKey) || null;
    const rearCity = playerCities.find((city) => city.centralQuarter === bonusAxis.rearCityKey) || null;
    const stagingQuarterKeys = stagingCity ? getAllQuartersOfCity(stagingCity) : [];

    // Цепочка снабжения тыл → кузница: пока ни один квартал кузницы не в
    // relay-досягаемости ферм тылового хаба, ячейки кратчайшего пути между
    // ними получают дорогу (две BFS раз на тик)
    const rearFarmKeys = rearCity ? (rearCity[COMMON_BUILDING_TOKENS.FARM] || []) : [];
    if (stagingCity && rearCity && stagingCity !== rearCity && rearFarmKeys.length > 0) {
      const ownFarmKeysForChain = ownFarmKeysForRelay || new Set(playerFarmsForReach);
      const rearReachCells = new Set();
      rearFarmKeys.forEach((farmHexKey) => {
        calcSupplyReachCellsWithRelay(farmHexKey, ownFarmKeysForChain, hexes, gridRenderer)
          .forEach((cellKey) => rearReachCells.add(cellKey));
      });
      if (!stagingQuarterKeys.some((quarterKey) => rearReachCells.has(quarterKey))) {
        supplyChainPathCells = findSupplyChainPathCells(
          calcBfsDistances(rearFarmKeys, hexes, gridRenderer),
          calcBfsDistances(stagingQuarterKeys, hexes, gridRenderer),
          stagingQuarterKeys,
          hexes
        );
      }
    }

    // Свободные казармы вне кузницы (гейт «3 свободных» для казарм кузницы)
    const stagingBarracksKeys = new Set(stagingCity ? (stagingCity[COMMON_BUILDING_TOKENS.BARRACKS] || []) : []);
    freeOwnBarracksCountOutsideForge = playerCities
      .flatMap((city) => city[COMMON_BUILDING_TOKENS.BARRACKS] || [])
      .filter((barracksCellKey) => !stagingBarracksKeys.has(barracksCellKey)
        && !units.some((unit) => unit.ownerId === currentPlayer.id && unit.hexKey === barracksCellKey)).length;
  }

  const scoringContext = {
    gridRenderer,
    remainingRounds,
    round,
    // Лимит раундов для окна дохода руин (см. getRuinsBoostStartRound)
    ruinsIncomeRoundLimit,
    // Последний раунд партии — по ее собственному лимиту раундов (см. checkIfFinalRound)
    isFinalRound: checkIfFinalRound(state),
    playerUnitsCount,
    ownCityCells,
    playerCities,
    specialBuildings,
    profile,
    // Надбавки бота с экономическим бонусом (null у обычных ботов)
    bonusProfile,
    currentPlayer,
    enemyCitiesForInfluence,
    threatenedQuarters,
    cityThreats,
    hasArmyInferiority,
    isElementalsAvoidingAggressors,
    isAvoidingAggressorsDirection,
    aggressiveCityDistances,
    minOwnCityAggressorDistance,
    campaignTargetDistances,
    minOwnCityCampaignDistance,
    campaignDestinationDistances,
    rushCityDistances,
    minOwnCityRushDistance,
    hasRushCaptureSuperiority,
    kpProximityValues,
    roadInfluenceDeltas: null,
    farmsTarget,
    barracksTarget,
    isInsectoids,
    // Раш ВАРВАРСТВА: рой d6 идет к противнику по коридорам дорог
    isInsectoidBarbarismRush: isInsectoids && isPlayerPolicy(currentPlayer, POLICIES.BARBARISM),
    isBeastmen,
    isRatmen,
    // Кварталы, уже поставленные в ЭТОМ раунде (для дебютного капа в 2 квартала)
    pendingQuartersCount: pendingBuildings.filter(
      (building) => building.token === COMMON_BUILDING_TOKENS.QUARTER
    ).length,
    enemyQuarterDistances,
    minOwnCityEnemyDistance,
    // Ранний драйв дохода: пока доход ниже целевого, захват точек важнее.
    // Действует всю 1-ю половину до дохода 7+ (военные профили) / 10+
    // (остальные), в первые 3 раунда — усиленно x1.6 (16.4/16.8)
    earlyIncomeDrive: (() => {
      const incomeTarget = profile.military >= 1.2 ? 7 : 10;
      if (round > FINAL_ROUND / 2 || (currentPlayer.coinsIncome || 0) >= incomeTarget) {
        return 1;
      }
      return round <= 3 ? 1.6 : 1.3;
    })(),
    // В 1-й половине все действия строительства должны быть потрачены — фермам
    // сверх цели дается минимальный ненулевой скоринг (16.8). Волна 18
    // (запечена, 17.2/17.8): до 7-го раунда включительно
    isFirstHalfSpendAll: round <= 7,
    // Волна 18 (запечена, 17.7): инсектоиды первые 3 раунда до дохода 7
    // расширяются городами
    isInsectoidsEarlyExpansion: isInsectoids
      && round <= 3 && (currentPlayer.coinsIncome || 0) < 7,
    // Лимит дорог за фазу (дороги этой фазы уже в pending) (16.13)
    pendingRoadsCount: pendingBuildings.filter(
      (building) => building.token === COMMON_BUILDING_TOKENS.ROAD
    ).length,
    roadPhaseLimit: (currentPlayer.constructionPhaseActions || 0) >= 4 ? 2 : 1,
    keyPointCenterBias,
    keyPointApproachValues,
    supplyReachCells,
    unsuppliedOwnUnitCells,
    centralQuartersOutOfSupply,
    noFarmCellsAvailable,
    noBarracksCellsAvailable,
    ownMilitaryCampsCount,
    preferredUnitPrice,
    playerUnits,
    // Демоны в раундах 1-4 форсируют захват рудников строительством
    demonEarlyMineFocus: isPlayerFaction(currentPlayer, FACTIONS.DEMONS) && round <= 4 ? 1.5 : 1,
    isDwarves,
    isElves: isElvesPlayer,
    isLeprechauns: isPlayerFaction(currentPlayer, FACTIONS.LEPRECHAUNS),
    isVentory: isPlayerFaction(currentPlayer, FACTIONS.VENTORY),
    isEnlightened: isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED),
    // Волна 39: гильдии и храмы — экономика гоблинов (у остальных 1)
    w39IncomeSpecialMultiplier: checkIfW39GoblinsPlayer(currentPlayer) ? W39_GOBLINS_INCOME_SPECIAL_MULTIPLIER : 1,
    isElementals,
    isDemons: isPlayerFaction(currentPlayer, FACTIONS.DEMONS),
    enemyUnitDistances,
    earlierEnemyUnitDistances,
    orcExpansionDistances,
    minOwnCityOrcExpansionDistance,
    hasServarianism: isPlayerPolicy(currentPlayer, POLICIES.SERVARIANISM),
    // Волна 19 (18.3, запечено): фракциям с рекомендованной SERVARIANISM
    // казармы-мостики у рудников приоритетны до 6 раунда включительно даже
    // без самой политики
    hasServarianismDrive: round <= 6
      && (getFactionPreferences(currentPlayer.factionId, currentPlayer.id).policies || [])
        .includes(POLICIES.SERVARIANISM),
    hasTotalitarianism: isPlayerPolicy(currentPlayer, POLICIES.TOTALITARIANISM),
    // Волна 19 (18.11b): свои казармы без своего отряда на них — «свободные»
    freeOwnBarracksCount: (citiesCache?.[currentPlayer.color] || [])
      .flatMap((city) => city[COMMON_BUILDING_TOKENS.BARRACKS] || [])
      .filter((barracksCellKey) => !units.some(
        (unit) => unit.ownerId === currentPlayer.id && unit.hexKey === barracksCellKey
      )).length,
    hasPatriotism: isPlayerPolicy(currentPlayer, POLICIES.PATRIOTISM),
    hasOligarchy: isPlayerPolicy(currentPlayer, POLICIES.OLIGARCHY)
      || currentPlayer.nextPolicy?.id === POLICIES.OLIGARCHY,
    // Гильдии во владении — для фокуса OLIGARCHY на 2 гильдии
    ownGuildsCount: (citiesCache?.[currentPlayer.color] || []).reduce(
      (sum, city) => sum + (city[SPECIAL_BUILDING_TOKENS.GUILD] || []).length, 0
    ),
    // Волна 18 (запечена, живой тест 17.1/17.9): олигархия-опенинг вентори/
    // просвещенных/элементалей — в первых 2 раундах 2 базовых квартала +
    // 2 гильдии в одном городе; форсируется флат-приоритетом
    oligarchyOpeningActive: round <= 2
      && (isPlayerFaction(currentPlayer, FACTIONS.VENTORY)
        || isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED)
        || isElementals)
      && (isPlayerPolicy(currentPlayer, POLICIES.OLIGARCHY)
        || currentPlayer.nextPolicy?.id === POLICIES.OLIGARCHY),
    hasFortificationism: isPlayerPolicy(currentPlayer, POLICIES.FORTIFICATIONISM),
    hasTheocracy: isPlayerPolicy(currentPlayer, POLICIES.THEOCRACY),
    // Волна 23: политики П1-П3 (AGRARIANISM ферма/склад, HEGEMONISM центр,
    // FANATICISM руины)
    hasAgrarianism: isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM),
    hasHegemonism: isPlayerPolicy(currentPlayer, POLICIES.HEGEMONISM),
    hasFanaticism: isPlayerPolicy(currentPlayer, POLICIES.FANATICISM),
    // П1: бесплатная ферма аграризма еще не размещена в этой фазе
    agrarianismFreeFarmAvailable: !pendingBuildings.some((action) =>
      action.token === COMMON_BUILDING_TOKENS.FARM
      && action.type === CONSTRUCTION_ACTION_TYPES.CONSTRUCTION),
    // Свои казармы и арсеналы — гейт лепреконского арсенального буста
    ownBarracksCells: ownBarracksCellsSet,
    ownArsenalsCount: (citiesCache?.[currentPlayer.color] || []).reduce(
      (sum, city) => sum + (city[SPECIAL_BUILDING_TOKENS.ARSENAL] || []).length, 0
    ),
    // Свои рудники (для казарм-мостиков SERVARIANISM)
    ownMineCells: ownMineCellsSet,
    // Волна 22: новые правила строительства
    realEnemyQuarterDistances,
    // Волна 32 (экономика, блок E), подгейты isPreW32RuinsReservePlayer ...
    // isPreW32RushTemplePlayer
    e32CityFollowUpActive,
    e32QuarterZoneCells: quarterZoneCells,
    e32EnemyUnitZoneCells,
    e32LateCityActive: !isPreW32LateCityPlayer(currentPlayer.id),
    e32LateSpecialsActive,
    e32CoinsToPoints,
    e32RushTempleActive: !isPreW32RushTemplePlayer(currentPlayer.id),
    getE32ChainRoadInfo: null, // E3: заполняется ниже, после isCellFreeForBuilding
    ownPalacesCount,
    checkIfEnemyOutweighsNear,
    centralQuarterPriceCoins,
    checkIfCellCanHostQuarter,
    hasUnservedOwnMines,
    isEcoanarchism: isPlayerPolicy(currentPlayer, POLICIES.ECOANARCHISM),
    // Профицит монет (кроме Просвещенных — им копить и положено)
    hasCoinSurplus: currentPlayer.coins >= BOT_COIN_SURPLUS_THRESHOLD
      && !isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED),
    // Волна 25 (23.2/23.4/23.5/23.7, запечена): правила безусловны
    agrarianismFarmCap: isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM)
    && !isRatmen && !isInsectoids && round < 6
      ? playerUnitsCount + 1
      : null,
    lateTempleGateActive: true,
    influenceRoadBoostActive: true,
    firstHalfQuarterPriorityActive: true,
    // Волна 27 (common_problems_24, запечена): правила безусловны
    wave27ArsenalBanActive: true,
    wave27BeastmenCorridorActive: true,
    enlightenedEarlyRoadGateActive,
    livingOpponentCityDistances,
    minOwnCityLivingOpponentDistance,
    // Волна 28 (common_problems_25): новые правила, ревертируются гейтом
    // волны 28 (легаси-группы играют по поведению волны 27)
    wave28FinalRoundBanActive: !isWave27BehaviorPlayer(currentPlayer.id),
    wave28ElementalsFarmForceActive: !isWave27BehaviorPlayer(currentPlayer.id),
    wave28ElementalsBarracksCapActive: !isWave27BehaviorPlayer(currentPlayer.id),
    // Волна 33: пакет «сильных городов» (false/null у обычных ботов и
    // legacy-группы A/B — все ветки ниже по этим полям не выполняются)
    strongCities: isStrongCitiesActive,
    bonusAxis,
    isBonusArmyPhase: isBonusArmyPhaseNow,
    bonusArmyPhaseCityPriorityOff: isBonusArmyPhaseNow,
    distanceFactor: bonusDistanceFactor,
    supplyChainPathCells,
    freeOwnBarracksCountOutsideForge,
    // Индекс городов на итерацию плана — заполняется в цикле (virtual меняется)
    bonusCities: null,
    checkIfFrontierFarmCapWaived: null,
    // Волна 35 (B): риск места нового города — заполняется ниже (у legacy-группы null)
    getNewCitySiteFactor: null,
    // Волна 36 (участок «Стройка», боты золото+): подчасти C1–C5 — заполняются
    // ниже; у остальных ботов и legacy-наборов null, ветки скоринга по ним не
    // выполняются
    w36RingReserve: null,
    w36QuarterGrowth: null,
    w36BarracksCap: null,
    w36PendingFreeBarracks: null,
    w36CityDensity: null,
    w36GateMilitary,
    w36EarlyTemple: null,
  };

  // Дельты дохода влияния от дорог считаем один раз на план (BFS дороги недешевы)
  if (ownInfluenceBuildings.length > 0 && realAvailability[COMMON_BUILDING_TOKENS.ROAD]) {
    const roadInfluenceDeltas = new Map();
    const roadAvailability = realAvailability[COMMON_BUILDING_TOKENS.ROAD];
    const calcRoadInfluenceIncomeDelta = createRoadInfluenceIncomeDeltaCalculator(
      currentPlayer, ownInfluenceBuildings, enemyCitiesForInfluence, hexes
    );
    roadAvailability.availableCells.forEach((cellKey) => {
      if (roadAvailability.blockedCells.has(cellKey)) {
        return;
      }
      const delta = calcRoadInfluenceIncomeDelta(cellKey);
      if (delta > 0) {
        roadInfluenceDeltas.set(cellKey, delta);
      }
    });
    scoringContext.roadInfluenceDeltas = roadInfluenceDeltas;
  }

  // Волна 36 (участок «Стройка», боты золото+): замыкания подчастей C1–C5 на
  // этот план. Город — по виртуальному состоянию цепочки (реальные строения,
  // pending этой фазы и шаги цепочки), формулы — evaluation/cityGrowth
  if (isW36Construction) {
    const w36OwnCityCentralQuarters = new Set(playerCities.map((city) => city.centralQuarter));
    const getW36CityQuarters = (centralQuarterKey, virt) => [
      centralQuarterKey,
      ...getW36CityBaseQuarters(centralQuarterKey, virt.allQuarters, virt.centralQuarters, gridRenderer),
    ];
    const getW36QuarterSpecial = (quarterKey, virt) => specialBuildings[quarterKey]
      || virt.plannedSpecials.get(quarterKey)
      || null;
    // Тыловой хаб и кузница «сильных городов» (ось есть только у LARGE+) растут
    // на ступень больше
    const getW36CityTarget = (centralQuarterKey) => (isStrongCitiesActive
      && (centralQuarterKey === bonusAxis.rearCityKey || centralQuarterKey === bonusAxis.stagingCityKey)
      ? w36Knobs.hubBaseQuartersTarget
      : w36Knobs.cityBaseQuartersTarget);
    // C5: город для раннего храма — нефронтирный (кварталы дальше
    // W36_FRONTIER_CITY_MAX_ENEMY_DISTANCE от кварталов живых противников) и
    // не под угрозой. Тыловой хаб оси волны 33 здесь не исключение: при одном
    // городе он же и кузница у границы
    const checkIfW36EarlyTempleCity = (cityQuarters, templeRound) => isW36DoctrineSpecials
      && w36Doctrine === GARRISON_DOCTRINES.ECONOMIC
      && templeRound >= W36_EARLY_TEMPLE_MIN_ROUND
      && !cityQuarters.some((quarterKey) => threatenedQuarters.has(quarterKey))
      && !checkIfW36FrontierCity(cityQuarters, realEnemyQuarterDistances, W36_FRONTIER_CITY_MAX_ENEMY_DISTANCE);

    // C1: бронь легальных клеток кольца ЦК под базовые кварталы
    if (isW36RingReserve) {
      scoringContext.w36RingReserve = (cellKey, adjacentKeys, virt) => {
        const centralQuarterKey = findW36AdjacentCentralQuarter(adjacentKeys, virt.centralQuarters);
        if (!centralQuarterKey) {
          return 1;
        }
        const baseQuartersCount = getW36CityBaseQuarters(
          centralQuarterKey, virt.allQuarters, virt.centralQuarters, gridRenderer
        ).length;
        const target = w36Knobs.cityBaseQuartersTarget;
        return calcW36RingReserveFactor({
          baseQuartersCount,
          target,
          // Легальность — тем же расчетом, что у кандидатов квартала (правило
          // дальности кварталов разных городов, в т.ч. своих)
          isLegalQuarterCell: baseQuartersCount < target && checkIfCellCanHostQuarter(cellKey),
          factor: w36Knobs.ringReserveFactor,
        });
      };
    }

    // C2: ценность роста города — лучшее спецстроение следующего раунда по
    // доктрине на новом базовом квартале
    if (isW36QuarterGrowth) {
      // Окно роста — от лимита раундов этой партии (gameState.finalRound), а
      // не от общего FINAL_ROUND: локальные партии и миссии короче/длиннее
      const w36RoundLimit = getRoundLimit(selectFinalRound(state)) ?? FINAL_ROUND;
      const w36DoctrineSpecialTokens = getW36DoctrineSpecialTokens(w36Doctrine);
      const w36SpecialPrices = new Map();
      const getW36SpecialPrice = (token) => {
        if (!w36SpecialPrices.has(token)) {
          w36SpecialPrices.set(token, getBuildingPrice(token, currentPlayer, pendingBuildings).priceCoins);
        }
        return w36SpecialPrices.get(token);
      };
      // Пути влияния с клетки — на шаг цепочки (дороги цепочки их меняют)
      const w36InfluenceMemo = new Map();
      const getW36InfluenceIncome = (token, cellKey, virt) => {
        const memoKey = `${token}|${cellKey}|${virt.plannedCells.size}`;
        if (!w36InfluenceMemo.has(memoKey)) {
          w36InfluenceMemo.set(memoKey, projectInfluenceBuildingIncome(
            token, cellKey, currentPlayer, enemyCitiesForInfluence, virt.hexes
          ));
        }
        return w36InfluenceMemo.get(memoKey);
      };
      const getW36ImmediatePathsCount = (cellKey, virt) => {
        const memoKey = `paths|${cellKey}|${virt.plannedCells.size}`;
        if (!w36InfluenceMemo.has(memoKey)) {
          w36InfluenceMemo.set(memoKey, calculateInfluencePathIncome(cellKey, enemyCitiesForInfluence, virt.hexes).count);
        }
        return w36InfluenceMemo.get(memoKey);
      };
      const economyInfluenceFactor = (profile.economy + profile.influence) / 2;
      const isEnlightenedForW36 = isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED);
      const hasTheocracyForW36 = isPlayerPolicy(currentPlayer, POLICIES.THEOCRACY);
      // Ценность спецстроения на новом БК через раунд — те же формулы и гейты,
      // что у скоринга самих спецстроений (дальше в scoreCandidate), без
      // ситуативных множителей; 0 — бот его там не поставит
      const calcW36NextSpecialValue = (token, cellKey, adjacentKeys, cityQuarters, specialRounds, virt) => {
        const templeRound = round + 1;
        if (token === SPECIAL_BUILDING_TOKENS.TEMPLE) {
          if (isEnlightenedForW36 && !hasTheocracyForW36) {
            return 0;
          }
          if (!profile.earlyTempleAllowed && templeRound <= 5 && !hasTheocracyForW36
            && !checkIfW36EarlyTempleCity(cityQuarters, templeRound)
          ) {
            return 0;
          }
          return getW36InfluenceIncome(token, cellKey, virt) * specialRounds
            * BOT_WEIGHTS.SPECIAL_INCOME_PER_ROUND * economyInfluenceFactor;
        }
        if (token === SPECIAL_BUILDING_TOKENS.GUILD) {
          if ((w36GateMilitary ?? profile.military) > 1 && !scoringContext.oligarchyOpeningActive
            && getW36ImmediatePathsCount(cellKey, virt) < 1
          ) {
            return 0;
          }
          return getW36InfluenceIncome(token, cellKey, virt) * BOT_WEIGHTS.COINS_TO_POINTS * specialRounds
            * BOT_WEIGHTS.SPECIAL_INCOME_PER_ROUND * economyInfluenceFactor;
        }
        if (token === SPECIAL_BUILDING_TOKENS.WAREHOUSE) {
          const adjacentFarms = adjacentKeys.filter((adjKey) => virt.hexes[adjKey] === COMMON_BUILDING_TOKENS.FARM).length;
          if (adjacentFarms === 0 || (isRatmen && adjacentFarms < 2)) {
            return 0;
          }
          return adjacentFarms * specialRounds * BOT_WEIGHTS.WAREHOUSE_PER_ADJACENT_FARM_PER_ROUND * profile.economy
            + (scoringContext.hasAgrarianism && adjacentFarms >= 2 ? BOT_WEIGHTS.AGRARIANISM_WAREHOUSE_BOOST : 0);
        }
        if (token === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP) {
          const adjacentBarracks = adjacentKeys.filter(
            (adjKey) => virt.hexes[adjKey] === COMMON_BUILDING_TOKENS.BARRACKS
          ).length;
          if (isRatmen || adjacentBarracks === 0) {
            return 0;
          }
          let campFactionMultiplier = 1;
          if (isBeastmen) {
            campFactionMultiplier = 2;
          } else if (isInsectoids) {
            if (adjacentBarracks < 2 || ownMilitaryCampsCount + virt.plannedMilitaryCamps.size >= 1) {
              return 0;
            }
            campFactionMultiplier = 3;
          }
          return adjacentBarracks * specialRounds * BOT_WEIGHTS.MILITARY_CAMP_PER_ADJACENT_BARRACKS_PER_ROUND
            * profile.military * campFactionMultiplier;
        }
        if (token === SPECIAL_BUILDING_TOKENS.ARSENAL) {
          if (isInsectoids || isRatmen || isBeastmen || playerUnitsCount === 0) {
            return 0;
          }
          const cityBarracksCount = countW36CityAdjacentTokens(
            [...cityQuarters, cellKey], COMMON_BUILDING_TOKENS.BARRACKS, virt.hexes, gridRenderer
          );
          if (cityBarracksCount === 0) {
            return 0;
          }
          let arsenalFactionMultiplier = 1;
          if (isElvesPlayer) {
            arsenalFactionMultiplier = scoringContext.hasPatriotism ? 2.5 : 1.5;
          }
          return (cityQuarters.length + 1) * specialRounds * BOT_WEIGHTS.ARSENAL_PER_CITY_QUARTER_PER_ROUND
            * profile.military * arsenalFactionMultiplier;
        }
        return 0;
      };
      scoringContext.w36QuarterGrowth = (cellKey, adjacentKeys, virt) => {
        const centralQuarterKey = findW36AdjacentCentralQuarter(adjacentKeys, virt.centralQuarters);
        // Спецстроение встанет через раунд — ему нужен срок окупиться
        const specialRounds = Math.max(0, w36RoundLimit - round);
        if (!centralQuarterKey || specialRounds < W36_QUARTER_GROWTH_MIN_SPECIAL_ROUNDS) {
          return 0;
        }
        const cityQuarters = getW36CityQuarters(centralQuarterKey, virt);
        const baseQuartersCount = cityQuarters.length - 1;
        const target = getW36CityTarget(centralQuarterKey);
        if (baseQuartersCount >= target) {
          return 0;
        }
        // Тип — только если его еще нет в городе (реальные, pending и цепочка)
        const options = [];
        w36DoctrineSpecialTokens.forEach((token) => {
          if (cityQuarters.some((quarterKey) => getW36QuarterSpecial(quarterKey, virt) === token)) {
            return;
          }
          const value = calcW36NextSpecialValue(token, cellKey, adjacentKeys, cityQuarters, specialRounds, virt);
          if (value > 0) {
            options.push({value, priceCoins: getW36SpecialPrice(token)});
          }
        });
        return calcW36QuarterGrowthValue({
          baseQuartersCount,
          target,
          bestSpecialValue: pickW36BestSpecialValue(options, W36_QUARTER_GROWTH_PRICE_FACTOR),
          intensity: bonusProfile.intensity,
          base: w36Knobs.quarterGrowthBase,
          specialShare: w36Knobs.quarterGrowthSpecialShare,
        });
      };
    }

    // C3: кап казарм на город и счетчики «город без казармы/фермы», «3
    // свободные казармы» с pending этой фазы
    if (isW36BarracksCap) {
      const w36PendingQuarterCells = new Set(pendingBuildings
        .filter((building) => building.token === COMMON_BUILDING_TOKENS.QUARTER)
        .map((building) => building.hexKey));
      const isW36FreshQuarter = (quarterKey, virt) => w36PendingQuarterCells.has(quarterKey)
        || (virt.plannedCells.has(quarterKey) && virt.hexes[quarterKey] === COMMON_BUILDING_TOKENS.QUARTER);
      const ownUnitCellsForW36 = new Set(playerUnits.map((unit) => unit.hexKey));
      scoringContext.w36PendingFreeBarracks = pendingBuildings
        .filter((building) => building.token === COMMON_BUILDING_TOKENS.BARRACKS
          && building.type !== CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT
          && !ownUnitCellsForW36.has(building.hexKey))
        .map((building) => building.hexKey);
      // Лагерь растит отряды на смежных казармах: крысолюдам он не нужен
      // (армия — рой с ферм), инсектоидам разрешен только у 2+ казарм
      const isSecondBarracksCampGated = !isRatmen && !isInsectoids;
      scoringContext.w36BarracksCap = {
        isCityCapReached: (adjacentKeys, virt) => {
          const centralQuarterKey = findW36CandidateCityCentralQuarter(
            adjacentKeys, virt.allQuarters, virt.centralQuarters, gridRenderer
          );
          if (!centralQuarterKey) {
            return false;
          }
          const cityQuarters = getW36CityQuarters(centralQuarterKey, virt);
          const cityBarracksCount = countW36CityAdjacentTokens(
            cityQuarters, COMMON_BUILDING_TOKENS.BARRACKS, virt.hexes, gridRenderer
          );
          if (cityBarracksCount === 0) {
            return false;
          }
          // Кузница (отделенная от тыла) собирает кластер до своего капа
          const isForgeCity = isStrongCitiesActive
            && centralQuarterKey === bonusAxis.stagingCityKey
            && bonusAxis.stagingCityKey !== bonusAxis.rearCityKey;
          let cap = w36Knobs.cityBarracksCap;
          if (isInsectoids) {
            cap = Math.max(cap, W36_INSECTOIDS_CITY_BARRACKS_CAP);
          } else if (isForgeCity) {
            cap = Math.max(cap, bonusProfile.forgeBarracksCap);
          }
          return checkIfW36CityBarracksCapReached({
            cityBarracksCount,
            cap,
            requiresCampForSecond: isSecondBarracksCampGated,
            hasCampOrArsenal: cityQuarters.some((quarterKey) => {
              const special = getW36QuarterSpecial(quarterKey, virt);
              return special === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP || special === SPECIAL_BUILDING_TOKENS.ARSENAL;
            }),
            // Свежий базовый квартал (этой фазы или цепочки) рядом без
            // спецстроения — площадка лагеря у обеих казарм
            hasFreshCampPlatform: adjacentKeys.some((adjKey) => adjKey !== centralQuarterKey
              && cityQuarters.includes(adjKey)
              && isW36FreshQuarter(adjKey, virt)
              && !getW36QuarterSpecial(adjKey, virt)),
          });
        },
        // Строения города (только своих городов из кэша — как у прежних
        // надбавок), смежного кандидату; null — кандидат не у такого города
        countOwnCityTokens: (adjacentKeys, token, virt) => {
          const centralQuarterKey = findW36CandidateCityCentralQuarter(
            adjacentKeys, virt.allQuarters, virt.centralQuarters, gridRenderer
          );
          if (!centralQuarterKey || !w36OwnCityCentralQuarters.has(centralQuarterKey)) {
            return null;
          }
          return countW36CityAdjacentTokens(getW36CityQuarters(centralQuarterKey, virt), token, virt.hexes, gridRenderer);
        },
        countCityBarracks: (playerCity, virt) => countW36CityAdjacentTokens(
          getW36CityQuarters(playerCity.centralQuarter, virt), COMMON_BUILDING_TOKENS.BARRACKS, virt.hexes, gridRenderer
        ),
      };
    }

    // C4: плотность своих городов — штраф нового ЦК
    if (isW36CityDensity) {
      const getW36QuarterRangeCells = createW36QuarterRangeCells();
      const w36CityDensityMemo = new Map();
      scoringContext.w36CityDensity = (cellKey, virt) => {
        const memoKey = `${cellKey}|${virt.plannedCells.size}`;
        if (w36CityDensityMemo.has(memoKey)) {
          return w36CityDensityMemo.get(memoKey);
        }
        const isFreeRingCell = (ringCellKey) => !virt.plannedCells.has(ringCellKey)
          && checkIfW36FreeQuarterCell(virt.hexes[ringCellKey]);
        // Свои города в досягаемости правила дальности: клетка кольца в 2 от
        // нового ЦК — значит ЦК города не дальше 3
        const ownCities = [];
        virt.centralQuarters.forEach((centralQuarterKey) => {
          if (calcHexDistanceLocal(centralQuarterKey, cellKey) > QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN + 1) {
            return;
          }
          ownCities.push({
            baseQuartersCount: getW36CityBaseQuarters(
              centralQuarterKey, virt.allQuarters, virt.centralQuarters, gridRenderer
            ).length,
            legalRingCells: getNeighborHexKeys(centralQuarterKey, gridRenderer)
              .filter((ringCellKey) => isFreeRingCell(ringCellKey) && checkIfCellCanHostQuarter(ringCellKey)),
          });
        });
        // Собственная емкость кольца нового города: свободные клетки вне зоны
        // кварталов других городов
        const newCityCapacity = getNeighborHexKeys(cellKey, gridRenderer).filter((ringCellKey) =>
          isFreeRingCell(ringCellKey) && !(quarterZoneCells && quarterZoneCells.has(ringCellKey))
        ).length;
        const {lostSlots, missingSlots} = calcW36CityDensityLoss({
          ownCities,
          takenCells: ownCities.length > 0 ? getW36QuarterRangeCells(cellKey, virt.hexes) : new Set(),
          newCityCapacity,
          target: w36Knobs.cityBaseQuartersTarget,
        });
        const penalty = (lostSlots * w36Knobs.densityLostSlotPenalty
          + missingSlots * w36Knobs.densitySmallCityPenalty) * bonusProfile.intensity;
        w36CityDensityMemo.set(memoKey, penalty);
        return penalty;
      };
    }

    // C5: ранний храм экономической доктрины в нефронтирном городе
    if (isW36DoctrineSpecials) {
      scoringContext.w36EarlyTemple = (cellKey) => {
        const centralQuarterKey = findW36AdjacentCentralQuarter(
          getNeighborHexKeys(cellKey, gridRenderer), virtual.centralQuarters
        );
        return Boolean(centralQuarterKey)
          && checkIfW36EarlyTempleCity(getW36CityQuarters(centralQuarterKey, virtual), round);
      };
    }
  }

  // Волна 35 (B, гейт isPreW35BuildRiskPlayer): риск потерять город, в
  // который идет постройка (см. configs/botBuildRisk). Модель угрозы своих
  // городов (W32) считается лениво, один раз на план: ее досягаемость врагов
  // общая с остальными вызовами тика (кэш в cityCaptureThreat)
  let getBuildRiskFactor = null;
  let getBuildGiftWinPoints = null;
  if (isBuildRiskActive) {
    let ownCityThreatsMemo = null;
    const getOwnCityThreats = () => {
      if (!ownCityThreatsMemo) {
        ownCityThreatsMemo = getCityCaptureThreats(state, currentPlayer);
      }
      return ownCityThreatsMemo;
    };
    const isFinalRoundForRisk = checkIfFinalRound(state);

    // Ожидаемый урон по свежему держателю на квартале: отряды живых
    // противников, ходящих после бота, которые дотянутся до клетки рядом с
    // кварталом (гекс-дистанция без препятствий — оценка сверху). Ходящие
    // раньше уже походили: держатель на любом квартале срывает захват
    const laterDamageMemo = new Map();
    const getLaterMoversDamage = (cityThreat) => {
      if (laterDamageMemo.has(cityThreat)) {
        return laterDamageMemo.get(cityThreat);
      }
      let damage = 0;
      units.forEach((unit) => {
        if (!buildRiskLaterMoverIds.has(unit.ownerId)) {
          return;
        }
        const speed = (getUnitTypeConfig(unit.type)?.speed || 2)
          + (unit.additionalSupply === true ? ADDITIONAL_SUPPLY_SPEED : 0);
        if (cityThreat.quarters.some((quarterHexKey) => calcHexDistanceLocal(unit.hexKey, quarterHexKey) <= speed + 1)) {
          damage += Math.max(0, unit.size - BOT_EXPECTED_DICE_PENALTY);
        }
      });
      const result = Math.max(damage, cityThreat.threatDamage || 0);
      laterDamageMemo.set(cityThreat, result);
      return result;
    };

    // Свободные рабочие казармы на начало фазы (для исключения казармы/арсенала)
    let recruitmentCellsMemo = null;
    const getRecruitmentCells = () => {
      if (!recruitmentCellsMemo) {
        recruitmentCellsMemo = new Set(calculateAvailableRecruitmentCells(state));
      }
      return recruitmentCellsMemo;
    };
    // Казарма города, поставленная в этой фазе (прошлыми тиками или этой
    // цепочкой): в кэш городов она попадет только после фазы, а найм в войне
    // с нее уже возможен
    const checkIfCityHasNewBarracks = (cityThreat) => {
      const isCityBarracksCell = (barracksCellKey) => getNeighborHexKeys(barracksCellKey, gridRenderer)
        .some((adjKey) => cityThreat.quarters.includes(adjKey));
      return pendingBuildings.some((building) => building.token === COMMON_BUILDING_TOKENS.BARRACKS
        && isCityBarracksCell(building.hexKey))
        || [...virtual.plannedCells].some((plannedCellKey) =>
          virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.BARRACKS && isCityBarracksCell(plannedCellKey));
    };
    // Самый крупный держатель, которого можно нанять в этом раунде на казарме
    // города: новый отряд размера 1 и докупка размера с каждого квартала
    // города по разу (×2 при арсенале), на монеты, оставшиеся после постройки.
    // Потолки найма — те же, что у этапа найма (checkIfBotCanHireNewGuardUnit):
    // общий потолок отрядов, фермерский (заслон угрозы этого раунда его обходит,
    // кроме крысолюдов) и лимит типа — иначе казарма строилась в городе, где
    // держателя никто не наймет, и отдавалась вместе с городом
    const calcBestHireableHolderSize = (cityThreat, coinsForHolder, hasArsenal) => {
      let bestSize = 0;
      getRecruitableUnitTypeIds(currentPlayer).forEach((unitTypeId) => {
        const unitPrice = getUnitPrice(unitTypeId, currentPlayer).price;
        if (unitPrice > coinsForHolder
          || !checkIfBotCanHireNewGuardUnit({
            state, currentPlayer, playerUnits, unitTypeId, threatLevel: cityThreat.level,
          })
        ) {
          return;
        }
        const unitConfig = getUnitTypeConfig(unitTypeId);
        const {sizePrice} = getSizeRecruitmentPrice(unitConfig, currentPlayer);
        const sizeHires = sizePrice > 0
          ? Math.min(cityThreat.quarters.length, Math.floor((coinsForHolder - unitPrice) / sizePrice))
          : cityThreat.quarters.length;
        bestSize = Math.max(bestSize, Math.min(unitConfig.maxSize, 1 + sizeHires * (hasArsenal ? 2 : 1)));
      });
      return bestSize;
    };
    // Казарма и арсенал — оборонные постройки: риск их не режет, если они
    // дают в этом же раунде держателя, который переживет ожидаемый урон.
    // Казарма — только первая рабочая в городе (вторая гарнизона не добавит)
    const checkIfDefensiveBuild = (token, cityThreat, priceCoins) => {
      if (isFinalRoundForRisk
        || (token !== COMMON_BUILDING_TOKENS.BARRACKS && token !== SPECIAL_BUILDING_TOKENS.ARSENAL)
      ) {
        return false;
      }
      const cityBarracks = cityThreat.city[COMMON_BUILDING_TOKENS.BARRACKS] || [];
      const hasWorkingBarracks = cityBarracks.some((barracksCellKey) => getRecruitmentCells().has(barracksCellKey))
        || checkIfCityHasNewBarracks(cityThreat);
      const hasArsenal = (cityThreat.city[SPECIAL_BUILDING_TOKENS.ARSENAL] || []).length > 0;
      if (token === COMMON_BUILDING_TOKENS.BARRACKS ? hasWorkingBarracks : (!hasWorkingBarracks || hasArsenal)) {
        return false;
      }
      const holderSize = calcBestHireableHolderSize(
        cityThreat, virtual.coins - priceCoins, hasArsenal || token === SPECIAL_BUILDING_TOKENS.ARSENAL
      );
      return holderSize > 0 && holderSize > getLaterMoversDamage(cityThreat);
    };

    const citySurvivalMemo = new Map();
    const getCitySurvivalFactor = (cityThreat) => {
      if (!citySurvivalMemo.has(cityThreat)) {
        const hasHolders = cityThreat.holders.length > 0;
        citySurvivalMemo.set(cityThreat, calcCityBuildSurvivalFactor({
          isThisRound: cityThreat.level >= CITY_THREAT_LEVELS.THIS_ROUND,
          isNextRound: cityThreat.level >= CITY_THREAT_LEVELS.NEXT_ROUND
            && cityThreat.level < CITY_THREAT_LEVELS.THIS_ROUND,
          hasHolders,
          hasSurvivingHolder: hasHolders && !cityThreat.isHolderAtRisk,
          isOutweighed: checkIfEnemyOutweighsNear(cityThreat.city.centralQuarter),
        }));
      }
      return citySurvivalMemo.get(cityThreat);
    };

    // Кварталы, к которым привязана постройка: спецстроение — свой квартал;
    // базовый квартал — смежный ЦК; ферма, казарма и разведка рудника —
    // смежные кварталы. Кварталы виртуального плана: реальные, поставленные в
    // этой фазе и запланированные этой цепочкой
    const getAnchorQuarters = (token, cellKey) => {
      if (SPECIAL_CANDIDATE_TOKENS.includes(token)) {
        return [cellKey];
      }
      const neighborKeys = getNeighborHexKeys(cellKey, gridRenderer);
      return token === COMMON_BUILDING_TOKENS.QUARTER
        ? neighborKeys.filter((adjKey) => virtual.centralQuarters.has(adjKey))
        : neighborKeys.filter((adjKey) => virtual.allQuarters.has(adjKey));
    };
    // Вероятность удержать город квартала anchorKey для постройки token.
    // Свой город из кэша — модель угрозы своих городов (с исключением для
    // оборонной казармы/арсенала); квартал, поставленный в этой фазе, — город
    // его ЦК; новый город этой фазы (ЦК еще не в кэше) — проверка его места
    const getAnchorSurvivalFactor = (token, anchorKey, priceCoins) => {
      const {threatByQuarter} = getOwnCityThreats();
      let cityThreat = threatByQuarter.get(anchorKey);
      let centralKey = anchorKey;
      if (!cityThreat && !virtual.centralQuarters.has(anchorKey)) {
        centralKey = getNeighborHexKeys(anchorKey, gridRenderer).find((adjKey) => virtual.centralQuarters.has(adjKey));
        cityThreat = centralKey ? threatByQuarter.get(centralKey) : null;
      }
      if (cityThreat) {
        const cityFactor = getCitySurvivalFactor(cityThreat);
        return cityFactor < 1 && checkIfDefensiveBuild(token, cityThreat, priceCoins) ? 1 : cityFactor;
      }
      return centralKey ? scoringContext.getNewCitySiteFactor(centralKey) : 1;
    };

    // Множитель риска постройки (1 — без риска). Крепость — оборонное
    // строение, дорога вне города, место нового города оценивает сама оценка ЦК
    getBuildRiskFactor = (token, cellKey, priceCoins) => {
      if (token === COMMON_BUILDING_TOKENS.ROAD
        || token === SPECIAL_BUILDING_TOKENS.FORTRESS
        || checkIfCentralQuarter(token)
      ) {
        return 1;
      }
      const anchorKeys = getAnchorQuarters(token, cellKey);
      if (anchorKeys.length === 0) {
        return 1;
      }
      // Строение на стыке городов остается и при потере одного из них
      let factor = 0;
      anchorKeys.forEach((anchorKey) => {
        factor = Math.max(factor, getAnchorSurvivalFactor(token, anchorKey, priceCoins));
      });
      return factor;
    };
    // Очки, которые постройка добавит захватчику (calcCityWinPoints): строение —
    // очко, дворец — еще PALACE_CAPTURE_WIN_POINTS, квартал — и точки, которые
    // он возьмет в город
    getBuildGiftWinPoints = (token, cellKey) => {
      if (token === SPECIAL_BUILDING_TOKENS.PALACE) {
        return 1 + PALACE_CAPTURE_WIN_POINTS;
      }
      if (token === COMMON_BUILDING_TOKENS.QUARTER) {
        return 1 + getNeighborHexKeys(cellKey, gridRenderer).filter((adjKey) =>
          checkIfKeyPointToken(virtual.hexes[adjKey]) && !virtual.controlledKeyPoints.has(adjKey)
        ).length;
      }
      return 1;
    };

    // Место нового города: синтетический город из одной клетки в той же
    // модели угрозы (стейт с подмененным кэшем городов бота — досягаемость
    // врагов берется из общего кэша модели, новых BFS почти нет)
    const gameStateForSite = state.shared.gameState;
    const newCitySiteMemo = new Map();
    scoringContext.getNewCitySiteFactor = (cellKey) => {
      if (newCitySiteMemo.has(cellKey)) {
        return newCitySiteMemo.get(cellKey);
      }
      const siteState = {
        ...state,
        shared: {
          ...state.shared,
          gameState: {
            ...gameStateForSite,
            citiesCache: {...(gameStateForSite.citiesCache || {}), [currentPlayer.color]: [{centralQuarter: cellKey}]},
          },
        },
      };
      const [siteThreat] = getCityCaptureThreats(siteState, currentPlayer).cityThreats;
      const hasHolders = Boolean(siteThreat) && siteThreat.holders.length > 0;
      const factor = siteThreat
        ? calcNewCitySiteSurvivalFactor({
          isThisRound: siteThreat.level >= CITY_THREAT_LEVELS.THIS_ROUND,
          isNextRound: siteThreat.level >= CITY_THREAT_LEVELS.NEXT_ROUND
            && siteThreat.level < CITY_THREAT_LEVELS.THIS_ROUND,
          hasHolders,
          hasSurvivingHolder: hasHolders && !siteThreat.isHolderAtRisk,
        })
        : 1;
      newCitySiteMemo.set(cellKey, factor);
      return factor;
    };
  }

  const isCellFreeForBuilding = (cellKey) => {
    if (virtual.plannedCells.has(cellKey)) {
      return false;
    }
    const token = virtual.hexes[cellKey];
    return !token || token === TOKENS.ROAD;
  };

  // Волна 32 (экономика, E3): оценка дороги-опоры = доля оценки ЦК на площадке,
  // если ЦК встает в ЭТОМ ходу (2 действия, монеты на обе постройки сверх резерва
  // присоединения). Кэш — на одну итерацию цикла плана (virtual меняется)
  let e32ChainRoadInfoMemo = new Map();
  let e32ChainSiteScoreMemo = new Map();
  if (e32RoadEnablers) {
    scoringContext.getE32ChainRoadInfo = (roadCellKey) => {
      if (e32ChainRoadInfoMemo.has(roadCellKey)) {
        return e32ChainRoadInfoMemo.get(roadCellKey);
      }
      let bestInfo = null;
      const siteCellKeys = e32RoadEnablers.get(roadCellKey) || [];
      // Элементали: ЦК требует свободный элемент земли (дорога элемента не требует)
      const hasElementForCentralQuarter = !isElementals || findFreeElementIndex(centralQuarterToken) !== -1;
      if (hasElementForCentralQuarter) {
        siteCellKeys.forEach((siteCellKey) => {
          if (siteCellKey === roadCellKey || !isCellFreeForBuilding(siteCellKey)) {
            return;
          }
          if (!e32ChainSites.get(siteCellKey).some((ruinsHexKey) => !virtual.controlledKeyPoints.has(ruinsHexKey))) {
            return;
          }
          let siteScore = e32ChainSiteScoreMemo.get(siteCellKey);
          if (siteScore === undefined) {
            siteScore = scoreCandidate(centralQuarterToken, siteCellKey, virtual, scoringContext);
            if (plannerBuildFocus) {
              siteScore *= getPlannerBuildFocusFactor(centralQuarterToken, plannerBuildFocus);
            }
            e32ChainSiteScoreMemo.set(siteCellKey, siteScore);
          }
          const score = calcE32ChainRoadScore({
            siteScore,
            actionsRemaining,
            coinsAfterChain: virtual.coins - e32RoadPriceCoins - centralQuarterPriceCoins,
            joinCoinReserve: getE32RuinsJoinCoinReserve(),
            nearestEnemyUnitDistance: enemyUnitDistances ? (enemyUnitDistances.get(siteCellKey) ?? Infinity) : Infinity,
            roadThreatFactor: threatenedQuarters.size > 0 ? 0.3 : 1,
            hasUnsuppliedUnits: Boolean(unsuppliedOwnUnitCells && unsuppliedOwnUnitCells.size > 0),
          });
          if (score > 0 && (!bestInfo || score > bestInfo.score)) {
            bestInfo = {score, siteCellKey};
          }
        });
      }
      e32ChainRoadInfoMemo.set(roadCellKey, bestInfo);
      return bestInfo;
    };
  }

  const getCandidateCells = (token) => {
    const availability = realAvailability[token];
    if (!availability) {
      return [];
    }

    const isSpecial = SPECIAL_CANDIDATE_TOKENS.includes(token);
    if (isSpecial) {
      // Спецстроения ставятся на свои свободные базовые кварталы (реальный калькулятор)
      const specialCells = [...availability.availableCells].filter(
        (cellKey) => !virtual.plannedCells.has(cellKey)
      );
      // Волна 18 (17.9): олигархия-опенинг — гильдию можно ставить на базовый
      // квартал, запланированный В ЭТОМ ЖЕ ходу (реальная доступность заморожена
      // на начало раунда и не видит свежих кварталов). Цепочка «квартал -> гильдия»
      // валидна: оба строения существуют к концу хода, валидатор это подтвердит.
      // Кап 2 гильдии/город (OLIGARCHY) обеспечивается движком и санитайзером
      if (scoringContext.oligarchyOpeningActive && token === SPECIAL_BUILDING_TOKENS.GUILD) {
        virtual.plannedCells.forEach((plannedCellKey) => {
          if (virtual.hexes[plannedCellKey] === COMMON_BUILDING_TOKENS.QUARTER
            && !virtual.plannedGuildCells.has(plannedCellKey)
            && !specialCells.includes(plannedCellKey)
          ) {
            specialCells.push(plannedCellKey);
          }
        });
      }
      return specialCells;
    }

    const cells = new Set();
    availability.availableCells.forEach((cellKey) => {
      if (!availability.blockedCells.has(cellKey) && isCellFreeForBuilding(cellKey)) {
        cells.add(cellKey);
      }
    });

    // Расширение цепочки: центральный квартал на ячейках, которые откроет ЛЮБОЕ
    // запланированное строение (правило: новый город можно начать не с ЦК —
    // ферма/казарма/квартал первым действием, ЦК смежно вторым).
    // Волна 19 (18.7): ячейки внутри зоны влияния кварталов других городов
    // отсекаются — реальный калькулятор их не видел (они не были доступны на
    // начало раунда), и ЦК там не встанет никогда
    if (checkIfCentralQuarter(token)) {
      virtual.plannedCells.forEach((roadCellKey) => {
        getNeighborHexKeys(roadCellKey, gridRenderer).forEach((cellKey) => {
          if (!isCellFreeForBuilding(cellKey)) {
            return;
          }
          if (availability.blockedCells.has(cellKey)) {
            return;
          }
          if (quarterZoneCells
            && !availability.availableCells.has(cellKey)
            && quarterZoneCells.has(cellKey)
          ) {
            return;
          }
          if (checkIfHexBlockedByEnemyUnit(cellKey, null, currentPlayer.id, units, null, null, null)) {
            return;
          }
          cells.add(cellKey);
        });
      });
    }

    // Расширение цепочки: пустые ячейки, смежные виртуально запланированным кварталам.
    // Путь строительства к ним гарантирован через сам новый квартал.
    if (token === COMMON_BUILDING_TOKENS.FARM
      || token === COMMON_BUILDING_TOKENS.BARRACKS
      || token === COMMON_BUILDING_TOKENS.QUARTER
    ) {
      virtual.allQuarters.forEach((quarterHexKey) => {
        getNeighborHexKeys(quarterHexKey, gridRenderer).forEach((cellKey) => {
          if (!isCellFreeForBuilding(cellKey)) {
            return;
          }
          if (token === COMMON_BUILDING_TOKENS.QUARTER && availability.blockedCells.has(cellKey)) {
            return;
          }
          // Волна 19 (18.7): базовый квартал новой цепочки не ставится в зону
          // влияния кварталов, которую реальный калькулятор не проверял
          if (token === COMMON_BUILDING_TOKENS.QUARTER
            && quarterZoneCells
            && !availability.availableCells.has(cellKey)
            && quarterZoneCells.has(cellKey)
          ) {
            return;
          }
          if (checkIfHexBlockedByEnemyUnit(cellKey, null, currentPlayer.id, units, null, null, null)) {
            return;
          }
          cells.add(cellKey);
        });
      });
    }

    // Фильтры смежности по правилам размещения (проверяются при завершении фазы,
    // но планировщик сразу строит валидную цепочку)
    return [...cells].filter((cellKey) => {
      const adjacentKeys = getNeighborHexKeys(cellKey, gridRenderer);
      if (token === COMMON_BUILDING_TOKENS.QUARTER) {
        return adjacentKeys.some((adjKey) => virtual.centralQuarters.has(adjKey));
      }
      if (token === COMMON_BUILDING_TOKENS.FARM || token === COMMON_BUILDING_TOKENS.BARRACKS) {
        return adjacentKeys.some((adjKey) => virtual.allQuarters.has(adjKey));
      }
      return true;
    });
  };

  const plan = [];
  let bestUnaffordable = null;

  // Волна 19 (18.1): в раундах 1-4 действия строительства тратятся ПОЛНОСТЬЮ —
  // если обычный скоринг не нашел кандидата, добор с минимальными полами
  // ценности: ферма > казарма > квартал > спецстроение (элементали, жетоны
  // сгорают) > дорога. Экономия раша на найм соблюдается автоматически:
  // кандидаты добора проходят ту же проверку доступности с coinReserve
  const ACTION_FILL_FLOORS = new Map([
    [COMMON_BUILDING_TOKENS.FARM, 0.6],
    [COMMON_BUILDING_TOKENS.BARRACKS, 0.5],
    [COMMON_BUILDING_TOKENS.QUARTER, 0.4],
    [COMMON_BUILDING_TOKENS.ROAD, 0.2],
  ]);
  const ELEMENTALS_FILL_SPECIALS = new Set([
    SPECIAL_BUILDING_TOKENS.GUILD, SPECIAL_BUILDING_TOKENS.TEMPLE, SPECIAL_BUILDING_TOKENS.PALACE,
    SPECIAL_BUILDING_TOKENS.WAREHOUSE, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP, SPECIAL_BUILDING_TOKENS.ARSENAL,
  ]);
  const getActionFillFloor = (token) => {
    if (ACTION_FILL_FLOORS.has(token)) {
      return ACTION_FILL_FLOORS.get(token);
    }
    // Добор действий не должен воскрешать то, что скоринг обнулил намеренно:
    // дворец бонусному боту не нужен даже вместо пропущенного действия
    if (token === SPECIAL_BUILDING_TOKENS.PALACE && bonusProfile?.hasConstructionActionsBonus) {
      return 0;
    }
    if (isElementals && ELEMENTALS_FILL_SPECIALS.has(token)) {
      return 0.3;
    }
    return 0;
  };
  // Волна 25 (23.6, запечена): добор действий расширен с раундов 1-4 на 1-10 —
  // живой тест 23: боты изредка вовсе пропускали строительство при монетах на
  // руках, хотя в раундах 1-10 полезное действие есть всегда. Полы ценности
  // добора минимальны — добор срабатывает только когда обычный скоринг пуст
  const isActionFillRound = round <= 10;

  while (actionsRemaining > 0) {
    if (e32RoadEnablers) {
      e32ChainRoadInfoMemo = new Map();
      e32ChainSiteScoreMemo = new Map();
    }
    // Волна 33: индекс городов «сильных городов» на итерацию плана — ЦК и
    // кварталы, запланированные в цепочке, становятся его городами; ответы
    // (класс, число строений) мемоизируются до следующего шага
    if (isStrongCitiesActive) {
      scoringContext.bonusCities = createBonusCityIndex({
        futureOwnCities: bonusFutureOwnCities,
        virtual,
        axis: bonusAxis,
        enemyQuarterDistances: realEnemyQuarterDistances,
        specialBuildings,
        gridRenderer,
        hexes,
        units,
        currentPlayerId: currentPlayer.id,
        buildingsDamage: bonusBuildingsDamage,
      });
      // Кап ферм фронтира снимается, пока цель ферм не достигнута, а ни у
      // одного тылового города нет ни одной легальной ячейки под ферму
      // (кандидаты этого шага, мемо на итерацию)
      let hasRearFarmCellMemo = null;
      // eslint-disable-next-line no-loop-func
      scoringContext.checkIfFrontierFarmCapWaived = () => {
        if (virtual.farmsCount >= farmsTarget) {
          return false;
        }
        if (hasRearFarmCellMemo === null) {
          const {bonusCities} = scoringContext;
          // Город ячейки — как в скоринге (getCandidateCity): ячейка на стыке
          // тыла и фронта считается фронтирной, а не «тыловой под ферму»
          hasRearFarmCellMemo = getCandidateCells(COMMON_BUILDING_TOKENS.FARM).some((farmCellKey) => {
            const adjCity = bonusCities.getCandidateCity(getNeighborHexKeys(farmCellKey, gridRenderer));
            return Boolean(adjCity) && bonusCities.classifyCity(adjCity) === 'rear';
          });
        }
        return !hasRearFarmCellMemo;
      };
    }
    const isFirstPlanningStep = plan.length === 0;

    // Замыкание синхронно и не переживает итерацию — ссылки на bestUnaffordable безопасны
    // eslint-disable-next-line no-loop-func
    const findBestCandidate = (isActionFillPass) => {
      let bestCandidate = null;
      let bestScore = 0;

      for (const token of candidateTokens) {
        if (!realAvailability[token]) {
          continue;
        }
        // Элементали: без свободного элемента этот тип строения недоступен в цепочке
        if (isElementals && findFreeElementIndex(token) === -1) {
          continue;
        }
        // Волна 22 (21.14): элементали в 1 раунде — казармы только под реальный
        // найм, ферм не больше, чем реально нанимаемых отрядов; излишек
        // элементов огня/земли не конвертируем в постройки (живой тест 21:
        // 3 казармы без единой фермы) — жесткий фильтр перекрывает и добор-пасс.
        // Волна 22.1 (отчет №18 §4.2): смягчение — ВТОРАЯ казарма разрешена
        // добор-пассом (элемент огня иначе сгорает: обычный скоринг применения
        // не нашел), когда реально нанимаются 2 отряда и монет хватает на
        // казарму + найм; третья казарма запрещена всегда
        if (isElementals && round === 1) {
          if (token === COMMON_BUILDING_TOKENS.BARRACKS) {
            if (affordableNewUnits === 0 || virtual.barracksCount >= 2) {
              continue;
            }
            if (virtual.barracksCount === 1
              && (!isActionFillPass
                || affordableNewUnits < 2
                || virtual.coins < elementalsBarracksPriceCoins + preferredUnitPrice)
            ) {
              continue;
            }
          }
          if (token === COMMON_BUILDING_TOKENS.FARM
            && virtual.farmsCount >= Math.max(1, affordableNewUnits)
          ) {
            continue;
          }
        }

        // Волна 23: AGRARIANISM делает бесплатной только ПЕРВУЮ ферму фазы —
        // ферма, уже запланированная в этой цепочке, учитывается как построенная,
        // иначе планировщик посчитал бы бесплатными обе
        const pendingForPricing = token === COMMON_BUILDING_TOKENS.FARM
        && (virtual.plannedFarmActions || 0) > 0
          ? [...pendingBuildings, {token: COMMON_BUILDING_TOKENS.FARM, type: CONSTRUCTION_ACTION_TYPES.CONSTRUCTION}]
          : pendingBuildings;
        const {priceCoins, isFreeAction} = getBuildingPrice(token, currentPlayer, pendingForPricing);
        const isUnaffordable = virtual.coins < priceCoins
          || (!isFreeAction && virtual.coins - priceCoins < coinReserve);

        for (const cellKey of getCandidateCells(token)) {
          // DEFENSIVE: не развиваемся дальше радиуса экспансии от стартового квартала
          if (homeDistances) {
            const homeDistance = homeDistances.get(cellKey);
            if (homeDistance === undefined || homeDistance > profile.expansionRadius) {
              continue;
            }
          }

          let score = scoreCandidate(token, cellKey, virtual, scoringContext);
          if (plannerBuildFocus) {
            score *= getPlannerBuildFocusFactor(token, plannerBuildFocus);
          }
          // Волна 35 (B): постройка в своем городе под угрозой захвата — ценность ×
          // вероятность удержать город минус потеря при захвате: очки строения
          // захватчику и монеты на него (одна точка для всех строений города,
          // см. configs/botBuildRisk). Новый город учтен в самой оценке ЦК
          let buildRiskFactor = 1;
          if (getBuildRiskFactor) {
            if (!checkIfCentralQuarter(token)) {
              buildRiskFactor = getBuildRiskFactor(token, cellKey, priceCoins);
              if (buildRiskFactor < 1) {
                score = applyBuildSurvivalFactor(score, buildRiskFactor, calcCaptureLoss(
                  getBuildGiftWinPoints(token, cellKey), priceCoins, e32CoinsToPoints
                ));
              }
            } else if (isActionFillPass && score > 0) {
              // Добор не строит новый город на опасном месте (множитель места
              // уже посчитан оценкой ЦК — ответ из кэша)
              buildRiskFactor = scoringContext.getNewCitySiteFactor(cellKey);
              if (buildRiskFactor < 1) {
                score = 0;
              }
            }
          }
          // Волна 32 (экономика, E6): поздно доходное спецстроение платит свою цену
          // (монеты в конце партии — очки 2:1); крепость не в перечне (оборона)
          if (e32LateSpecialsActive && score > 0 && E32_LATE_COST_TOKENS.has(token)) {
            score = Math.max(0, score - calcLatePriceDeduction(priceCoins, e32CoinsToPoints, round));
          }
          // Оценка самой ячейки до пола добора (0 — скоринг обнулил ее намеренно)
          const ownScore = score;
          if (isActionFillPass) {
            // Волна 33 (C9): у «сильных городов» пол добора не воскрешает
            // обнуленные фермы и кварталы (кап ферм фронтира, лишние
            // кварталы) — иначе 5-6 действий богатого бота уходили в фермы
            // «куда угодно»; казармы и дороги добираются как раньше, фермы
            // крысолюдов (их армия) — тоже
            // Волна 36 (C3, боты золото+): не воскрешает и казармы (кап города,
            // гейт «3 свободных», «казарм не больше ферм»), а у золотого бота
            // без пакета «сильных городов» — еще фермы и кварталы. Реплеи: 36 из
            // 117 казарм ботов и 11 из 17 казарм Сципиона пришли из добора
            const isBonusFloorBlocked = (isStrongCitiesActive
              && ownScore <= 0
              && (token === COMMON_BUILDING_TOKENS.QUARTER
                || (token === COMMON_BUILDING_TOKENS.FARM && !isRatmen)))
              || (isW36BarracksCap
                && ownScore <= 0
                && (token === COMMON_BUILDING_TOKENS.BARRACKS
                  || token === COMMON_BUILDING_TOKENS.QUARTER
                  || (token === COMMON_BUILDING_TOKENS.FARM && !isRatmen)));
            // Волна 35 (B): добор не воскрешает постройку в городе под угрозой
            // и новый город на опасном месте — пол добора им не дается
            if (!isBonusFloorBlocked && buildRiskFactor >= 1) {
              score = Math.max(score, getActionFillFloor(token));
            }
          }

          // Волна 32 (экономика, E2): доступность по ячейке — присоединение руин
          // (и дорога-опора цепочки E3) проверяется по резерву присоединения.
          // Только при собственной оценке ячейки > 0: дорогу защитные гейты (агрессоры,
          // раш, просвещенные, 3+ соседей) обнуляют раньше бонуса цепочки, и пол добора
          // не должен строить такую постройку на монеты найма
          let isCellUnaffordable = isUnaffordable;
          let isE32ReserveRelaxed = false;
          if (e32RuinsReserveActive && ownScore > 0 && isUnaffordable && !isFreeAction && virtual.coins >= priceCoins) {
            const isRuinsJoin = token === COMMON_BUILDING_TOKENS.QUARTER || checkIfCentralQuarter(token)
              ? checkIfCellJoinsUncontrolledRuins(cellKey, virtual.hexes, virtual.controlledKeyPoints, gridRenderer)
              : token === COMMON_BUILDING_TOKENS.ROAD
                && Boolean(scoringContext.getE32ChainRoadInfo && scoringContext.getE32ChainRoadInfo(cellKey));
            if (isRuinsJoin) {
              isCellUnaffordable = virtual.coins - priceCoins < getE32RuinsJoinCoinReserve();
              isE32ReserveRelaxed = !isCellUnaffordable;
            }
          }

          if (isCellUnaffordable) {
            // Кросс-фазовое бюджетирование: ценный, но недоступный по монетам кандидат
            // становится накопительной целью (война придержит траты до его финансирования)
            if (!isActionFillPass
              && isFirstPlanningStep
              && score >= BOT_WEIGHTS.SAVINGS_GOAL_MIN_SCORE
              && (!bestUnaffordable || score > bestUnaffordable.score)
            ) {
              bestUnaffordable = {token, cellKey, price: priceCoins, score};
            }
            continue;
          }

          if (score > bestScore) {
            bestScore = score;
            bestCandidate = {token, cellKey, priceCoins, isFreeAction};
            if (isE32ReserveRelaxed) {
              bestCandidate.e32ReserveRelaxed = true;
            }
            // E3: дорога выбрана ради цепочки (оценка цепочки не ниже обычной)
            const e32ChainInfo = token === COMMON_BUILDING_TOKENS.ROAD && scoringContext.getE32ChainRoadInfo
              ? scoringContext.getE32ChainRoadInfo(cellKey)
              : null;
            if (e32ChainInfo && e32ChainInfo.score === score) {
              bestCandidate.e32ChainSiteCellKey = e32ChainInfo.siteCellKey;
            }
          }
        }
      }

      return bestCandidate;
    };

    let bestCandidate = findBestCandidate(false);
    if (!bestCandidate && isActionFillRound) {
      bestCandidate = findBestCandidate(true);
    }

    if (!bestCandidate) {
      break;
    }

    // Применяем шаг к виртуальному состоянию цепочки
    virtual.coins -= bestCandidate.priceCoins;
    virtual.plannedCells.add(bestCandidate.cellKey);
    if (bestCandidate.token === MINE_EXPLORATION) {
      // Разведанный рудник смежен своему кварталу — контролируется сразу
      virtual.hexes[bestCandidate.cellKey] = KEY_POINT_TOKENS.MINE;
      virtual.controlledKeyPoints.add(bestCandidate.cellKey);
      virtual.plannedMineCells.add(bestCandidate.cellKey);
    } else if (!SPECIAL_CANDIDATE_TOKENS.includes(bestCandidate.token)) {
      virtual.hexes[bestCandidate.cellKey] = bestCandidate.token;
    } else {
      virtual.plannedSpecials.set(bestCandidate.cellKey, bestCandidate.token);
    }
    // Элементали: виртуально расходуем элемент на запланированный шаг
    if (isElementals) {
      const elementIndex = findFreeElementIndex(bestCandidate.token);
      if (elementIndex !== null && elementIndex >= 0) {
        virtualUsedElementIndices.add(elementIndex);
      }
    }
    if (bestCandidate.token === COMMON_BUILDING_TOKENS.QUARTER) {
      virtual.allQuarters.add(bestCandidate.cellKey);
    } else if (checkIfCentralQuarter(bestCandidate.token)) {
      virtual.centralQuarters.add(bestCandidate.cellKey);
      virtual.allQuarters.add(bestCandidate.cellKey);
    }
    // Запланированный квартал берет под контроль смежные ключевые точки:
    // последующие шаги цепочки не получают за них повторную ценность
    if (bestCandidate.token === COMMON_BUILDING_TOKENS.QUARTER || checkIfCentralQuarter(bestCandidate.token)) {
      getNeighborHexKeys(bestCandidate.cellKey, gridRenderer).forEach((adjKey) => {
        if (checkIfKeyPointToken(virtual.hexes[adjKey])) {
          virtual.controlledKeyPoints.add(adjKey);
        }
      });
    }
    if (bestCandidate.token === COMMON_BUILDING_TOKENS.FARM) {
      virtual.farmsCount += 1;
      virtual.plannedFarmActions += 1;
    } else if (bestCandidate.token === COMMON_BUILDING_TOKENS.BARRACKS) {
      virtual.barracksCount += 1;
    } else if (bestCandidate.token === COMMON_BUILDING_TOKENS.ROAD) {
      virtual.plannedRoadCells.add(bestCandidate.cellKey);
    } else if (bestCandidate.token === SPECIAL_BUILDING_TOKENS.WAREHOUSE) {
      // Запланированный склад: смежные фермы в этой же цепочке получают бонус
      virtual.plannedWarehouses.add(bestCandidate.cellKey);
    } else if (bestCandidate.token === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP) {
      virtual.plannedMilitaryCamps.add(bestCandidate.cellKey);
    } else if (bestCandidate.token === SPECIAL_BUILDING_TOKENS.FORTRESS) {
      virtual.plannedFortresses.add(bestCandidate.cellKey);
    } else if (bestCandidate.token === SPECIAL_BUILDING_TOKENS.GUILD) {
      // Ячейка занята гильдией — второй раз под гильдию не предлагается (17.9)
      virtual.plannedGuildCells.add(bestCandidate.cellKey);
    } else if (bestCandidate.token === SPECIAL_BUILDING_TOKENS.PALACE) {
      // Волна 22 (21.8a): дворец в цепочке учитывается для запрета второго
      virtual.plannedPalaces.add(bestCandidate.cellKey);
    }

    plan.push(bestCandidate);
    if (!bestCandidate.isFreeAction) {
      actionsRemaining -= 1;
    }
  }

  // Управление накопительной целью (кросс-фазовое бюджетирование)
  if (strategy.savingsGoal) {
    const goal = strategy.savingsGoal;
    const isGoalBuilt = plan.some((action) => action.cellKey === goal.cellKey)
      || hexes[goal.cellKey] === goal.token
      || specialBuildings[goal.cellKey] === goal.token;
    const isGoalCellTaken = Boolean(hexes[goal.cellKey])
      && hexes[goal.cellKey] !== TOKENS.ROAD
      && hexes[goal.cellKey] !== goal.token;
    const isStale = round - goal.round > SAVINGS_GOAL_MAX_ROUNDS;
    if (isGoalBuilt || isGoalCellTaken || isStale) {
      strategy.savingsGoal = null;
    }
  }
  if (!strategy.savingsGoal && bestUnaffordable) {
    strategy.savingsGoal = {
      token: bestUnaffordable.token,
      cellKey: bestUnaffordable.cellKey,
      price: bestUnaffordable.price,
      round,
    };
  }

  return plan;
};

/**
 * Фаза CONSTRUCTION: одно строительство за тик по плану цепочки.
 * План пересчитывается каждый тик на свежем состоянии; выполняется первый шаг.
 * Промежуточная валидация не производится — как и у игрока, размещение
 * проверяется при завершении фазы (см. sanitizeInvalidPendingBuildings в completeBotPhase).
 */
export const decideConstructionStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const pendingBuildings = selectPendingBuildings(state);
  const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
  const isInitiativeInterceptedWithIsolationism = selectIsInitiativeInterceptedWithIsolationism(state);

  // Элементали: жетоны элементов на фазу выдает UI-компонент NewBuildingToolbar,
  // который для ботов (и в headless-симуляции) не монтируется — бот докидывает
  // недостающие жетоны сам, зеркаля логику тулбара
  if (isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS)) {
    const elementalsTokens = selectElementalsConstructionTokens(state);
    const additionalTokensCount = (currentPlayer.constructionPhaseActions || 0) - elementalsTokens.length;
    if (additionalTokensCount > 0) {
      store.dispatch(elementalsSetRandomElements(
        addMoreElementalsConstructionTokens(elementalsTokens, additionalTokensCount)
      ));
    }
  }

  // Волна 23 (22.4): просвещенные с FANATICISM конвертируют 3 очка -> 5 монет
  // раз в раунд (зеркало PolicyFanaticismConversionButton). В финальном раунде
  // невыгодно: монеты уже не потратить, а очки решают победу
  if (isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED)
    && isPlayerPolicy(currentPlayer, POLICIES.FANATICISM)
    && !currentPlayer.isFanaticismConversionUsed
    && (currentPlayer.winPoints || 0) >= POLICY_FANATICISM_WIN_POINTS_CONVERSION
    && !checkIfFinalRound(state)
  ) {
    store.dispatch(fanaticismConvertWinPoints());
    return true;
  }

  // Волна 35 (S, гейт isPreW35ArmyReservePlayer): монеты на начало хода стройки —
  // на первом тике раунда, до покупки политики. Политика списывает цену сразу и
  // в pending не попадает, поэтому резерв армейской фазы и в политике, и в стройке
  // считается от этой записи (resolveConstructionPhaseCoins): минимальный бюджет
  // стройки у них общий, а не у каждой свой
  if (!isPreW35ArmyReservePlayer(currentPlayer.id)) {
    const strategy = getBotStrategy(currentPlayer.id);
    const round = selectRound(state);
    if (!strategy.w35ConstructionPhaseCoins || strategy.w35ConstructionPhaseCoins.round !== round) {
      strategy.w35ConstructionPhaseCoins = {round, coins: calcConstructionPhaseCoins(currentPlayer, pendingBuildings)};
    }
  }

  // Покупка политики: не тратит действие строительства, максимум 1 за фазу.
  // (Проба pb: построение плана планировщика здесь, ДО политики, вместе со
  // смещением политик дало регресс +9.2 -> +4.1 — план строится позже, в
  // planConstructionChain, как в сборке fc)
  if (decidePolicyStep(store)) {
    return true;
  }

  // Волна 36 (боты золото+): обновление рынка политик ради цели из топ-3
  // фракции (тратит действие строительства, раз в раунд); после обновления
  // следующий шаг снова начинается с покупки политики
  if (decidePolicyMarketRefreshStep(store)) {
    return true;
  }

  // Перехват инициативы при тактическом окне (тратит действие)
  if (decideInitiativeStep(store)) {
    return true;
  }

  const hasActionsRemaining = checkIfHasRemainingConstructionActions(
    currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism
  );
  if (!hasActionsRemaining) {
    return false;
  }

  const plan = planConstructionChain(store);
  if (plan.length === 0) {
    return false;
  }

  const action = plan[0];

  // Волна 32 (экономика): счетчики харнесса (в проде __e32Stats не задан)
  if (!store.__isBotSandbox) {
    if (action.e32ReserveRelaxed) {
      recordE32Stat(currentPlayer.id, 'reserveRelaxedBuilds');
    }
    if (action.e32ChainSiteCellKey) {
      recordE32ChainRoad(currentPlayer.id, action.e32ChainSiteCellKey);
    }
  }

  // Элементали: тратим элемент перед строительством (как в GameScene)
  if (isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS)) {
    const element = getElementForTokenType(action.token);
    if (element) {
      const elementIndex = getUnusedElementIndex(element, selectElementalsConstructionTokens(store.getState()));
      if (elementIndex === -1) {
        return false; // элемент кончился между планированием и исполнением
      }
      store.dispatch(elementalsSpendElement(elementIndex));
    }
  }

  // Фронт строительства: армия агрессивных профилей тяготеет туда, куда бот строится
  rememberBuildCell(currentPlayer.id, action.cellKey);

  store.dispatch(setSelectedToken(action.token));
  if (action.token === MINE_EXPLORATION) {
    // Гномы: перед разведкой рудника коммитятся pending-строения — сначала чистим невалидные
    sanitizeInvalidPendingBuildings(store);
    // Подкрутка d6 бота его уровнем бонуса (у «божества» d6 всегда 6 — рудник
    // всегда великий): пол (см. getBotD6MinRoll) и прибавка к значению (см.
    // getBotD6RollBonus)
    const diceResult = getD6Roll(getBotD6MinRoll(currentPlayer), getBotD6RollBonus(currentPlayer));
    const mineToken = diceResult > 4 ? KEY_POINT_TOKENS.SUPER_MINE : KEY_POINT_TOKENS.MINE;
    store.dispatch(setHexToken(action.cellKey, mineToken, action.priceCoins, true));
  } else if (SPECIAL_CANDIDATE_TOKENS.includes(action.token)) {
    store.dispatch(setSpecialBuilding(action.cellKey, action.token));
  } else {
    store.dispatch(setHexToken(
      action.cellKey,
      action.token,
      action.priceCoins,
      false,
      action.isFreeAction,
    ));
  }
  store.dispatch(setSelectedToken(null));

  return true;
};
